// Simple i18n implementation without external dependencies
export type Language = 'en' | 'de';

export interface Translation {
  [key: string]: string | Translation | string[];
}

export const translations: Record<Language, Translation> = {
  en: {
    common: {
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      cancel: 'Cancel',
      save: 'Save',
      edit: 'Edit',
      delete: 'Delete',
      yes: 'Yes',
      no: 'No',
      ok: 'OK',
      gotIt: 'Got it',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      search: 'Search',
      filter: 'Filter',
      sort: 'Sort',
      export: 'Export',
      import: 'Import',
      upload: 'Upload',
      download: 'Download',
      view: 'View',
      open: 'Open',
      close: 'Close',
      select: 'Select',
      clear: 'Clear',
      refresh: 'Refresh',
      retry: 'Retry',
      language: 'Language',
      appName: 'AI Customs Engine',
    },
    navigation: {
      dashboard: 'Dashboard',
      process: 'Process Documents',
      transactions: 'Transaction History',
      files: 'Files',
      profile: 'My Profile',
      settings: 'Settings',
      adminDashboard: 'Admin Dashboard',
      tenants: 'Tenants',
      users: 'Users',
      logout: 'Logout',
    },
    settings: {
      title: 'Settings',
      subtitle: 'Manage your modules, preferences and ATLAS configuration',
      tabs: {
        modules: 'Modules & Integrations',
        preferences: 'Preferences',
        atlas: 'ATLAS Configuration',
        tenantUsers: 'Users',
        tenantOrganisation: 'Organisation',
      },
    },
    plans: {
      essential: 'Essential',
      advanced: 'Advanced',
      enterprise: 'Enterprise',
    },
    dashboard: {
      title: 'Document Processing Center',
      subtitle: 'Upload, extract, and integrate your business documents with powerful APIs',
      welcome: {
        header: 'Welcome back, {0}!',
        message: 'Welcome, {0} {1}',
        subtitle: 'Ready to process your documents? Choose an option below to get started.',
      },
      totalDocuments: 'Total Documents',
      successfulTransactions: 'Successful Transactions',
      failedTransactions: 'Failed Transactions',
      connectedModules: 'Connected Modules',
      recentActivity: 'Recent Activity',
      successRate: 'Success Rate',
      topModules: 'Top Modules',
      recentTransactions: 'Recent Transactions',
      viewAll: 'View All',
      noData: 'No data available',
      planDetails: 'Plan Details',
      monthlyLimit: 'Monthly Document Limit',
      documentsUsed: 'Documents Used',
      dataRetention: 'Data Retention',
      days: 'days',
      unlimited: 'Unlimited',
      cards: {
        processDocuments: {
          title: 'Process Documents',
          description: 'Upload and extract data from your documents, then push to external modules.',
          button: 'Start Processing',
        },
        history: {
          title: 'Processing History',
          description: 'View detailed processing history with files, API responses, and success/failure records.',
          button: 'View History',
        },
        tamperingDetection: {
          title: 'Document-Tampering Detection',
          description: 'Advanced AI-powered detection of document tampering and fraud',
          button: 'Coming Soon',
        },
        emailProcessor: {
          title: 'Email Attachment Processor',
          description: 'Intelligent processing and extraction from email attachments',
          button: 'Coming Soon',
        },
        tariffClassifier: {
          title: 'Tariff Classifier',
          description: 'AI-powered HS code classification',
          fileUpload: {
            dropZone: {
              main: 'Drag & drop file here',
              active: 'Drop the file here',
              browse: 'or click to browse',
              supported: 'Supported: CSV, Excel (Max 10MB)',
              uploading: 'Uploading File...',
              complete: 'Complete',
              selected: 'File Selected',
              rejected: 'File rejected:',
              tips: {
                bulk: 'Tip: Use the Bulk Upload tab for multiple files.',
                formats: {
                  title: 'Supported File Formats:',
                  csv: 'CSV: Comma-separated values with header row',
                  excel: 'Excel: .xlsx or .xls files (first sheet will be processed)',
                  columns: 'Expected columns: Description/Item (required), HS Code (optional)'
                }
              }
            },
            bulk: {
              dropZone: {
                main: 'Drag & drop multiple files here',
                active: 'Drop files here',
                limits: 'Max {{maxFiles}} files, {{maxSize}}MB total'
              },
              errors: {
                maxFiles: 'Maximum {{maxFiles}} files allowed. You can add {{remaining}} more.',
                maxSize: 'Total size exceeds {{maxSize}}MB limit'
              },
              status: {
                files: 'Files ({{current}}/{{max}})',
                total: 'Total: {{size}} / {{maxSize}}MB',
                items: 'items',
                uploading: 'Uploading...',
                uploadButton: 'Upload {{count}} Files',
                clear: 'Clear All',
                complete: 'Bulk Upload Complete',
                summary: '{{success}}/{{total}} files successful • {{items}} items extracted'
              },
              tips: {
                title: 'Bulk Upload Tips:',
                mix: 'Mix different file types in one upload',
                parallel: 'Each file is processed in parallel for faster results',
                failed: 'Failed files won\'t affect successful ones'
              }
            }
          },
          tabs: {
            direct: 'Direct Classification',
            fileUpload: 'File Upload',
            bulkUpload: 'Bulk Upload',
            history: 'History'
          },
          button: 'Coming Soon',
          uploadedFile: 'Uploaded File',
          viewFile: 'View File',
          classificationResults: 'Classification Results',
          viewResults: 'View Results',
          downloadResultsCSV: 'Download Results (CSV)',
          downloadResultsExcel: 'Download Results (Excel)',
          downloadOriginal: 'Download Original',
          fileContents: 'File Contents ({{count}} items)',
          fileContent: 'File Content',
          excelFile: 'Excel File',
          excelCannotBeDisplayed: 'Excel files cannot be displayed directly. Please download to view.',
          copyContent: 'Copy Content',
          download: 'Download',
          downloadExcelFile: 'Download Excel File',
          file: 'File',
          fileTypeNotSupported: 'This file type cannot be displayed in the browser.',
          downloadCSV: 'Download CSV',
          downloadExcel: 'Download Excel',
          close: 'Close',
          transactionDetails: 'Transaction Details',
          inputData: 'Input Data',
          outputData: 'Classification Results',
          processingInformation: 'Processing Information',
          productDescription: 'Product Description',
          materials: 'Materials',
          origin: 'Origin',
          intendedUse: 'Intended Use',
          generatedHS: 'Generated HS',
          generatedHSCode: 'Generated HS Code',
          confidence: 'Confidence',
          enhancedDescription: 'Description',
          reasoning: 'Reasoning',
          processingTime: 'Processing Time',
          status: 'Status',
          analysis: {
            logic: 'AI Analysis Logic',
            pathTaken: 'Path Taken',
            verdict: 'Verdict',
          },
          input: {
            label: 'Product Description *',
            placeholder: 'E.g., Laptop computer with Intel Core i7 processor, 16GB RAM, 512GB SSD, 15.6 inch display',
            helperText: 'Provide a detailed description for better accuracy',
            analyzing: 'AI is analyzing your product... (first request may take longer)'
          },
          quickMode: 'Quick Reasoning',
          advancedMode: 'Advanced Reasoning',
          quickModeDesc: 'Vector search only (faster)',
          advancedModeDesc: 'Full RAG with LLM (more accurate)',
          speed: {
            quick: '~1s/item',
            advanced: '~5s/item'
          },

          uploadAnother: 'Upload Another Document',
          howItWorks: {
            title: 'How it works:',
            direct: 'Direct Classification: Enter product details to get instant HS code classification',
            file: 'File Upload: Upload CSV/Excel files with product lists for batch classification',
            history: 'History: View and download your past classification results'
          },
          createdAt: 'Created At',
          transactionId: 'Transaction ID',
          noDataAvailable: 'No data available',
          noResultsAvailable: 'No results available',
          item: 'Item',
          classifyWithAI: 'Classify with AI',
          classifying: 'Classifying...',
          reset: 'Reset',
          classificationResult: 'Classification Result',
          hsCode: 'HS Code',
          confidencePercentage: '{{percent}}% Confidence',
          copyHSCode: 'Copy HS Code',
          copiedToClipboard: 'Copied to clipboard!',
          processingTimeSeconds: 'Processing Time: {{seconds}} seconds',
          errors: {
            failedToLoadFile: 'Failed to load file',
            downloadFailed: 'Download failed',
            failedToLoadDetails: 'Failed to load transaction details',
            classificationFailed: 'Classification failed',
            failedToCopyContent: 'Failed to copy content',
            noFileAssociated: 'No file associated with this transaction',
            failedToViewFile: 'Failed to view file',
            failedToViewResults: 'Failed to view results',
            failedToDownloadResults: 'Failed to download results',
          },
          processedViaRag: 'Processed via RAG Pipeline',
          success: {
            resultsDownloaded: 'Results downloaded successfully!',
            fileDownloaded: 'File downloaded!',
            downloadStarted: 'Download started!',
            contentCopied: 'Content copied to clipboard',
            hsCodeCopied: 'HS Code copied to clipboard!',
          },
          shippingCompliance: {
            title: 'EU Shipping Compliance',
            subtitle: 'Check this HS code against EU TARIC for a destination country.',
            destinationLabel: 'Destination country',
            destinationHelper: '2-letter ISO code (e.g. DE, RU, KP)',
            checkButton: 'Check shipping',
            checking: 'Checking…',
            invalidCountry: 'Enter a 2-letter ISO country code (e.g. DE, RU, KP)',
            commodityNotFound: 'Commodity {{code}} not found in EU TARIC.',
            genericError: 'Shipping check failed. Please try again.',
            verdict: {
              prohibited: 'Prohibited',
              prohibitedHelper: 'At least one Series A prohibition measure applies to this destination.',
              conditionsApply: 'Conditions Apply',
              conditionsApplyHelper: 'Shipping is permitted but conditions (documents, licences) must be satisfied.',
              clear: 'Clear',
              clearHelper: 'No restrictive EU TARIC measures apply for this destination.',
            },
            importTitle: 'Import-side restrictions',
            exportTitle: 'Export-side restrictions',
            noneApplicable: 'none applicable',
            seriesLabel: 'Series {{series}}',
            typeLabel: 'Type {{id}}',
            conditionsCountSingular: '1 condition',
            conditionsCountPlural: '{{count}} conditions',
            documentsHeading: 'Required documents ({{count}})',
            legalHeading: 'Legal references ({{count}})',
            category: {
              dualUse: 'Dual-use',
              military: 'Military goods',
              sanctions: 'Sanctions',
              culturalGoods: 'Cultural goods',
              tortureRiot: 'Torture / riot control',
              prohibition: 'Prohibition',
            },
            caveatsHeading: 'COMMODITY-LEVEL WARNINGS',
            caveatBanner: 'TARIC verdict insufficient — commodity-level controls apply',
            caveatBannerHelper: 'EU TARIC tracks customs-border measures only. The destination-keyed verdict above does not capture all applicable controls for this commodity.',
            caveat: {
              ARMS_AND_AMMUNITION: {
                title: 'Arms and ammunition',
                body: 'This commodity is classified as arms/munitions. Export and intra-EU transfer require licences under national export-control law and EU Directive 2009/43/EC, regardless of destination.',
              },
              EXPLOSIVES_PYROTECHNICS: {
                title: 'Explosives and pyrotechnics',
                body: 'This commodity is in Chapter 36 (explosives, pyrotechnics). National licences and EU/national transport regulations may apply regardless of destination.',
              },
              RADIOACTIVE_NUCLEAR: {
                title: 'Radioactive material',
                body: 'This commodity covers fissile/radioactive elements. IAEA safeguards, Euratom controls, and national authorisations apply regardless of destination.',
              },
              RADIOACTIVE_ISOTOPES: {
                title: 'Radioactive isotopes and compounds',
                body: 'This commodity covers radioactive isotopes/compounds. IAEA safeguards, Euratom controls, and national authorisations apply regardless of destination.',
              },
              COMMODITY_FOOTNOTE: {
                title: 'Blanket regulatory restriction',
                body: 'TARIC attaches a prohibition-flavoured footnote to this commodity. Review the footnote text and applicable EU regulations before shipping.',
              },
              AMBIGUOUS_SUBHEADING: {
                title: 'Ambiguous subheading',
                body: 'This code is a non-declarable subheading covering multiple specific commodities. The verdict below is the union of every declarable child — any of these restrictions could apply depending on the exact product. Confirm the 10-digit declarable code before shipping.',
              },
            },
          },
        },
        exportControl: {
          title: 'AI Export Control',
          description: 'AI-powered export control compliance verification',
          button: 'Coming Soon',
        },
        documentSets: {
          title: 'Document Sets',
          description: 'Combine multiple documents for customs declarations',
          button: 'Combine Documents',
        },
        magicMarker: {
          title: 'Magic Marker Pro',
          description: 'AI-powered document annotation with smart field extraction and validation',
          button: 'Annotate Documents',
        },
        complianceEngine: {
          title: 'Compliance Engine',
          description: 'Real-time sanctions screening and 1000+ trade regulations compliance',
          button: 'Check Compliance',
        },
        customsDeclaration: {
          title: 'AI Customs Declaration',
          description: '190+ country support with BEO ATLAS integration',
          button: 'Create Declaration',
        },
        aiLearning: {
          title: 'AI Learning Module',
          description: 'Continuous improvement from user feedback and corrections',
          button: 'Coming Soon',
        },
        businessIntelligence: {
          title: 'Business Intelligence',
          description: 'Real-time analytics and insights dashboard',
          button: 'Coming Soon',
        },
        aiAssistant: {
          title: 'AI Assistant',
          description: 'Natural language interface for document queries',
          button: 'Coming Soon',
        },
        workflowAutomation: {
          title: 'Workflow Automation',
          description: 'Visual workflow builder with smart automation',
          button: 'Coming Soon',
        },
        mobileApp: {
          title: 'Mobile App',
          description: 'Process documents on-the-go with React Native',
          button: 'Coming Soon',
        },
        blockchain: {
          title: 'Blockchain Integration',
          description: 'Immutable verification and audit trails',
          button: 'Coming Soon',
        },
        multiLanguage: {
          title: 'Multi-Language Support',
          description: '100+ languages with AI-powered translation',
          button: 'Coming Soon',
        },
        masterData: {
          title: 'Master Data Management',
          description: 'Centralized data repository with 98% matching',
          button: 'Coming Soon',
        },
        predictiveAnalytics: {
          title: 'Predictive Analytics',
          description: 'ML-based shipping predictions and forecasting',
          button: 'Coming Soon',
        },
        smartContracts: {
          title: 'Smart Contracts',
          description: 'Automated trade agreement generation',
          button: 'Coming Soon',
        },
        documentRouting: {
          title: 'Smart Document Routing',
          description: 'AI-based intelligent workflow routing',
          button: 'Coming Soon',
        },
        supplyChain: {
          title: 'Supply Chain Visibility',
          description: 'End-to-end tracking and monitoring',
          button: 'Coming Soon',
        },
        qualityScoring: {
          title: 'Quality Scoring',
          description: 'AI-powered document quality assessment',
          button: 'Coming Soon',
        },
        versionControl: {
          title: 'Version Control',
          description: 'Complete document versioning and change tracking',
          button: 'Coming Soon',
        },
        adminPanel: {
          title: 'Admin Panel',
          description: 'Manage users, modules, and system configuration.',
          button: 'Open Admin',
        },
      },
      badges: {
        comingSoon: 'Coming Soon',
        inProgress: 'In Progress',
        new: 'New',
        pro: 'Pro',
        advanced: 'Advanced',
        beta: 'Beta',
      },
      errors: {
        loadStatsFailed: 'Failed to load dashboard statistics',
      },
      quickOverview: {
        title: 'Quick Overview',
      },
      stats: {
        documentsProcessed: 'Documents Processed',
        successfulIntegrations: 'Successful Integrations',
        successRate: '{0}% success rate',
        recentActivity: 'Recent Activity (7 days)',
      },
      noRecentTransactions: 'No recent transactions',
      viewAllTransactions: 'View All Transactions',
      mostUsedModules: 'Most Used Modules',
      moduleUsage: 'Used {0} times',
      noModulesUsed: 'No modules used yet',
      comingSoon: 'Coming Soon',
      inProgress: 'In Progress',
    },
    moduleDrawer: {
      title: 'Select Module',
      authenticate: 'Authenticate',
      switchUser: 'Switch User',
      empty: 'No modules available',
      closeAriaLabel: 'Close module panel',
      openAriaLabel: 'Open module panel',
    },
    appShell: {
      brandLogoAlt: 'BEO Software',
      userMenuAriaLabel: 'User menu',
      collapseSidebar: 'Collapse sidebar',
      expandSidebar: 'Expand sidebar',
    },
    documentProcessing: {
      title: 'Smart Exports',
      subtitle: '',
      sections: {
        selectModule: 'Select Module/Endpoint',
        uploadDocuments: 'Upload Documents',
        reviewExtractedData: 'Review Extracted Data',
        reviewSendData: 'Review & Send Data',
      },
      module: {
        chooseModule: 'Choose Module',
        label: 'Module: {0}',
        authenticated: 'Authenticated',
        tokenExpired: 'Token Expired',
        notAuthenticated: 'Not Authenticated',
      },
      moduleAuth: {
        title: 'Authenticate with {0}',
        demoMode: 'Demo Mode',
        demoDescription: 'Enter any credentials to authenticate with {0}. In production, real module credentials will be required.',
        username: 'Username',
        usernamePlaceholder: 'Enter username',
        usernameRequired: 'Username is required',
        password: 'Password',
        passwordPlaceholder: 'Enter password',
        passwordRequired: 'Password is required',
        authSuccess: 'Successfully authenticated with {0}!',
        userDetailsSaved: 'User details saved.',
        authFailed: 'Authentication failed. Please try again.',
        cancel: 'Cancel',
        authenticate: 'Authenticate',
        authenticating: 'Authenticating...',
        demoWarning: 'Demo Mode: Any credentials work for testing. In production, real module credentials will be required. Test tokens expire automatically after 24 hours.',
      },
      upload: {
        dragDrop: 'Drag & drop files',
        dropHere: 'Drop files here...',
        clickToBrowse: 'or click to browse • PDF, Images • Max 50MB per file',
        filesCount: '{0} file{1}',
        minDocumentsHint: 'Upload 2 or more documents to validate correlation for ATLAS submission.',
      },
      file: {
        ready: 'Ready',
        fileXofY: 'File {0} of {1}',
        imagePreview: '🖼️ Image Preview',
        pdfDocument: '📄 PDF Document',
        annotate: 'Annotate with Magic Marker',
        remove: 'Remove file',
      },
      processing: {
        start: 'Start Processing',
        inProgress: 'Processing...',
        uploadingFiles: 'Uploading files...',
        uploadingToServer: 'Uploading files to server...',
        uploadingFile: 'Uploading file {0} of {1}: {2}',
        processingExtracted: 'Processing extracted data...',
        filesUploadedSuccess: 'Files uploaded and processed successfully',
        checkingAuth: 'Checking authentication...',
        validatingData: 'Validating data...',
        preparingData: 'Preparing data for transmission...',
        sendingData: 'Sending data to external API...',
        complete: 'Processing complete!',
        completePercentage: '{0}% complete',
        complianceCheck: 'Running compliance check...',
        complianceCheckComplete: 'Compliance check completed',
      },
      navGuard: {
        activeReason: 'Document processing is in progress.',
        title: 'Leave this page?',
        body: '{reason} If you leave now, the work will continue in the background.',
        stay: 'Stay',
        leave: 'Leave',
      },
      viewMode: {
        table: 'Table View',
        form: 'ATLAS Form View',
      },
      actions: {
        selectAll: 'Select All Fields',
        clearAll: 'Clear All Selections',
        reset: 'Reset',
        sendData: 'Validate and Send',
        close: 'Close',
      },
      switchUser: {
        button: 'Switch User',
        title: 'Switch User',
        message: 'Prediction data is currently loaded. How would you like to proceed?',
        resetForm: 'Reset Form',
        keepForm: 'Keep Form',
        cancel: 'Cancel',
        resetFormDone: 'Form reset to original extracted data.',
        keepFormDone: 'Form data kept. User switched.',
      },
      emptyState: {
        title: 'Process documents to preview data',
        subtitle: 'Upload files and click "Start Processing" to extract data',
      },
      atlasStatus: {
        title: 'ATLAS Field Status (matches BEO ATLAS validation):',
        complete: 'Complete ({0})',
        missing: 'Missing ({0})',
        note: 'Note: Yellow fields have values but need verification',
      },
      fieldStatus: {
        required: 'Required',
        verify: 'Verify',
        aiPowered: 'AI-Powered',
        autoClassify: 'Auto-classify with AI',
        enterRequired: 'Enter value (required)',
      },
      tableView: {
        fieldName: 'Field Name',
        extractedValue: 'Extracted Value',
        mlPrediction: 'ML Prediction',
        applyAll: 'Apply All',
        applyAllTooltip: 'Apply all ML predictions to form fields',
      },
      sendPartial: {
        label: 'Send Partial Data',
        description: 'Send the partial data from here and later add/modify from ATLAS to create declaration',
      },
      dialogs: {
        magicMarker: 'Magic Marker Pro - Document Annotation',
        complianceReport: 'Compliance Report',
      },
      toasts: {
        loadModulesFailed: 'Failed to load modules',
        uploadFailed: 'Failed to upload files',
        fileRemoved: 'File removed',
        selectModuleFirst: 'Please select a module and upload files first',
        filesUploaded: '{0} file(s) uploaded successfully',
        authExpired: 'Authentication expired for {0}. Please re-authenticate.',
        moduleAuthenticated: 'Module "{0}" is now authenticated for 24 hours!',
        documentRouted: 'Document routed to: {0}',
        complianceCompliant: '✅ Document is compliant with all regulations',
        complianceIssues: '⚠️ Compliance issues detected! Review the report.',
        complianceReview: '⚠️ Document requires compliance review',
        complianceCheckFailed: 'Could not complete compliance check',
        complianceExported: 'Compliance report exported',
        readyForNext: 'Ready to process another document',
        noPositionData: 'No position data found to classify',
        processingCancelled: 'Processing cancelled',
        cancellingProcessing: 'Cancelling processing...',
        complianceCheckComplete: 'Compliance check completed!',
        complianceResultsFailed: 'Failed to get compliance results',
        complianceTimeout: 'Compliance check is taking longer than expected',
        filesUploadedProcessed: '{0} file(s) uploaded and processed successfully',
        dataSentSuccess: '✅ Data sent to {0} successfully!',
        dataSentWithDetails: '✅ Data sent to {0} successfully!\n📄 Files: {1} processed\n📊 Transaction ID: {2}',
        hsCodeApplied: 'HS Code {0} applied ({1}% confidence)',
        classifyingProducts: 'Classifying {0} products... This may take up to 2 minutes.',
        classificationSuccess: 'Successfully classified {0} out of {1} positions{2, select, 0{} other{ ({2} failed)}}',
        classificationFailed: 'Classification failed for all {0} positions. Check console for details.',
      },
      successDialog: {
        title: 'Data Sent Successfully!',
        message: 'Your data has been successfully sent to the module.',
        transactionDetails: 'Transaction Details',
        module: 'Module',
        filesProcessed: 'Files Processed',
        transactionId: 'Transaction ID',
        processAnother: 'Would you like to process another document?',
        goToDashboard: 'Go to Dashboard',
        processAnotherButton: 'Process Another',
      },
      atlasValidation: {
        title: 'ATLAS Declaration Validation',
        percentComplete: '{0}% Complete',
        allFieldsComplete: 'All required ATLAS fields are complete. Ready to submit to customs.',
        missingFields: '{0} required field(s) missing. Cannot submit until all fields are filled.',
        missingRequiredFields: 'Missing Required Fields',
        completeFields: 'Complete Fields ({0})',
        pleaseVerify: 'Please Verify',
        verifyRecipient: 'Please verify recipient information is correct',
        verifyHsCode: 'Please verify HS code classification is accurate',
        requiredDocuments: 'Required Documents for ATLAS Export',
        andMore: '... and {0} more',
        sections: {
          header: 'Header Information',
          exporter: 'Exporter (Your Company)',
          recipient: 'Recipient (Buyer)',
          declarant: 'Declarant (Anmelder)',
          delivery: 'Delivery Details',
          shipment: 'Shipment Details',
          lineItems: 'Line Items',
        },
        documents: {
          commercialInvoice: 'Commercial Invoice',
          packingList: 'Packing List (if multiple items)',
          certificateOfOrigin: 'Certificate of Origin (for certain countries)',
          exportLicense: 'Export License (for controlled goods)',
        },
      },
      atlasForm: {
        sections: {
          header: 'Header Information',
          headerDesc: 'Invoice details and declaration type',
          exporter: 'Exporter (Your Company)',
          exporterDesc: 'Your company information - auto-filled from profile',
          recipient: 'Recipient (Buyer)',
          recipientDesc: 'Buyer company information',
          declarant: 'Declarant (Anmelder)',
          declarantDesc: 'Declaration filing party information',
          delivery: 'Delivery Details',
          deliveryDesc: 'Export destination and delivery terms',
          shipment: 'Shipment Details',
          shipmentDesc: 'Package count, weight, and transport mode',
          lineItems: 'Line Items',
          lineItemsDesc: 'Product details, quantities, and values',
          borderTransport: 'Cross-border means of transport',
        },
        fieldGroups: {
          declaration: 'Declaration & identifiers',
          customsOffices: 'Customs offices',
          transaction: 'Transaction & invoice',
          countries: 'Countries',
          delivery: 'Delivery terms (Incoterms)',
          transport: 'Transport',
          goodsLocation: 'Goods location',
          dates: 'Dates & timing',
          security: 'Security & special',
          authorisation: 'Authorisation',
          declarant: 'Declarant',
          representative: 'Representative',
          subcontractor: 'Subcontractor',
          general: 'Other',
        },
        fieldHelpers: {
          required: 'Required field - must be filled',
          verify: 'Please verify this value',
        },
        activeFields: 'Active Fields',
        fieldsCount: '{0} fields',
        requiredCount: '{0} required',
        showOptionalFields: 'Other Fields ({0})',
        optionalCount: '{0} optional',
        noFieldsConfigured: 'No fields configured for this section',
        mlPredictionsUnavailable: 'ML predictions temporarily unavailable',
        fieldMovedToTab: '"{0}" moved to the "{1}" tab',
      },
      errors: {
        missingAtlasFields: 'Missing {0} required ATLAS fields',
        missingFieldsList: '{0} and {1} more',
        fillRedFields: 'Please fill all RED fields before submitting.',
        authenticationRequired: 'Authentication Required',
        authRequiredFor: 'Authentication required for {0}. Please authenticate first.',
        tokenExpired: 'Authentication token expired for {0}. Please re-authenticate.',
        validationFailed: 'Validation failed: {0} errors',
        complianceAuthRequired: 'BEO Sanctions authentication is required to perform automatic compliance screening on your documents. You can authenticate now or skip this step and continue without compliance checks.',
        continueWithout: 'Continue Without',
        authenticateNow: 'Authenticate Now',
        generalErrorsTitle: 'Validation Errors - {0} issue(s) found',
        generalErrorsDescription: 'The following errors could not be mapped to specific form fields. Please review and correct them:',
        fixFormErrors: 'Please fix the form errors highlighted below and try again.',
      },
      fieldsSelected: '{0} of {1} fields selected',
      classifying: 'Classifying...',
      autoClassifyHsCodes: 'Auto-Classify HS Codes',
      enableComplianceScreening: 'Enable Compliance Screening',
      complianceScreeningDesc: 'Automatically screen entities against sanctions lists (requires BEO Sanctions authentication)',
    },
    teachAI: {
      title: 'Teach AI',
      viewMode: 'View Mapped Fields',
      drawMode: 'Draw Mappings',
      resetRules: 'Reset Rules',
      loadingPdf: 'Loading PDF…',
      loadingRules: 'Loading rules…',
      extractingText: 'Extracting text…',
      drawModeHint: 'Draw Mode — drag on the PDF to select a region',
      emptyStateHint: 'Switch to <strong>Draw Mode</strong>, then drag a rectangle over any field in the PDF to create a rule.',
      extractedText: 'Extracted Text',
      mapToField: 'Map to Field',
      searchFields: 'Search fields…',
      applyMapping: 'Apply Mapping',
      noFieldsFound: 'No fields found',
      savedRules: 'Saved Rules',
      rulesSaved: '{0} rule saved',
      rulesSavedPlural: '{0} rules saved',
      onThisPage: '{0} on this page',
      ruleSelected: 'Rule selected',
      deleteHint: 'Press <kbd>Delete</kbd> or trash icon to remove:',
      hoverDeleteHint: 'Hover a rule to reveal its delete button.',
      pageLabel: 'Page {0} / {1}',
      resetAllTitle: 'Reset All Rules?',
      resetAllMessage: 'This will delete all extraction rules for this vendor template. Future documents will revert to default AI extraction.',
      resetAllWarning: 'This action cannot be undone.',
      cancel: 'Cancel',
      yesResetAll: 'Yes, Reset All',
      failedToLoadPdf: 'Failed to load PDF.',
    },
    fieldValidator: {
      noPattern: 'No validation pattern defined',
      validFormat: 'Format is valid',
      expectedFormat: 'Expected Format',
    },
    atlasFields: {
      // Kopf (Header) fields
      invoiceNumber: 'Invoice Number (LRN)',
      declarationType: 'Type of Registration',
      exportDeclarationType: 'Export Declaration Type',
      participantConstellation: 'Participant Constellation',
      declarationTimestamp: 'Declaration Timestamp',
      securityIndicator: 'Security Indicator',
      customsOfficePresentation: 'Customs Office of Presentation',
      exportCustomsOffice: 'Export Customs Office',
      totalGrossMass: 'Total Gross Mass',
      countryOfDestination: 'Country of Destination',
      incotermCode: 'Incoterm Code',
      totalInvoiceAmount: 'Total Invoice Amount',
      invoiceCurrency: 'Invoice Currency',
      invoiceDate: 'Invoice Date',
      exportCountry: 'Export Country',
      deliveryPlace: 'Delivery Place',
      domesticTransportMode: 'Domestic Transport Mode',
      borderTransportMode: 'Border Transport Mode',
      // Exporter fields
      exporterName: 'Exporter Name',
      exporterStreet: 'Exporter Street',
      exporterPostalCode: 'Exporter Postal Code',
      exporterCity: 'Exporter City',
      exporterCountry: 'Exporter Country',
      exporterEORI: 'Exporter EORI/TIN',
      // Recipient fields
      recipientName: 'Recipient Name',
      recipientStreet: 'Recipient Street',
      recipientPostalCode: 'Recipient Postal Code',
      recipientCity: 'Recipient City',
      recipientCountry: 'Recipient Country',
      recipientEORI: 'Recipient EORI/TIN',
      recipientIdentificationType: 'Recipient Identification Type',
      // Declarant (Anmelder) fields
      declarantName: 'Declarant Name',
      declarantStreet: 'Declarant Street',
      declarantPostalCode: 'Declarant Postal Code',
      declarantCity: 'Declarant City',
      declarantCountry: 'Declarant Country',
      declarantEORI: 'Declarant EORI/TIN',
      declarantAuthorizedName: 'Declarant Authorized Name',
      declarantContactPerson: 'Declarant Contact Person',
      declarantPhone: 'Declarant Phone',
      declarantBranchNumber: 'Declarant Branch Number',
      declarantIdentificationType: 'Declarant Identification Type',
      // Declaration Details fields
      containerIndicator: 'Container Indicator',
      numberOfPositions: 'Number of Positions',
      referenceNumber: 'Reference Number',
      eoriBranchNumber: 'EORI Branch Number',
      // Customs/Transport fields
      intendedExitCustomsOffice: 'Intended Exit Customs Office',
      supplementaryExportCustomsOffice: 'Supplementary Export Customs Office',
      loadingPlaceCode: 'Loading Place Code',
      borderTransportModeType: 'Border Transport Mode Type',
      borderTransportNationality: 'Border Transport Nationality',
      // Shipment fields
      numberOfPackages: 'Number of Packages',
      totalGrossWeight: 'Total Gross Weight (kg)',
      transportMode: 'Transport Mode',
      transitCountries: 'Transit Countries',
      // Delivery fields
      destinationCountry: 'Destination Country',
      incoterm: 'Incoterm',
      deliveryTermsText: 'Delivery Terms Text',
      transportCostPaymentMethod: 'Transport Cost Payment Method',
      // Timestamps / dates
      relevantDate: 'Relevant Date',
      dateOfExit: 'Date of Exit',
      presentationTimestamp: 'Presentation Timestamp',
      endOfLoadingTimestamp: 'End of Loading Timestamp',
      // Location (Warenort) fields
      locationType: 'Location Type',
      locationDeterminationType: 'Location Determination Type',
      locationAuthorizationNumber: 'Location Authorization Number',
      // Departure transport fields
      transportIdTypeAtDeparture: 'Transport ID Type at Departure',
      vehicleRegistrationAtDeparture: 'Vehicle Registration at Departure',
      vehicleNationalityAtDeparture: 'Vehicle Nationality at Departure',
      // Recipient extra
      recipientBranchNumber: 'Recipient Branch Number',
      recipientTINEORI: 'Recipient TIN/EORI',
      // Position fields
      positionNumber: 'Position Number',
      requestedProcedure: 'Requested Procedure',
      previousProcedure: 'Previous Procedure',
      countryOfOrigin: 'Country of Origin',
      productDescription: 'Product Description',
      hsCode: 'HS/CN Code',
      grossWeight: 'Gross Weight (kg)',
      netWeight: 'Net Weight (kg)',
      quantity: 'Quantity',
      quantityInSpecialUnit: 'Quantity in Special Unit',
      statisticalValue: 'Statistical Value',
      packageType: 'Package Type',
      numberOfPackagesItem: 'Number of Packages',
      shippingMarks: 'Shipping Marks',
      unit: 'Unit',
      unitValue: 'Unit Value',
      totalValue: 'Total Value',
      currency: 'Currency',
      userId: 'User ID (System)',
      item: 'Item',
      // Additional fields - Header
      typeOfTransaction: 'Type of Transaction',
      natureOfTransaction: 'Nature of Transaction',
      customsOfficeNumber: 'Customs Office Number',
      specialCircumstances: 'Special Circumstances',
      additionalLocationIdentifier: 'Additional Location Identifier',
      // Additional fields - Border Transport
      borderVehicleNationality: 'Border Vehicle Nationality',
      borderVehicleRegistration: 'Border Vehicle Registration',
      // Additional fields - Representative
      representativeTIN: 'Representative EORI/TIN',
      representativeName: 'Representative Name',
      representativePhone: 'Representative Phone',
      representativeBranchNumber: 'Representative Branch Number',
      representativeContactPerson: 'Representative Contact Person',
      // Additional fields - Subcontractor
      subcontractorBranchNumber: 'Subcontractor Branch Number',
      // Authorization
      authorizationType: 'Authorization Type',
      authorizationReference: 'Authorization Reference Number',
      // System / header fields
      versionNumber: 'Version Number',
      creationDate: 'Creation Date',
      procedureCode: 'Procedure Code',
      // Sender
      senderName: 'Sender Name',
      senderStreet: 'Sender Street',
      senderPostalCode: 'Sender Postal Code',
      senderCity: 'Sender City',
      senderCountry: 'Sender Country',
      senderEORI: 'Sender EORI/TIN',
      // External Exporter
      externalExporterName: 'External Exporter Name',
      externalExporterStreet: 'External Exporter Street',
      externalExporterPostalCode: 'External Exporter Postal Code',
      externalExporterCity: 'External Exporter City',
      externalExporterCountry: 'External Exporter Country',
      externalExporterEORI: 'External Exporter EORI/TIN',
      // Additional fields - Position
      regionOfOrigin: 'Region of Origin',
      // Buttons
      exportStatus: 'Export Status',
      loadFromHistory: 'Load from History',
      applyAllPredictions: 'Apply All Predictions',
      auditDeclaration: 'Audit Declaration',
      teachAI: 'Teach AI',
      enterField: 'Enter {0}',
      selectField: 'Select {0}',
      filterTabs: {
        extracted: 'Extracted',
        aiPrefilled: 'AI Prefilled',
        errors: 'Errors',
        otherFields: 'Other Fields',
      },
      allErrors: 'All Errors',
      aiGenerated: 'AI Generated',
      fillFromHistory: 'Fill from history: "{0}"',
      errorCount: '{0} errors',
      noValidationErrors: 'No validation errors',
      allFieldsValid: 'All fields pass validation.',
    },
    submissionPreview: {
      title: 'Preview Submission Data',
      proceed: 'Proceed',
      edit: 'Cancel',
      sections: {
        registrationTypes: 'Registration Types',
        generalInformation: 'General Information',
        provisionInformation: 'Information on the Provision',
        transportMeansDetails: 'Details of the Means of Transport',
        customsOffices: 'Information on the Departments',
        businessTransaction: 'Details of the Business Transaction',
        deliveryTerms: 'Information on the Delivery Terms',
        participants: 'Participants',
        locationOfGoods: 'Information on the Location of Goods',
        locationAddress: 'Location of Goods / Address',
        positions: 'Positions',
        contactPerson: 'Contact Person',
        otherFields: 'Other Fields',
      },
      participants: {
        empfanger: 'Recipient',
        anmelder: 'Applicant',
        vertreter: 'Representative',
        subunternehmer: 'Subcontractor',
        ausfuhrer: 'Executor',
        aussenAusfuhrer: 'Exporter under Foreign Trade Law',
        beforderer: 'Carrier',
        versender: 'Sender',
      },
    },
    // English ATLAS field path labels
    atlasFieldPaths: {
      // Kopf (Header) - Main fields
      'kopf.lrn': 'Local Reference Number (LRN)',
      'kopf.artderAnmeldung': 'Declaration Type',
      'kopf.artderAusfuhranmeldung': 'Export Declaration Type',
      'kopf.beteiligtenKonstellation': 'Participant Constellation',
      'kopf.zeitpunktderAnmeldung': 'Declaration Timestamp',
      'kopf.sicherheit': 'Security',
      'kopf.gestellungszollstelle': 'Customs Office of Presentation',
      'kopf.ausfuhrzollstelleDienststellennummer': 'Export Customs Office Number',
      'kopf.inRechnunggestellterGesamtbetrag': 'Total Invoice Amount',
      'kopf.rechnungswahrung': 'Invoice Currency',
      'kopf.datum': 'Date',
      'kopf.eoriNiederlassungsnummer': 'EORI Branch Number',
      'kopf.dienststellennummer': 'Customs Office Number',
      'kopf.massgeblichesDatum': 'Relevant Date',
      'kopf.besondereUmstande': 'Special Circumstances',
      'kopf.artdesGeschafts': 'Type of Transaction',
      'kopf.ausfuhrland': 'Export Country',
      'kopf.bestimmungsLand': 'Destination Country',
      'kopf.containerIndikator': 'Container Indicator',
      'kopf.container': 'Container Indicator',
      'kopf.gesamtRohmasse': 'Total Gross Mass (kg)',
      'kopf.inlandischerVerkehrszweig': 'Domestic Transport Mode',
      'kopf.verkehrszweigAnDerGrenze': 'Border Transport Mode',
      'kopf.kopfDatumdesAusgangs': 'Date of Exit',
      'kopf.zeitpunktDerGestellung': 'Presentation Timestamp',
      'kopf.zeitpunktdesEndesderLadetatigkeit': 'End of Loading Timestamp',
      'kopf.packstuckanzahl': 'Number of Packages',
      'kopf.packstuckzeichennummern': 'Shipping Marks',
      // Delivery conditions
      'kopf.lieferbedingungIncotermCode': 'Incoterm Code',
      'kopf.lieferbedingungOrt': 'Delivery Place',
      'kopf.lieferbedingungLand': 'Delivery Country',
      'kopf.lieferbedingungUnLocode': 'Delivery UN/LOCODE',
      'kopf.lieferbedingungText': 'Delivery Terms Text',
      // Customs offices
      'kopf.ausfuhrZollstelleFurDieErganzende': 'Supplementary Export Customs Office',
      'kopf.erganzungszollstelleAusfuhrDienststellennummer': 'Supplementary Export Customs Office',
      'kopf.vorgeseheneAusgangszollstelleDienststellennummer': 'Exit Customs Office',
      // Exporter
      'kopf.ausfuhrer.name': 'Exporter Name',
      'kopf.ausfuhrer.strasse': 'Exporter Street',
      'kopf.ausfuhrer.plz': 'Exporter Postal Code',
      'kopf.ausfuhrer.ort': 'Exporter City',
      'kopf.ausfuhrer.land': 'Exporter Country',
      'kopf.ausfuhrer.tin': 'Exporter EORI/TIN',
      'kopf.ausfuhrer.telefonnummer': 'Exporter Phone',
      'kopf.ausfuhrer.fax': 'Exporter Fax',
      'kopf.ausfuhrer.email': 'Exporter Email',
      'kopf.ausfuhrer.bundesland': 'Exporter Federal State',
      // Recipient
      'kopf.empfanger.name': 'Recipient Name',
      'kopf.empfanger.strasse': 'Recipient Street',
      'kopf.empfanger.plz': 'Recipient Postal Code',
      'kopf.empfanger.ort': 'Recipient City',
      'kopf.empfanger.land': 'Recipient Country',
      'kopf.empfanger.tin': 'Recipient EORI/TIN',
      'kopf.empfanger.identifikationsart': 'Recipient ID Type',
      'kopf.empfanger.niederlassungsNummer': 'Recipient Branch Number',
      // Declarant
      'kopf.anmelder.tin': 'Declarant EORI/TIN',
      'kopf.anmelder.niederlassungsNummer': 'Declarant Branch Number',
      'kopf.anmelder.phone': 'Declarant Phone',
      'kopf.anmelder.ansprechName': 'Declarant Contact Person',
      'kopf.anmelder.beauftragterName': 'Declarant Authorized Name',
      'kopf.anmelder.ansprechpartnerName': 'Declarant Contact Person',
      'kopf.anmelder.identifikationsart': 'Declarant ID Type',
      // Representative
      'kopf.vertreter.tin': 'Representative EORI/TIN',
      'kopf.vertreter.niederlassungsNummer': 'Representative Branch Number',
      'kopf.vertreter.phone': 'Representative Phone',
      'kopf.vertreter.ansprechName': 'Representative Contact Person',
      // Subcontractor
      'kopf.subunternehmer.tin': 'Subcontractor EORI/TIN',
      'kopf.subunternehmer.niederlassungsNummer': 'Subcontractor Branch Number',
      // Authorization
      'kopf.bewilligung[0].art': 'Authorization Type',
      'kopf.bewilligung[0].referenznummer': 'Authorization Reference Number',
      // Location of goods
      'kopf.warenortArtdesOrtes': 'Location Type',
      'kopf.warenortArtDerOrtsbestimmung': 'Location Determination Type',
      'kopf.warenortZusatzlicheKennung': 'Additional Location Identifier',
      'kopf.warenortBewilligungsnummer': 'Location Authorization Number',
      // Transport
      'kopf.beforderungsmittelBeimAbgang[0].artderIdentifikation': 'Transport ID Type at Departure',
      'kopf.beforderungsmittelBeimAbgang[0].kennzeichen': 'Vehicle Registration at Departure',
      'kopf.beforderungsmittelBeimAbgang[0].staatszugehorigkeit': 'Vehicle Nationality at Departure',
      'kopf.beforderungsroute.ausgewahlteLander': 'Transit Countries',
      'kopf.beforderungsmittelderGrenzeArt': 'Border Transport Mode Type',
      'kopf.beforderungsmittelderGrenzeKennzeichen': 'Border Vehicle Registration',
      'kopf.beforderungsmittelderGrenzeStaatszugehorigkeit': 'Border Vehicle Nationality',
      'kopf.beforderungskostenZahlungsart': 'Transport Payment Method',
      // Position fields
      'warePositionsnummer': 'Position Number',
      'beantragtesVerfahren': 'Requested Procedure',
      'vorhergehendesVerfahren': 'Previous Procedure',
      'zusatzlichesVerfahren': 'Additional Procedure',
      'ursprungsland': 'Country of Origin',
      'ursprungsVersendungsregion': 'Region of Origin/Dispatch',
      'wareWarenbezeichnung': 'Product Description',
      'wareWarennummerKN8': 'HS/CN Code',
      'wareRohmasse': 'Gross Weight (kg)',
      'wareEigenmasse': 'Net Weight (kg)',
      'mengeinbesondererMabeinheit': 'Quantity in Special Unit',
      'statistischerWert': 'Statistical Value',
      'referenznummerUCR': 'UCR Reference',
      'warecusnummer': 'CUS Number',
      'registriernummerextern': 'External Registration Number',
      // Packaging
      'artderVerpackung': 'Package Type',
      'anzahlderPackstucke': 'Number of Packages',
      'versandzeichen': 'Shipping Marks',
      'packstuckverweisPositionsnummer': 'Package Reference Position',
      // Supporting documents
      'unterlage.art': 'Document Type',
      'unterlage.referenznummer': 'Document Reference',
      'unterlage.qualifikator': 'Document Qualifier',
      'unterlage.datumderAusstellung': 'Document Issue Date',
      'unterlage.gultigkeitsdatum': 'Validity Date',
      'unterlage.zusatzlicheAngaben': 'Additional Document Info',
      'unterlage.detail': 'Document Detail',
      'unterlage.mabeinheit': 'Unit of Measure',
      'unterlage.menge': 'Quantity',
      'unterlage.wahrung': 'Currency',
      'unterlage.betrag': 'Amount',
      'unterlage.namederausstellendenBehorde': 'Issuing Authority',
      'unterlage.zeilenPositionsnummer': 'Document Line Position',
      // Other references
      'sonstigerVerweis.art': 'Other Reference Type',
      'sonstigerVerweis.referenznummer': 'Other Reference Number',
      'sonstigerVerweis.qualifikator': 'Other Reference Qualifier',
      'sonstigerVerweis.detail': 'Other Reference Detail',
      'sonstigerVerweis.wahrung': 'Currency',
      'sonstigerVerweis.betrag': 'Amount',
      // Missing header fields
      'kopf.anzahlPositionen': 'Number of Positions',
      'kopf.bezugsnummer': 'Reference Number',
      'kopf.ladeort.code': 'Loading Place Code',
      'kopf.verfahren': 'Procedure Code',
      'kopf.userid': 'User ID',
      'kopf.versionsnr': 'Version Number',
      'kopf.erstelldatum': 'Creation Date',
      'kopf.sicherheitIndikator': 'Security Indicator',
      'kopf.zeitpunktDerBeendigungDerVerladetatigkeit': 'End of Loading Timestamp',
      'kopf.artEinheitsBeforderungsmittels': 'Transport Unit Type',
      // Declarant extra fields
      'kopf.anmelder.beauftragterStellung': 'Declarant Representative Status',
      'kopf.anmelder.tinCheck': 'Declarant TIN Check',
      'kopf.anmelder.name': 'Declarant Name',
      // Representative extra fields
      'kopf.vertreter.identifikationsart': 'Representative ID Type',
      'kopf.vertreter.beauftragterStellung': 'Representative Status',
      'kopf.vertreter.tinCheck': 'Representative TIN Check',
      // Subcontractor extra fields
      'kopf.subunternehmer.name': 'Subcontractor Name',
      'kopf.subunternehmer.land': 'Subcontractor Country',
      // Sender (Versender)
      'kopf.versender.name': 'Sender Name',
      'kopf.versender.strasse': 'Sender Street',
      'kopf.versender.plz': 'Sender Postal Code',
      'kopf.versender.ort': 'Sender City',
      'kopf.versender.land': 'Sender Country',
      'kopf.versender.tin': 'Sender EORI/TIN',
      'kopf.versender.niederlassungsNummer': 'Sender Branch Number',
      // External Exporter (Außenwirtschaftsrechtlicher Ausführer)
      'kopf.aussenAusfuhrer.name': 'External Exporter Name',
      'kopf.aussenAusfuhrer.strasse': 'External Exporter Street',
      'kopf.aussenAusfuhrer.plz': 'External Exporter Postal Code',
      'kopf.aussenAusfuhrer.ort': 'External Exporter City',
      'kopf.aussenAusfuhrer.land': 'External Exporter Country',
      'kopf.aussenAusfuhrer.tin': 'External Exporter EORI/TIN',
      // Item suffix
      'itemSuffix': '(Item {0})',
    },
    adminPage: {
      processingHistory: {
        title: 'Processing History',
        subtitle: 'View and manage all document processing transactions',
        tabs: {
          moduleTransactions: 'Atlas Transactions',
          tamperingDetection: 'Tampering Detection',
          emailProcessing: 'Email Processing',
        },
        filters: {
          search: 'Search by file name, module, or identifier...',
          filterByUser: 'Filter by User',
          searchUsers: 'Type to search users...',
          allUsers: 'All Users',
          status: 'Status',
          allStatus: 'All Status',
          dateFrom: 'Date From',
          dateTo: 'Date To',
          clearFilters: 'Clear Filters',
        },
        table: {
          status: 'Status',
          user: 'User',
          file: 'File',
          module: 'Module',
          type: 'Type',
          dataIdentifier: 'Data Identifier',
          ipAddress: 'IP Address',
          processedAt: 'Processed At',
          processingTime: 'Processing Time',
          fileView: 'File View',
          actions: 'Actions',
          noFile: 'No file',
        },
        expandedView: {
          requestDetails: 'Request Details',
          responseDetails: 'Response Details',
          extractedData: 'Extracted Data',
          apiResponse: 'API Response',
          transactionId: 'Transaction ID',
          clientIp: 'Client IP',
          userAgent: 'User Agent',
          createdAt: 'Created At',
          completedAt: 'Completed At',
        },
        dialog: {
          title: 'Transaction Details',
        },
        statusValues: {
          success: 'Success',
          failed: 'Failed',
          processing: 'Processing',
          pending: 'Pending',
          completed: 'Completed',
        },
        kpi: {
          totalDocuments: 'Total documents',
          documentProcessing: 'Document processing',
          tariffClassification: 'Tariff classification',
          emailProcessing: 'Email processing',
        },
        types: {
          apiPost: 'API Post',
          extraction: 'Extraction',
        },
        toolbar: {
          refresh: 'Refresh',
          filters: 'Filters',
          viewDetails: 'View details',
          transactionsOne: '{{count}} transaction',
          transactionsOther: '{{count}} transactions',
        },
        emptyState: {
          noneTitle: 'No transactions yet',
          noneBody: 'Upload and process documents to see your transaction history here.',
          noMatchTitle: 'No matching transactions',
          noMatchBody: 'Try adjusting your search or filters to find what you are looking for.',
          clearSearchFilters: 'Clear search & filters',
        },
      },
      auditLogs: {
        title: 'Audit Logs',
        subtitle: 'System activity and security monitoring',
        filters: {
          search: 'Search logs...',
          eventType: 'Event Type',
          allEvents: 'All Events',
          user: 'User',
          allUsers: 'All Users',
          dateFrom: 'Date From',
          dateTo: 'Date To',
        },
        statistics: {
          totalEvents: 'Total Events',
          userActions: 'User Actions',
          systemEvents: 'System Events',
          securityEvents: 'Security Events',
        },
        table: {
          timestamp: 'Timestamp',
          user: 'User',
          event: 'Event',
          ipAddress: 'IP Address',
          details: 'Details',
        },
      },
      atlasStructure: {
        title: 'ATLAS Structure',
        subtitle: 'View BEO ATLAS declaration structure and field definitions',
        search: 'Search fields...',
        expandAll: 'Expand All',
        collapseAll: 'Collapse All',
        sections: {
          header: 'Header (kopf)',
          exporter: 'Exporter (ausfuhrer)',
          recipient: 'Recipient (empfaenger)',
          delivery: 'Delivery (lieferung)',
          shipment: 'Shipment (sendung)',
          lineItems: 'Line Items (position)',
        },
        fieldInfo: {
          path: 'Field Path',
          type: 'Type',
          required: 'Required',
          description: 'Description',
          example: 'Example',
          yes: 'Yes',
          no: 'No',
        },
      },
    },
    userProfile: {
      modules: {
        title: 'Your Modules',
        subtitle: 'Manage your module access and authentication',
        noModules: 'No modules assigned',
        authenticate: 'Authenticate',
        authenticated: 'Authenticated',
        expired: 'Expired',
        notAuthenticated: 'Not Authenticated',
      },
    },
    emailProcessorPage: {
      title: 'Email Attachment Processor',
      subtitle: 'Automatically monitor email accounts and process attachments with AI-powered document extraction',
      tabs: {
        configuration: 'Email Configuration',
        threads: 'Email Threads',
        statistics: 'Statistics',
      },
    },
    documentCombiner: {
      title: 'Document Combination Review',
      steps: {
        reviewData: 'Review Data',
        resolveConflicts: 'Resolve Conflicts',
        validate: 'Validate',
        submit: 'Submit',
      },
      alerts: {
        noConflicts: 'No conflicts detected. All data merged successfully.',
        conflictsFound: '{0} field{1} conflicting values. Please select the correct value for each.',
        resolveAllConflicts: 'Please resolve all conflicts before proceeding',
        reviewMergedData: 'Review the merged data from {0} documents. You can edit any field by clicking the edit icon.',
      },
      sections: {
        commercialData: 'Commercial Data',
        invoiceData: 'Invoice Data',
        goodsInformation: 'Goods Information',
        transportData: 'Transport Data',
      },
      fields: {
        seller: 'Seller',
        sellerAddress: 'Seller Address',
        sellerCountry: 'Seller Country',
        sellerEori: 'Seller EORI',
        buyer: 'Buyer',
        buyerAddress: 'Buyer Address',
        buyerCountry: 'Buyer Country',
        invoiceNumber: 'Invoice Number',
        invoiceDate: 'Invoice Date',
        currency: 'Currency',
        value: 'Value',
        paymentTerms: 'Payment Terms',
        goodsDescription: 'Goods Description',
        hsCode: 'HS Code',
        quantity: 'Quantity',
        netWeight: 'Net Weight',
        grossWeight: 'Gross Weight',
        countryOfOrigin: 'Country of Origin',
        transportMode: 'Transport Mode',
        billOfLading: 'Bill of Lading',
        awbNumber: 'AWB Number',
        containerNumber: 'Container Number',
        vesselName: 'Vessel Name',
        portOfLoading: 'Port of Loading',
        portOfDischarge: 'Port of Discharge',
      },
      conflict: {
        from: 'From',
        confidence: 'Confidence',
      },
      validation: {
        title: 'Data Validation',
        confidenceScore: 'Confidence Score',
        requiredFields: 'Required Fields',
        allFieldsPresent: 'All required fields present',
        missingFields: 'Missing fields: {0}',
        mergedDataSummary: 'Merged Data Summary',
        parties: 'Parties',
        from: 'From',
        to: 'To',
        invoice: 'Invoice',
        number: 'Number',
        goods: 'Goods',
        notAvailable: 'N/A',
      },
      actions: {
        back: 'Back',
        next: 'Next',
        submitToAtlas: 'Submit to BEO ATLAS',
        tryAgain: 'Try Again',
      },
      submission: {
        title: 'Submit to BEO ATLAS',
        chooseMode: 'Choose submission mode and add an optional reference.',
        testMode: 'Test Mode',
        production: 'Production',
        customReference: 'Custom Reference (Optional)',
        success: 'Successfully Submitted!',
        submissionId: 'Submission ID: {0}',
        failed: 'Submission Failed',
      },
      processing: 'Processing documents...',
    },
    complianceChecker: {
      title: 'AI-Powered Compliance Check',
      subtitle: 'Mistral AI Enhanced',
      options: {
        semanticMatching: 'Semantic Matching',
        semanticMatchingTooltip: '95% accuracy using mistral-embed for entity matching',
        autoEntityExtraction: 'Auto Entity Extraction',
        autoEntityExtractionTooltip: 'Automatically extract entities using mistral-large',
        riskAssessment: 'Risk Assessment',
        riskAssessmentTooltip: 'AI-powered risk pattern analysis',
      },
      steps: {
        uploadDocument: 'Upload Document',
        documentProcessing: 'Document Processing',
        entityExtraction: 'Entity Extraction',
        complianceAnalysis: 'Compliance Analysis',
        results: 'Results',
      },
      upload: {
        dragDrop: 'Drag & drop a document',
        dropHere: 'Drop the document here',
        clickToSelect: 'or click to select (PDF, PNG, JPG)',
      },
      processing: {
        withMistralOcr: 'Processing document with Mistral OCR...',
        runningAnalysis: 'Running AI-powered compliance analysis...',
        success: 'Document processed successfully: {0}',
      },
      extraction: {
        aiExtracted: 'AI extracted {0} entities and {1} products with {2}% confidence',
        entities: 'Entities ({0})',
        products: 'Products ({0})',
        transactionDetails: 'Transaction Details',
        type: 'Type',
        route: 'Route',
        totalValue: 'Total Value',
        reference: 'Reference',
        hsCode: 'HS',
        quantity: 'Qty',
      },
      actions: {
        runComplianceCheck: 'Run AI Compliance Check',
        checkAnother: 'Check Another Document',
      },
      results: {
        status: 'Status: {0}',
        riskAssessment: 'Risk Assessment',
        riskScore: 'Risk Score',
        riskLevel: 'Risk Level',
        highestRisks: 'Highest Risks',
        aiRecommendations: 'AI Recommendations',
      },
      toasts: {
        processing: '🔍 Processing document...',
        extracting: '🤖 Processing extracted data...',
        foundEntities: '✨ Found {0} entities and {1} products!',
        runningCheck: '🚀 Running compliance check...',
        checkPassed: '✅ Compliance check passed!',
        issuesDetected: '⚠️ Compliance issues detected!',
        reviewRequired: '📋 Review required',
        timeout: 'Document processing timed out. Please try again with a smaller file.',
        extractionFailed: 'Unable to extract data from document. Please ensure the document is valid.',
        checkTimeout: 'Compliance check timed out. Please try again.',
        serviceUnavailable: 'Compliance service unavailable. Please try again later.',
        failed: 'Failed to process document - please try again',
        checkFailed: 'Failed to run compliance check',
        noDocumentText: 'No document text available',
      },
    },
    documentAnnotator: {
      title: 'Annotations',
      statistics: {
        totalAnnotations: '{0} Annotations',
        criticalCount: '{0} Critical',
        missingCount: '{0} Missing',
        complete: '{0}% Complete',
        complianceIssues: '{0} compliance issues found',
      },
      modes: {
        view: 'View',
        edit: 'Edit',
      },
      types: {
        critical: 'Critical',
        compliance: 'Compliance',
        missing: 'Missing',
        workflow: 'Workflow',
        comment: 'Comment',
      },
      priority: {
        critical: 'Critical',
        high: 'High',
        medium: 'Medium',
        low: 'Low',
      },
      status: {
        resolved: 'Resolved',
        unresolved: 'Unresolved',
      },
      actions: {
        refresh: 'Refresh',
        export: 'Export',
        toggleAnnotations: 'Toggle Annotations',
        toggleCompliance: 'Toggle Compliance',
        toggleRouting: 'Toggle Routing',
        startCollaboration: 'Start Collaboration',
        saveAnnotation: 'Save Annotation',
        resolveAnnotation: 'Resolve Annotation',
      },
      messages: {
        loading: 'Loading annotations...',
        noDocumentContent: 'No document content available',
        pdfNotAvailable: 'PDF preview not available. Showing extracted data:',
        success: 'Document annotated successfully',
        annotationAdded: 'Annotation added',
        annotationResolved: 'Annotation resolved',
        collaborationStarted: 'Collaboration started',
        annotationsExported: 'Annotations exported',
        failed: 'Failed to load annotations',
        addFailed: 'Failed to add annotation',
        resolveFailed: 'Failed to resolve annotation',
        collaborationFailed: 'Failed to start collaboration',
        exportFailed: 'Failed to export annotations',
      },
      collaboration: {
        title: 'Collaborative Annotation',
        realTime: 'Real-time collaboration enabled',
        participants: 'Participants',
        activeUsers: 'Active Users',
      },
      compliance: {
        title: 'Compliance Report',
        compliant: 'Compliant',
        nonCompliant: 'Non-Compliant',
        requiresReview: 'Requires Review',
      },
      routing: {
        title: 'Document Routing',
        decision: 'Routing Decision',
        destination: 'Destination',
        priority: 'Priority',
      },
    },
    profile: {
      title: 'My Profile',
      personalInformation: 'Personal Information',
      accountStatus: 'Account Status',
      planFeatures: 'Plan Features',
      billingInformation: 'Billing Information',
      firstName: 'First Name',
      lastName: 'Last Name',
      phoneNumber: 'Phone Number',
      email: 'Email',
      fullName: 'Full Name',
      username: 'Username',
      memberSince: 'Member Since',
      active: 'Active',
      inactive: 'Inactive',
      lastUpdated: 'Last Updated',
      administrator: 'Administrator',
      user: 'User',
      essential: 'Essential',
      advanced: 'Advanced',
      enterprise: 'Enterprise',
      planAdopted: 'Plan Adopted',
      nextBilling: 'Next Billing Date',
      paymentStatus: 'Payment Status',
      paymentMethod: 'Payment Method',
      paid: 'Paid',
      pending: 'Pending',
      overdue: 'Overdue',
      failed: 'Failed',
      notSet: 'Not set',
      settingsHintTitle: 'Modules, Preferences & ATLAS Configuration',
      settingsHintSubtitle: 'Manage module authentication, email language and ATLAS settings in Settings',
      paymentReference: 'Payment Reference',
      billingAddress: 'Billing Address',
      advancedExtraction: 'Advanced AI Extraction',
      tamperingDetection: 'Document Tampering Detection',
      workflowAutomation: 'Workflow Automation',
      prioritySupport: 'Priority Support',
      customApiDevelopment: 'Custom API Development',
      basicExtraction: 'Basic Extraction',
      enabled: 'Enabled',
      notAvailable: 'Not available',
      support24x7: '24/7 Support',
      standardSupport: 'Standard support',
      available: 'Available',
      // Usage Statistics
      usageStatistics: 'Usage Statistics',
      documentsUsedThisMonth: 'Documents Used This Month',
      usagePercentage: 'Usage Percentage',
      daysUntilReset: 'Days Until Reset',
      dataRetentionPeriod: 'Data Retention Period',
      managePlan: 'Manage Plan',
      planSummary: 'Plan Summary',
      usageNearLimit: 'You are approaching your monthly limit',
      usageAtLimit: 'You have reached your monthly limit',
      usageExceeded: 'You have exceeded your monthly limit',
      documentsRemaining: '{0} documents remaining',
      unlimitedDocuments: 'Unlimited documents',
      daysSingular: 'day',
      daysPlural: 'days',
      // ATLAS Configuration
      atlasConfiguration: 'ATLAS Configuration',
      atlasCredentials: 'ATLAS Credentials',
      complete: 'Complete',
      incomplete: 'Incomplete',
      atlasConfigDescription: "Configure your company's ATLAS credentials for automated customs declarations. These settings are required for BEO ATLAS module submissions.",
      atlasUserId: 'ATLAS User ID',
      atlasUserIdHelper: "Your company's ATLAS user ID",
      eoriNumber: 'EORI Number',
      eoriHelper: 'Economic Operators Registration and Identification number',
      customsOffice: 'Customs Office',
      customsOfficeHelper: 'Your designated customs office code',
      declarationOffice: 'Declaration Office',
      declarationOfficeHelper: 'Office code for declarations',
      exportOffice: 'Export Office',
      exportOfficeHelper: 'Export office code',
      companyInformation: 'Company Information',
      companyName: 'Company Name',
      companyNameHelper: 'Full legal company name',
      streetAddress: 'Street Address',
      streetAddressHelper: 'Company street address',
      postalCode: 'Postal Code',
      postalCodeHelper: 'Postal/ZIP code',
      city: 'City',
      cityHelper: 'City name',
      countryCode: 'Country Code',
      countryCodeHelper: 'ISO country code (e.g., DE, FR)',
      defaultSettings: 'Default Settings',
      currency: 'Currency',
      currencyHelper: 'Default currency for transactions',
      incoterm: 'Incoterm',
      incotermHelper: 'Default Incoterms (e.g., FOB, CIF)',
      originCountry: 'Origin Country',
      originCountryHelper: 'Default origin country for goods',
      transportMode: 'Transport Mode',
      transportModeHelper: 'Default transport mode',
      transportModes: {
        sea: 'Sea',
        rail: 'Rail',
        road: 'Road',
        air: 'Air',
        inlandWaterway: 'Inland Waterway',
      },
      countries: {
        germany: 'Germany (DE)',
        china: 'China (CN)',
        france: 'France (FR)',
        usa: 'USA (US)',
        uk: 'United Kingdom (GB)',
      },
      saveConfiguration: 'Save Configuration',
      saving: 'Saving...',
      configSaved: 'ATLAS configuration saved successfully',
      configSaveFailed: 'Failed to save ATLAS configuration',
    },
    auth: {
      login: 'Login',
      register: 'Register',
      logout: 'Logout',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      firstName: 'First Name',
      lastName: 'Last Name',
      phoneNumber: 'Phone Number',
      rememberMe: 'Remember me',
      forgotPassword: 'Forgot password?',
      signIn: 'Sign In',
      signUp: 'Sign Up',
      createAccount: 'Create Account',
      alreadyHaveAccount: 'Already have an account?',
      dontHaveAccount: "Don't have an account?",
      signingIn: 'Signing in...',
      username: 'Username',
      usernameOrEmail: 'Username or Email',
      enterUsername: 'Enter your username or email',
      enterPassword: 'Enter your password',
      loginSuccessful: 'Login successful!',
      registrationSuccessful: 'Registration successful',
      logoutSuccessful: 'Logout successful',
      // Validation messages
      usernameRequired: 'Please enter your username or email address',
      usernameMinLength: 'Username or email must be at least 3 characters long',
      usernameMaxLength: 'Username or email is too long',
      passwordRequired: 'Please enter your password',
      passwordEmpty: 'Password cannot be empty',
      passwordMaxLength: 'Password is too long',
      // Error messages
      invalidCredentials: 'Invalid email or password',
      credentialsNoMatch: 'Credentials do not match. Please double-check your username/email and password. If you forgot your password, click "Forgot Password?" below.',
      invalidUsernamePassword: 'Invalid username or password. Your credentials do not match our records. Please verify and try again.',
      accountInactive: 'Your account is inactive. Please contact support to reactivate your account.',
      invalidPasswordFormat: 'Invalid password format. Please enter a valid password.',
      invalidUsernameFormat: 'Invalid username format. Please enter a valid username or email address.',
      invalidLoginInfo: 'Invalid login information. Please verify your credentials and try again.',
      accessDenied: 'Access denied. Your account may be suspended. Please contact support for assistance.',
      serviceUnavailable: 'Login service is temporarily unavailable. Please try again in a few moments.',
      invalidFormat: 'Please enter valid username/email and password. Check your input format.',
      tooManyAttempts: 'Too many login attempts. Please wait a few minutes before trying again.',
      serverError: 'Server error occurred. Please try again later or contact support if the problem persists.',
      networkError: 'Unable to connect to the server. Please check your internet connection and try again.',
      connectionError: 'Network connection error. Please check your internet connection and try again.',
      timeout: 'Request timed out. Please check your connection and try again.',
      passwordsDoNotMatch: 'Passwords do not match',
      passwordTooShort: 'Password must be at least 8 characters',
      emailRequired: 'Email is required',
      firstNameRequired: 'First name is required',
      lastNameRequired: 'Last name is required',
      invalidPhoneNumber: 'Please enter a valid phone number',
      // Additional validation messages
      invalidEmailFormat: 'Invalid email format',
      usernameMinChars: 'Username must be at least 3 characters',
      usernameMaxChars: 'Username must be less than 50 characters',
      usernameInvalidChars: 'Username can only contain letters, numbers, and underscores',
      passwordMinChars: 'Password must be at least 8 characters',
      passwordMaxChars: 'Password must be less than 100 characters',
      passwordUppercase: 'Password must contain at least one uppercase letter',
      passwordLowercase: 'Password must contain at least one lowercase letter',
      passwordDigit: 'Password must contain at least one digit',
      passwordsMustMatch: 'Passwords must match',
      // Form labels
      selectPlan: 'Select Plan',
      phoneOptional: 'Phone Number (Optional)',
      planHelper: 'Choose the plan that best fits your needs',
      // Plan names (already exist in plans section, reference them)
      // Buttons and actions
      haveAccount: 'Already have an account?',
      noAccount: "Don't have an account?",
      signInHere: 'Sign in here',
      registerHere: 'Register here',
      // Register modal
      registerSubtitle: 'Join us today and start processing your documents efficiently.',
      creating: 'Creating...',
      // Login modal
      loginSubtitle: 'Welcome back! Please sign in to your account.',
      // Error messages for login
      loginFailedCredentials: 'Login failed. Your credentials do not match. Please check your username/email and password.',
      credentialsMismatch: 'Credentials do not match. Please double-check your username/email and password. If you forgot your password, click "Forgot Password?" below.',
      accountCreated: 'Your account has been created successfully.',
      pendingApproval: 'Your account is pending admin approval. You will receive a notification when your account is activated.',
      approvalNote: '💡 You will be able to log in once an administrator approves your account.',
      registrationSuccess: 'Registration successful! Welcome aboard!',
      registrationFailed: 'Registration failed. Please try again.',
      validationFailed: 'Validation failed',
      confirmPasswordRequired: 'Please confirm your password',
      // Plan details
      plans: {
        essential: 'Essential - €29/month',
        advanced: 'Advanced - €79/month',
        enterprise: 'Enterprise - €199/month',
        essentialDesc: 'Up to 500 documents/month, basic extraction, standard support',
        advancedDesc: 'Up to 2,500 documents/month, AI extraction, priority support, workflow automation',
        enterpriseDesc: 'Unlimited documents, enterprise features, 24/7 support, custom development',
      },
    },
    admin: {
      title: 'Admin Dashboard',
      userManagement: 'User Management',
      moduleManagement: 'Module Management',
      groupManagement: 'Group Management',
      systemSettings: 'System Settings',
      analytics: 'Analytics',
      users: 'Users',
      superAdmins: 'Super Admins',
      modules: 'Modules',
      transactions: 'Transactions',
      reports: 'Reports',
      settings: 'Settings',
      groups: 'Groups',
      auditLogs: 'Audit Logs',
      totalUsers: 'Total Users',
      activeUsers: 'Active Users',
      totalModules: 'Total Modules',
      totalGroups: 'Total Groups',
      activeModules: 'Active Modules',
      totalTransactions: 'Total Transactions',
      createModule: 'Create Module',
      createGroup: 'Create Group',
      createUser: 'Create User',
      editUser: 'Edit User',
      deleteUser: 'Delete User',
      name: 'Name',
      description: 'Description',
      status: 'Status',
      created: 'Created',
      actions: 'Actions',
      username: 'Username',
      email: 'Email',
      role: 'Role',
      joined: 'Joined',
      members: 'Members',
      active: 'Active',
      inactive: 'Inactive',
      admin: 'Admin',
      user: 'User',
      confirmDelete: 'Confirm Delete',
      deleteConfirmMessage: 'Are you sure you want to delete this {0}? This action cannot be undone.',
      module: 'module',
      group: 'group',
      item: 'item',
      resetPassword: 'Reset Password',
      userDetails: 'User Details',
      passwordReset: 'Password Reset',
      newPassword: 'New Password',
      passwordRequirements: 'Password Requirements',
      chars8Plus: '8+ chars',
      mixedCase: 'Mixed case',
      numbers: 'Numbers',
      symbols: 'Symbols',
      resetPasswordButton: 'Reset Password',
      resetting: 'Resetting...',
      createSecurePassword: 'Create a new secure password for',
      planDetails: 'Plan Details',
      planType: 'Plan Type',
      planStatus: 'Plan Status',
      planAdopted: 'Plan Adopted',
      monthlyLimit: 'Monthly Limit',
      documentsUsed: 'Documents Used',
      limitResetDate: 'Limit Reset Date',
      planFeatures: 'Plan Features',
      essential: 'Essential',
      advanced: 'Advanced',
      enterprise: 'Enterprise',
      suspended: 'Suspended',
      expired: 'Expired',
      advancedAiExtraction: 'Advanced AI Extraction',
      tamperingDetection: 'Tampering Detection',
      workflowAutomation: 'Workflow Automation',
      prioritySupport: 'Priority Support',
      customApiDevelopment: 'Custom API Development',
      basicExtraction: 'Basic Extraction',
      editLimit: 'Edit Limit',
      saveLimit: 'Save Limit',
      cancelEdit: 'Cancel Edit',
      extendLimit: 'Extend Limit',
      resetUsage: 'Reset Usage',
      enterLimit: 'Enter limit or "unlimited"',
      limitUpdated: 'User limits updated successfully',
      limitExtended: 'Extended limit by {0} documents',
      usageReset: 'User usage reset successfully',
      updateLimitsFailed: 'Failed to update user limits',
      extendLimitFailed: 'Failed to extend user limit',
      resetUsageFailed: 'Failed to reset user usage',
      editResetDate: 'Edit Reset Date',
      saveResetDate: 'Save Reset Date',
      resetDateUpdated: 'Reset date updated successfully',
      updateResetDateFailed: 'Failed to update reset date',
      nextMonth: 'Next Month',
      today: 'Today',
      // User Form
      userForm: {
        createUser: 'Create New User',
        editUser: 'Edit User',
        userInformation: 'User Information',
        firstName: 'First Name',
        lastName: 'Last Name',
        email: 'Email',
        username: 'Username',
        phoneNumber: 'Phone Number (optional)',
        password: 'Password',
        newPasswordHint: 'New Password (leave empty to keep current)',
        administratorPrivileges: 'Administrator privileges',
        activeUser: 'Active User',
        inactiveUser: 'Inactive User',
        authenticated: 'AUTHENTICATED',
        blocked: 'BLOCKED',
        userCanLogin: 'User can log in and access the system',
        userBlocked: 'User is blocked from logging in',
        authenticationManagement: 'Authentication Management',
        moduleAuthStatus: 'Module Authentication Status',
        moduleAuthStatusDesc: 'This user may have authentication tokens stored for various modules. Use "Reset Auth" to force re-authentication with all modules.',
        moduleAccess: 'Module Access',
        moduleAccessDesc: 'Select which modules this user can access for document processing.',
        assignedModules: 'Assigned Modules',
        selectModules: 'Select modules...',
        moduleAccessHelperText: 'Choose which modules this user can access for document processing',
        noDescriptionAvailable: 'No description available',
        deactivateUser: 'Deactivate user account',
        activateUser: 'Activate user account',
        deactivate: 'Deactivate',
        activate: 'Activate',
        processing: 'Processing...',
        resetUserPassword: 'Reset user password',
        resetPasswordButton: 'Reset Password',
        resetting: 'Resetting...',
        resetAllModuleAuth: 'Reset all module authentication tokens',
        resetAuth: 'Reset Auth',
        cancel: 'Cancel',
        saving: 'Saving...',
        updateUser: 'Update User',
        createNewUser: 'Create User',
        resetUserAuthentication: 'Reset User Authentication',
        warning: 'Warning:',
        resetAuthWarning: 'This action will reset all authentication tokens for',
        performFollowingActions: 'This will perform the following actions:',
        removeAllTokens: 'Remove all module authentication tokens',
        userLoseAccess: 'User will lose access to all currently authenticated modules',
        forceReauth: 'Force re-authentication',
        userMustReauth: 'User must authenticate again with each module they want to use',
        immediateEffect: 'Immediate effect',
        cannotUndone: 'This action takes effect immediately and cannot be undone',
        useCases: 'Use cases:',
        useCasesDesc: 'Security concerns, access issues, token corruption, or policy changes',
        resetAuthentication: 'Reset Authentication',
        resetPassword: 'Reset Password',
        createSecurePassword: 'Create a new secure password for',
        passwordRequirements: 'Password Requirements',
        passwordStrength: 'Password Strength',
        weak: 'Weak',
        medium: 'Medium',
        strong: 'Strong',
        newPassword: 'New Password',
        confirmPassword: 'Confirm Password',
        passwordsDoNotMatch: 'Passwords do not match',
        securityTips: 'Security Tips:',
        securityTipsDesc: 'Use a combination of uppercase and lowercase letters, numbers, and special characters. Avoid common words or personal information.',
        // Validation messages
        invalidEmail: 'Invalid email',
        emailRequired: 'Email is required',
        usernameRequired: 'Username is required',
        usernameMinLength: 'Username must be at least 3 characters',
        firstNameRequired: 'First name is required',
        lastNameRequired: 'Last name is required',
        passwordRequired: 'Password is required',
        passwordMinLength: 'Password must be at least 6 characters',
        passwordMinLength8: 'Password must be at least 8 characters long',
        passwordTooWeak: 'Password is too weak. Please choose a stronger password.',
        // Success messages
        userUpdated: 'User updated successfully',
        userCreated: 'User created successfully',
        userSavedWithModules: 'User saved with {0} module assignments',
        modulesAssignFailed: 'User saved but module assignments failed',
        passwordResetSuccess: 'Password reset successfully',
        authResetSuccess: 'Authentication reset successfully',
        // Error messages
        saveFailed: 'Failed to save user',
        toggleStatusFailed: 'Failed to toggle user status',
        resetPasswordFailed: 'Failed to reset password',
        resetAuthFailed: 'Failed to reset user authentication',
        loadModulesFailed: 'Failed to load modules',
        tokensReset: 'Removed {0} authentication token(s) for {1}',
        pending: 'Pending',
      },
    },
    atlasStructure: {
      title: 'ATLAS Structure Viewer',
      loading: 'Loading ATLAS structure...',
      loadFailed: 'Failed to load ATLAS structure',
      documentation: {
        officialSpec: 'Official Specification',
        location: 'Location',
        usage: 'Usage',
      },
      sections: {
        positionFields: 'ATLAS Position Fields ({0} fields)',
        requiredFieldsOnly: 'Required Fields Only ({0} fields)',
        jsonTemplate: 'ATLAS JSON Template (for Mistral AI)',
        fieldTypeSummary: 'Field Type Summary',
      },
      table: {
        fieldName: 'Field Name',
        description: 'Description',
        type: 'Type',
        required: 'Required',
        source: 'Source',
        default: 'Default',
      },
      labels: {
        required: 'Required',
        optional: 'Optional',
        string: 'String',
        float: 'Float',
        array: 'Array',
      },
      fieldCounts: {
        string: 'String: {0}',
        float: 'Float: {0}',
        array: 'Array: {0}',
        required: 'Required: {0}',
        optional: 'Optional: {0}',
      },
      actions: {
        copyTemplate: 'Copy template',
        copied: 'Copied!',
        completeStructure: 'Complete ATLAS Structure',
        copyCompleteStructure: 'Copy Complete Structure',
      },
      toasts: {
        loadFailed: 'Failed to load ATLAS structure',
        copySuccess: '{0} copied to clipboard',
        copyFailed: 'Failed to copy to clipboard',
      },
    },
    errors: {
      somethingWentWrong: 'Something went wrong',
      networkError: 'Network error occurred',
      serverError: 'Server error occurred',
      notFound: 'Resource not found',
      unauthorized: 'Unauthorized access',
      forbidden: 'Access forbidden',
      validationError: 'Validation error',
      fileUploadError: 'File upload failed',
      processingError: 'Processing failed',
      tryAgain: 'Please try again',
      contactSupport: 'Contact support if the problem persists',
    },
    languages: {
      english: 'English',
      german: 'German',
      selectLanguage: 'Select Language',
    },
    notFound: {
      title: 'Page Not Found',
      message: "Sorry, the page you're looking for doesn't exist or has been moved.",
      goBack: 'Go Back',
      goHome: 'Go Home',
    },
    documentSetManager: {
      steps: {
        upload: 'Upload Documents',
        detectRelationships: 'Detect Relationships',
        reviewSubmit: 'Review & Submit',
      },
    },
    features: {
      documentProcessing: {
        title: 'Document Processing',
        subtitle: 'Upload and extract data from your documents with AI'
      },
      complianceDashboard: {
        title: 'AI Compliance Engine',
        subtitle: 'Comprehensive compliance checking with sanctions screening'
      },
      documentSetManager: {
        title: 'Cross-Document Combination',
        subtitle: 'Combine multiple documents for customs declarations'
      },
      magicMarker: {
        title: 'Magic Marker Pro',
        subtitle: 'Smart document annotation with AI assistance'
      },
      transactionHistory: {
        title: 'Transaction History',
        subtitle: 'View all your processing history and API integrations'
      },
      exportStatusPage: {
        title: 'Export Status',
        noModule: 'No module selected. Please go back and select a module first.',
        loading: 'Loading export status...',
        emptyTitle: 'No export status records found',
        emptyDescription: 'Export status records will appear here after you submit declarations.',
        refresh: 'Refresh',
        refreshing: 'Refreshing...',
        records: '{{count}} record(s)',
        authExpired: 'Authentication expired for {{module}}. Please re-authenticate and try again.',
        notAuthenticated: 'Not authenticated with {{module}}. Please authenticate first.',
        fetchError: 'Failed to fetch export status. Please try again.',
        messageNumber: 'Message Number',
        date: 'Date',
        mrnLrn: 'MRN / LRN',
        recipient: 'Recipient',
        shipmentId: 'Shipment ID',
        status: 'Status',
        releasedPdfs: 'Released PDFs',
        notificationPdfs: 'Notification PDFs',
        extPdfs: 'Ext. PDFs',
        released: 'Released',
        notification: 'Notification',
        external: 'External',
        download: 'Download {{label}} {{index}}',
        downloadFailed: 'Download failed',
        page: 'Page',
        rowsPerPage: 'Rows per page',
        allUsers: 'All Users',
        currentUser: 'Current User',
        filters: 'Filters',
        filterByLrn: 'Filter by MRN/LRN',
        filterByDate: 'Filter by Date',
        filterByStatus: 'Filter by Status',
        allStatuses: 'All Statuses',
        applyFilters: 'Apply',
        clearFilters: 'Clear',
        authenticate: 'Authenticate',
        statusRisiko: 'Risk',
        statusUeberlassen: 'Released',
        statusAusfuhranzeige: 'Export Notification',
        statusStorno: 'Cancellation',
        statusFehlermeldung: 'Error Message',
        statusStatusmeldung: 'Status Message',
      }
    },
    documentSets: {
      newDocumentSet: 'New Document Set',
    },
    tamperingDetection: {
      pageTitle: 'Document Tampering Detection',
      pageSubtitle: 'Advanced AI-powered analysis to detect forgery and manipulation',
      uploadTitle: 'Upload Document',
      uploadSubtitle: 'Select a PDF file to analyze for tampering indicators',
      dropHere: 'Drop PDF here',
      dragDrop: 'Drag & Drop PDF File',
      browseHint: 'or click to browse • PDF files only',
      analyzing: 'Analyzing document for tampering indicators...',
      pdfOnly: 'Only PDF files are supported',
      tamperingDetected: 'Tampering Detected',
      tamperingDescription: 'This document shows signs of manipulation',
      documentVerified: 'Document Verified',
      verifiedDescription: 'No tampering indicators detected',
      severityLevel: 'Severity Level',
      riskScore: 'Risk Score',
      detectedIssues: 'Detected Issues',
      analysisCompleted: 'Analysis completed in {0}ms',
      analyzeAnother: 'Analyze Another Document',
      analyzeFailed: 'Failed to analyze file',
    },
    customsDeclaration: {
      title: 'AI Customs Declaration',
      subtitle: 'Create customs declarations with AI-powered document processing',
      tabs: {
        aiUpload: 'AI Upload',
        general: 'General',
        parties: 'Parties',
        items: 'Items',
        documents: 'Documents',
        transport: 'Transport',
        summary: 'Summary'
      },
      aiUpload: {
        title: 'AI Document Upload & Extraction',
        subtitle: 'Upload documents to automatically extract customs data',
        uploadDocuments: 'Upload Documents',
        autoFillButton: 'Auto-Fill Declaration',
        confidence: 'Confidence',
        extractedData: 'Extracted Data',
        processing: 'Processing',
        completed: 'Completed',
        failed: 'Failed'
      },
      general: {
        title: 'General Information',
        subtitle: 'Enter basic declaration details',
        referenceNumber: 'Reference Number',
        declarationType: 'Declaration Type',
        export: 'Export',
        import: 'Import',
        declarationDate: 'Declaration Date',
        currency: 'Currency',
        totalValue: 'Total Value'
      },
      parties: {
        title: 'Parties Information',
        subtitle: 'Enter exporter, importer, and declarant details',
        exporter: 'Exporter',
        importer: 'Importer',
        declarant: 'Declarant',
        eori: 'EORI Number',
        name: 'Name',
        address: 'Address',
        city: 'City',
        postalCode: 'Postal Code',
        country: 'Country',
        contactPerson: 'Contact Person',
        phone: 'Phone',
        email: 'Email'
      },
      items: {
        title: 'Items & Goods',
        subtitle: 'Add and configure declaration items',
        addItem: 'Add Item',
        itemNumber: 'Item #',
        description: 'Description',
        hsCode: 'HS Code',
        quantity: 'Quantity',
        unit: 'Unit',
        netWeight: 'Net Weight (kg)',
        grossWeight: 'Gross Weight (kg)',
        value: 'Value',
        origin: 'Country of Origin'
      },
      documents: {
        title: 'Supporting Documents',
        subtitle: 'Attach required documentation',
        uploadDocument: 'Upload Document',
        documentType: 'Document Type',
        documentNumber: 'Document Number',
        documentDate: 'Document Date',
        invoice: 'Invoice',
        packingList: 'Packing List',
        certificate: 'Certificate',
        other: 'Other'
      },
      transport: {
        title: 'Transport Details',
        subtitle: 'Enter shipping and logistics information',
        transportMode: 'Transport Mode',
        sea: 'Sea',
        air: 'Air',
        road: 'Road',
        rail: 'Rail',
        vessel: 'Vessel/Flight',
        departureDate: 'Departure Date',
        arrivalDate: 'Arrival Date',
        portOfLoading: 'Port of Loading',
        portOfDischarge: 'Port of Discharge',
        incoterms: 'Incoterms'
      },
      summary: {
        title: 'Declaration Summary',
        subtitle: 'Review and submit your declaration',
        validation: 'Validation',
        exportOptions: 'Export Options',
        downloadPDF: 'Download PDF',
        exportJSON: 'Export JSON',
        submitDeclaration: 'Submit Declaration'
      },
      status: {
        draft: 'Draft',
        submitted: 'Submitted',
        processing: 'Processing',
        approved: 'Approved',
        rejected: 'Rejected'
      },
      actions: {
        save: 'Save',
        next: 'Next',
        previous: 'Previous',
        submit: 'Submit',
        cancel: 'Cancel'
      }
    },
    landing: {
      header: {
        login: 'Login',
        register: 'Register',
      },
      hero: {
        title: 'Customs Declaration Made Simple',
        subtitle: 'Upload, extract, and integrate your business documents with powerful APIs',
        getStartedFree: 'Get Started Free',
        signIn: 'Sign In',
      },
      features: {
        title: 'Powerful Features',
        subtitle: 'Everything you need to process and integrate your documents',
        documentUpload: {
          title: 'Document Upload',
          description: 'Easily upload images and PDFs for automated data extraction with drag-and-drop interface.',
        },
        smartExtraction: {
          title: 'Smart Extraction',
          description: 'Advanced AI-powered data extraction from invoices, contracts, and other business documents.',
        },
        apiIntegration: {
          title: 'API Integration',
          description: 'Seamlessly integrate with multiple external systems and APIs using OAuth authentication.',
        },
        secureCompliant: {
          title: 'Secure & Compliant',
          description: 'Enterprise-grade security with JWT authentication and role-based access control.',
        },
      },
      howItWorks: {
        title: 'How It Works',
        subtitle: 'Simple 3-step process to get your documents processed',
        step1: {
          title: 'Upload Documents',
          description: 'Simply drag and drop your PDF or image files into our secure platform.',
        },
        step2: {
          title: 'Extract Data',
          description: 'Our AI automatically extracts key information from your documents with high accuracy.',
        },
        step3: {
          title: 'Integrate & Push',
          description: 'Send the processed data to your chosen external systems via secure APIs.',
        },
      },
      modules: {
        title: 'Available Modules',
        subtitle: 'Choose from our growing collection of specialized document processors',
        invoiceProcessor: 'Invoice Processor',
        tamperingDetection: 'Document-Tampering Detection',
        emailProcessor: 'Intelligent Email Attachment Processor',
        hrDocuments: 'HR Documents',
        tariffClassifier: 'Tariff-Classifier',
        exportControl: 'AI Export Control',
        active: 'Active',
        comingSoon: 'Coming Soon',
        status: {
          inProgress: 'In Progress',
          comingSoon: 'Coming Soon',
        },
        documentProcessing: {
          name: 'Document Processing',
          description: 'Upload and process documents with AI-powered data extraction',
        },
        tamperingDetectionModule: {
          name: 'Tampering Detection',
          description: 'AI-powered detection of document forgery and manipulation',
        },
        emailProcessorModule: {
          name: 'Email Processor',
          description: 'Automatically monitor email accounts and process attachments with AI',
        },
        tariffClassifierModule: {
          name: 'Tariff-Classifier',
          description: 'ML-powered HS code determination with 98% accuracy',
        },
        aiExportControl: {
          name: 'AI Export Control',
          description: 'ECCN classification and dual-use item identification',
        },
        documentSets: {
          name: 'Document Sets',
          description: 'Intelligently combine data from multiple documents for comprehensive processing',
        },
        magicMarkerPro: {
          name: 'Magic Marker Pro',
          description: 'Smart document annotation with AI-assisted field detection',
        },
        complianceEngine: {
          name: 'Compliance Engine',
          description: 'Real-time sanctions screening and compliance validation',
        },
        aiLearningModule: {
          name: 'AI Learning Module',
          description: 'Continuous improvement engine learning from user feedback',
        },
        businessIntelligence: {
          name: 'Business Intelligence Dashboard',
          description: 'Real-time analytics and insights for trade operations',
        },
        conversationalAI: {
          name: 'Conversational AI Assistant',
          description: 'Natural language support for trade documentation queries',
        },
        workflowAutomation: {
          name: 'Workflow Automation Engine',
          description: 'Visual workflow builder for automated document processing',
        },
        blockchainIntegration: {
          name: 'Blockchain Integration',
          description: 'Immutable document verification with blockchain technology',
        },
        multiLanguageSupport: {
          name: 'Multi-Language Support',
          description: 'Process documents in 50+ languages with AI translation',
        },
        masterDataManagement: {
          name: 'Master Data Management',
          description: 'Central repository for products, suppliers, and trade data',
        },
        predictiveAnalytics: {
          name: 'Predictive Analytics',
          description: 'ML-based predictions for shipping times and costs',
        },
        smartContractGeneration: {
          name: 'Smart Contract Generation',
          description: 'Automated generation of trade agreements and contracts',
        },
        intelligentDocumentRouting: {
          name: 'Intelligent Document Routing',
          description: 'AI-based routing to appropriate processing workflows',
        },
        supplyChainVisibility: {
          name: 'Supply Chain Visibility',
          description: 'End-to-end tracking and visibility across supply chain',
        },
        documentQualityScoring: {
          name: 'Document Quality Scoring',
          description: 'AI quality assessment with automatic error detection',
        },
        versionControlSystem: {
          name: 'Version Control System',
          description: 'Document versioning with change tracking and rollback',
        },
      },
      pricing: {
        title: 'Choose Your Plan',
        subtitle: 'Select the perfect plan for your business needs',
        mostPopular: 'MOST POPULAR',
        essential: {
          name: 'Essential',
          description: 'Perfect for small businesses getting started',
          features: [
            'Up to 500 documents/month',
            'Basic document extraction',
            'Invoice processing',
            'Email support',
            'Standard API access',
            'Basic analytics dashboard',
            'Single user account',
            '30-day data retention',
          ],
        },
        advanced: {
          name: 'Advanced',
          description: 'Ideal for growing teams and medium businesses',
          features: [
            'Up to 2,500 documents/month',
            'Advanced AI extraction',
            'All document types',
            'Priority email & chat support',
            'Advanced API integrations',
            'Custom analytics & reporting',
            'Up to 5 user accounts',
            '90-day data retention',
            'Document tampering detection',
            'Workflow automation',
            'Custom field extraction',
          ],
        },
        enterprise: {
          name: 'Enterprise',
          description: 'For large organizations with complex needs',
          features: [
            'Unlimited documents',
            'Enterprise-grade AI processing',
            'All modules & integrations',
            '24/7 phone & dedicated support',
            'Custom API development',
            'Advanced security & compliance',
            'Unlimited user accounts',
            '1-year data retention',
            'All coming soon features',
            'Custom module development',
            'On-premise deployment option',
            'SLA guarantee (99.9% uptime)',
          ],
        },
        getStarted: 'Get Started',
        startFreeTrial: 'Start Free Trial',
        month: 'month',
      },
      footer: {
        tagline: 'Streamline your document processing workflow with our powerful extraction and integration platform.',
        quickLinks: 'Quick Links',
        signIn: 'Sign In',
        createAccount: 'Create Account',
        copyright: '© 2026 Document Builder. All rights reserved.',
      },
    },
    workflow: {
      uploadDocument: 'Upload Document',
      analyse: 'Analyse',
      review: 'Review',
    },
  },
  de: {
    common: {
      loading: 'Laden...',
      error: 'Fehler',
      success: 'Erfolg',
      cancel: 'Abbrechen',
      save: 'Speichern',
      edit: 'Bearbeiten',
      delete: 'Löschen',
      yes: 'Ja',
      no: 'Nein',
      ok: 'OK',
      gotIt: 'Verstanden',
      back: 'Zurück',
      next: 'Weiter',
      previous: 'Zurück',
      search: 'Suchen',
      filter: 'Filter',
      sort: 'Sortieren',
      export: 'Exportieren',
      import: 'Importieren',
      upload: 'Hochladen',
      download: 'Herunterladen',
      view: 'Anzeigen',
      open: 'Öffnen',
      close: 'Schließen',
      select: 'Auswählen',
      clear: 'Löschen',
      refresh: 'Aktualisieren',
      retry: 'Wiederholen',
      language: 'Sprache',
      appName: 'BEO AI Customs Engine',
    },
    navigation: {
      dashboard: 'Dashboard',
      process: 'Dokumente verarbeiten',
      transactions: 'Transaktionsverlauf',
      files: 'Dateien',
      profile: 'Mein Profil',
      settings: 'Einstellungen',
      adminDashboard: 'Admin-Dashboard',
      tenants: 'Mandanten',
      users: 'Benutzer',
      logout: 'Abmelden',
    },
    settings: {
      title: 'Einstellungen',
      subtitle: 'Verwalten Sie Ihre Module, Einstellungen und ATLAS-Konfiguration',
      tabs: {
        modules: 'Module & Integrationen',
        preferences: 'Einstellungen',
        atlas: 'ATLAS-Konfiguration',
        tenantUsers: 'Benutzer',
        tenantOrganisation: 'Organisation',
      },
    },
    plans: {
      essential: 'Essential',
      advanced: 'Advanced',
      enterprise: 'Enterprise',
    },
    dashboard: {
      title: 'Dokumentenverarbeitungszentrum',
      subtitle: 'Laden Sie Geschäftsdokumente hoch, extrahieren Sie Daten und integrieren Sie sie mit leistungsstarken APIs',
      welcome: {
        header: 'Willkommen zurück, {0}!',
        message: 'Willkommen, {0} {1}',
        subtitle: 'Bereit, Ihre Dokumente zu verarbeiten? Wählen Sie eine Option unten, um zu beginnen.',
      },
      totalDocuments: 'Gesamte Dokumente',
      successfulTransactions: 'Erfolgreiche Transaktionen',
      failedTransactions: 'Fehlgeschlagene Transaktionen',
      connectedModules: 'Verbundene Module',
      recentActivity: 'Kürzliche Aktivitäten',
      successRate: 'Erfolgsrate',
      topModules: 'Top-Module',
      recentTransactions: 'Kürzliche Transaktionen',
      viewAll: 'Alle anzeigen',
      noData: 'Keine Daten verfügbar',
      planDetails: 'Plandetails',
      monthlyLimit: 'Monatliches Dokumentenlimit',
      documentsUsed: 'Verwendete Dokumente',
      dataRetention: 'Datenspeicherung',
      days: 'Tage',
      unlimited: 'Unbegrenzt',
      cards: {
        processDocuments: {
          title: 'Dokumente verarbeiten',
          description: 'Laden Sie Daten aus Ihren Dokumenten hoch und extrahieren Sie sie, dann übertragen Sie sie an externe Module.',
          button: 'Verarbeitung starten',
        },
        history: {
          title: 'Verarbeitungshistorie',
          description: 'Detaillierte Verarbeitungshistorie mit Dateien, extrahierten Daten, IP-Adressen, API-Antworten und Erfolgs-/Fehleraufzeichnungen anzeigen.',
          button: 'Historie anzeigen',
        },
        tamperingDetection: {
          title: 'Dokumentenfälschungserkennung',
          description: 'Erweiterte KI-gestützte Erkennung von Dokumentenfälschungen und Betrug',
          button: 'Demnächst verfügbar',
        },
        emailProcessor: {
          title: 'E-Mail-Anhang-Prozessor',
          description: 'Intelligente Verarbeitung und Extraktion aus E-Mail-Anhängen',
          button: 'Demnächst verfügbar',
        },
        tariffClassifier: {
          title: 'Zolltarif-Klassifikator',
          description: 'KI-gestützte HS-Code-Klassifizierung',
          fileUpload: {
            dropZone: {
              main: 'Datei hierher ziehen & ablegen',
              active: 'Datei hier ablegen',
              browse: 'oder zum Durchsuchen klicken',
              supported: 'Unterstützt: CSV, Excel (Max. 10MB)',
              uploading: 'Datei wird hochgeladen...',
              complete: 'Abgeschlossen',
              selected: 'Datei ausgewählt',
              rejected: 'Datei abgelehnt:',
              tips: {
                bulk: 'Tipp: Verwenden Sie den Tab "Bulk-Upload" für mehrere Dateien.',
                formats: {
                  title: 'Unterstützte Dateiformate:',
                  csv: 'CSV: Kommagetrennte Werte mit Kopfzeile',
                  excel: 'Excel: .xlsx oder .xls Dateien (erstes Blatt wird verarbeitet)',
                  columns: 'Erwartete Spalten: Beschreibung/Artikel (erforderlich), HS-Code (optional)'
                }
              }
            },
            bulk: {
              dropZone: {
                main: 'Mehrere Dateien hierher ziehen & ablegen',
                active: 'Dateien hier ablegen',
                limits: 'Max. {{maxFiles}} Dateien, {{maxSize}}MB gesamt'
              },
              errors: {
                maxFiles: 'Maximal {{maxFiles}} Dateien erlaubt. Sie können noch {{remaining}} hinzufügen.',
                maxSize: 'Gesamtgröße überschreitet {{maxSize}}MB Limit'
              },
              status: {
                files: 'Dateien ({{current}}/{{max}})',
                total: 'Gesamt: {{size}} / {{maxSize}}MB',
                items: 'Elemente',
                uploading: 'Hochladen...',
                uploadButton: '{{count}} Dateien hochladen',
                clear: 'Alle löschen',
                complete: 'Bulk-Upload abgeschlossen',
                summary: '{{success}}/{{total}} Dateien erfolgreich • {{items}} Elemente extrahiert'
              },
              tips: {
                title: 'Tipps zum Bulk-Upload:',
                mix: 'Verschiedene Dateitypen in einem Upload mischen',
                parallel: 'Jede Datei wird parallel verarbeitet für schnellere Ergebnisse',
                failed: 'Fehlgeschlagene Dateien beeinträchtigen erfolgreiche nicht'
              }
            }
          },
          tabs: {
            direct: 'Direktklassifizierung',
            fileUpload: 'Datei-Upload',
            bulkUpload: 'Massenupload',
            history: 'Verlauf'
          },
          button: 'Demnächst verfügbar',
          uploadedFile: 'Hochgeladene Datei',
          viewFile: 'Datei anzeigen',
          classificationResults: 'Klassifizierungsergebnisse',
          viewResults: 'Ergebnisse anzeigen',
          downloadResultsCSV: 'Ergebnisse herunterladen (CSV)',
          downloadResultsExcel: 'Ergebnisse herunterladen (Excel)',
          downloadOriginal: 'Original herunterladen',
          fileContents: 'Dateiinhalt ({{count}} Elemente)',
          fileContent: 'Dateiinhalt',
          excelFile: 'Excel-Datei',
          excelCannotBeDisplayed: 'Excel-Dateien können nicht direkt angezeigt werden. Bitte herunterladen zum Ansehen.',
          copyContent: 'Inhalt kopieren',
          download: 'Herunterladen',
          downloadExcelFile: 'Excel-Datei herunterladen',
          file: 'Datei',
          fileTypeNotSupported: 'Dieser Dateityp kann nicht im Browser angezeigt werden.',
          downloadCSV: 'CSV herunterladen',
          downloadExcel: 'Excel herunterladen',
          close: 'Schließen',
          transactionDetails: 'Transaktionsdetails',
          inputData: 'Eingabedaten',
          outputData: 'Klassifizierungsergebnisse',
          processingInformation: 'Verarbeitungsinformationen',
          productDescription: 'Produktbeschreibung',
          materials: 'Materialien',
          origin: 'Ursprung',
          intendedUse: 'Verwendungszweck',
          generatedHS: 'Generierter HS',
          generatedHSCode: 'Generierter HS-Code',
          confidence: 'Vertrauen',
          enhancedDescription: 'Beschreibung',
          reasoning: 'Begründung',
          processingTime: 'Verarbeitungszeit',
          status: 'Status',
          analysis: {
            logic: 'KI-Analyse-Logik',
            pathTaken: 'Verwendeter Pfad',
            verdict: 'Urteil',
          },
          input: {
            label: 'Produktbeschreibung *',
            placeholder: 'Z.B. Laptop mit Intel Core i7 Prozessor, 16GB RAM, 512GB SSD, 15.6 Zoll Display',
            helperText: 'Geben Sie eine detaillierte Beschreibung für bessere Genauigkeit an',
            analyzing: 'KI analysiert Ihr Produkt... (erste Anfrage kann länger dauern)'
          },
          quickMode: 'Schnelle Analyse',
          advancedMode: 'Erweiterte Analyse',
          quickModeDesc: 'Nur Vektorsuche (schneller)',
          advancedModeDesc: 'Volles RAG mit LLM (genauer)',
          speed: {
            quick: '~1s/Artikel',
            advanced: '~5s/Artikel'
          },

          uploadAnother: 'Weiteres Dokument hochladen',
          howItWorks: {
            title: 'So funktioniert es:',
            direct: 'Direktklassifizierung: Produktdetails eingeben für sofortige HS-Code-Klassifizierung',
            file: 'Datei-Upload: CSV/Excel-Dateien mit Produktlisten für Batch-Klassifizierung hochladen',
            history: 'Verlauf: Frühere Klassifizierungsergebnisse anzeigen und herunterladen'
          },
          createdAt: 'Erstellt am',
          transactionId: 'Transaktions-ID',
          noDataAvailable: 'Keine Daten verfügbar',
          noResultsAvailable: 'Keine Ergebnisse verfügbar',
          item: 'Artikel',
          classifyWithAI: 'Mit KI klassifizieren',
          classifying: 'Klassifizierung...',
          reset: 'Zurücksetzen',
          classificationResult: 'Klassifizierungsergebnis',
          hsCode: 'HS-Code',
          confidencePercentage: '{{percent}}% Vertrauen',
          copyHSCode: 'HS-Code kopieren',
          copiedToClipboard: 'In Zwischenablage kopiert!',
          processingTimeSeconds: 'Verarbeitungszeit: {{seconds}} Sekunden',
          errors: {
            failedToLoadFile: 'Datei konnte nicht geladen werden',
            downloadFailed: 'Download fehlgeschlagen',
            failedToLoadDetails: 'Transaktionsdetails konnten nicht geladen werden',
            classificationFailed: 'Klassifizierung fehlgeschlagen',
            failedToCopyContent: 'Inhalt konnte nicht kopiert werden',
            noFileAssociated: 'Keine Datei mit dieser Transaktion verknüpft',
            failedToViewFile: 'Datei konnte nicht angezeigt werden',
            failedToViewResults: 'Ergebnisse konnten nicht angezeigt werden',
            failedToDownloadResults: 'Ergebnisse konnten nicht heruntergeladen werden',
          },
          processedViaRag: 'Via RAG-Pipeline verarbeitet',
          success: {
            resultsDownloaded: 'Ergebnisse erfolgreich heruntergeladen!',
            fileDownloaded: 'Datei heruntergeladen!',
            downloadStarted: 'Download gestartet!',
            contentCopied: 'Inhalt in Zwischenablage kopiert',
            hsCodeCopied: 'HS-Code in Zwischenablage kopiert!',
          },
          shippingCompliance: {
            title: 'EU-Versandkonformität',
            subtitle: 'Diesen HS-Code für ein Bestimmungsland gegen EU-TARIC prüfen.',
            destinationLabel: 'Bestimmungsland',
            destinationHelper: '2-Buchstaben-ISO-Code (z. B. DE, RU, KP)',
            checkButton: 'Versand prüfen',
            checking: 'Prüfe…',
            invalidCountry: 'Bitte 2-Buchstaben-ISO-Ländercode eingeben (z. B. DE, RU, KP)',
            commodityNotFound: 'Ware {{code}} in EU-TARIC nicht gefunden.',
            genericError: 'Versandprüfung fehlgeschlagen. Bitte erneut versuchen.',
            verdict: {
              prohibited: 'Verboten',
              prohibitedHelper: 'Mindestens eine Verbotsmaßnahme der Serie A gilt für dieses Bestimmungsland.',
              conditionsApply: 'Bedingungen gelten',
              conditionsApplyHelper: 'Versand ist erlaubt, aber Bedingungen (Dokumente, Lizenzen) müssen erfüllt sein.',
              clear: 'Frei',
              clearHelper: 'Keine restriktiven EU-TARIC-Maßnahmen gelten für dieses Bestimmungsland.',
            },
            importTitle: 'Einfuhrseitige Beschränkungen',
            exportTitle: 'Ausfuhrseitige Beschränkungen',
            noneApplicable: 'keine anwendbar',
            seriesLabel: 'Serie {{series}}',
            typeLabel: 'Typ {{id}}',
            conditionsCountSingular: '1 Bedingung',
            conditionsCountPlural: '{{count}} Bedingungen',
            documentsHeading: 'Erforderliche Dokumente ({{count}})',
            legalHeading: 'Rechtsgrundlagen ({{count}})',
            category: {
              dualUse: 'Dual-Use',
              military: 'Militärgüter',
              sanctions: 'Sanktionen',
              culturalGoods: 'Kulturgut',
              tortureRiot: 'Folter- / Aufstandsbekämpfung',
              prohibition: 'Verbot',
            },
            caveatsHeading: 'WARENBEZOGENE WARNHINWEISE',
            caveatBanner: 'TARIC-Verdikt unzureichend — warenbezogene Kontrollen gelten',
            caveatBannerHelper: 'EU-TARIC erfasst nur Zollgrenzmaßnahmen. Das oben angezeigte zielländerspezifische Verdikt deckt nicht alle anwendbaren Kontrollen für diese Ware ab.',
            caveat: {
              ARMS_AND_AMMUNITION: {
                title: 'Waffen und Munition',
                body: 'Diese Ware ist als Waffe/Munition klassifiziert. Ausfuhr und innergemeinschaftliche Verbringung erfordern Lizenzen nach nationalem Exportkontrollrecht und EU-Richtlinie 2009/43/EG, unabhängig vom Bestimmungsland.',
              },
              EXPLOSIVES_PYROTECHNICS: {
                title: 'Sprengstoffe und Pyrotechnik',
                body: 'Diese Ware fällt unter Kapitel 36 (Sprengstoffe, Pyrotechnik). Nationale Lizenzen sowie EU- und nationale Transportvorschriften können unabhängig vom Bestimmungsland gelten.',
              },
              RADIOACTIVE_NUCLEAR: {
                title: 'Radioaktives Material',
                body: 'Diese Ware umfasst spaltbare/radioaktive Elemente. IAEA-Sicherungsmaßnahmen, Euratom-Kontrollen und nationale Genehmigungen gelten unabhängig vom Bestimmungsland.',
              },
              RADIOACTIVE_ISOTOPES: {
                title: 'Radioaktive Isotope und Verbindungen',
                body: 'Diese Ware umfasst radioaktive Isotope/Verbindungen. IAEA-Sicherungsmaßnahmen, Euratom-Kontrollen und nationale Genehmigungen gelten unabhängig vom Bestimmungsland.',
              },
              COMMODITY_FOOTNOTE: {
                title: 'Pauschale regulatorische Beschränkung',
                body: 'TARIC weist dieser Ware eine verbotsartige Fußnote zu. Bitte Fußnotentext und einschlägige EU-Verordnungen vor dem Versand prüfen.',
              },
              AMBIGUOUS_SUBHEADING: {
                title: 'Mehrdeutige Unterposition',
                body: 'Dieser Code ist eine nicht-anmeldepflichtige Unterposition, die mehrere konkrete Waren umfasst. Das Verdikt unten ist die Vereinigung aller anmeldepflichtigen Unterwaren — je nach exaktem Produkt kann jede dieser Beschränkungen gelten. Bitte den 10-stelligen anmeldepflichtigen Code vor dem Versand bestätigen.',
              },
            },
          },
        },
        exportControl: {
          title: 'KI-Exportkontrolle',
          description: 'KI-gestützte Überprüfung der Exportkontroll-Compliance',
          button: 'Demnächst verfügbar',
        },
        documentSets: {
          title: 'Dokumentensätze',
          description: 'Kombinieren Sie mehrere Dokumente für Zollanmeldungen',
          button: 'Dokumente kombinieren',
        },
        magicMarker: {
          title: 'Magic Marker Pro',
          description: 'KI-gestützte Dokumentenannotation mit intelligenter Feldextraktion und Validierung',
          button: 'Dokumente annotieren',
        },
        complianceEngine: {
          title: 'Compliance-Engine',
          description: 'Echtzeit-Sanktionsscreening und Compliance mit 1000+ Handelsvorschriften',
          button: 'Compliance prüfen',
        },
        customsDeclaration: {
          title: 'KI-Zollanmeldung',
          description: 'Unterstützung für 190+ Länder mit BEO ATLAS-Integration',
          button: 'Anmeldung erstellen',
        },
        aiLearning: {
          title: 'KI-Lernmodul',
          description: 'Kontinuierliche Verbesserung durch Benutzerfeedback und Korrekturen',
          button: 'Demnächst verfügbar',
        },
        businessIntelligence: {
          title: 'Business Intelligence',
          description: 'Echtzeit-Analysen und Insights-Dashboard',
          button: 'Demnächst verfügbar',
        },
        aiAssistant: {
          title: 'KI-Assistent',
          description: 'Natürlichsprachige Schnittstelle für Dokumentenabfragen',
          button: 'Demnächst verfügbar',
        },
        workflowAutomation: {
          title: 'Workflow-Automatisierung',
          description: 'Visueller Workflow-Builder mit intelligenter Automatisierung',
          button: 'Demnächst verfügbar',
        },
        mobileApp: {
          title: 'Mobile App',
          description: 'Dokumente unterwegs mit React Native verarbeiten',
          button: 'Demnächst verfügbar',
        },
        blockchain: {
          title: 'Blockchain-Integration',
          description: 'Unveränderliche Verifizierung und Prüfpfade',
          button: 'Demnächst verfügbar',
        },
        multiLanguage: {
          title: 'Mehrsprachiger Support',
          description: '100+ Sprachen mit KI-gestützter Übersetzung',
          button: 'Demnächst verfügbar',
        },
        masterData: {
          title: 'Stammdatenverwaltung',
          description: 'Zentrales Daten-Repository mit 98% Übereinstimmung',
          button: 'Demnächst verfügbar',
        },
        predictiveAnalytics: {
          title: 'Predictive Analytics',
          description: 'ML-basierte Versandvorhersagen und Prognosen',
          button: 'Demnächst verfügbar',
        },
        smartContracts: {
          title: 'Smart Contracts',
          description: 'Automatisierte Generierung von Handelsvereinbarungen',
          button: 'Demnächst verfügbar',
        },
        documentRouting: {
          title: 'Intelligentes Dokumenten-Routing',
          description: 'KI-basiertes intelligentes Workflow-Routing',
          button: 'Demnächst verfügbar',
        },
        supplyChain: {
          title: 'Supply Chain Visibility',
          description: 'End-to-End-Tracking und Überwachung',
          button: 'Demnächst verfügbar',
        },
        qualityScoring: {
          title: 'Qualitätsbewertung',
          description: 'KI-gestützte Dokumentenqualitätsbewertung',
          button: 'Demnächst verfügbar',
        },
        versionControl: {
          title: 'Versionskontrolle',
          description: 'Vollständige Dokumentenversionierung und Änderungsverfolgung',
          button: 'Demnächst verfügbar',
        },
        adminPanel: {
          title: 'Admin-Panel',
          description: 'Benutzer, Module und Systemkonfiguration verwalten.',
          button: 'Admin öffnen',
        },
      },
      badges: {
        comingSoon: 'Demnächst verfügbar',
        inProgress: 'In Bearbeitung',
        new: 'Neu',
        pro: 'Pro',
        advanced: 'Erweitert',
        beta: 'Beta',
      },
      errors: {
        loadStatsFailed: 'Dashboard-Statistiken konnten nicht geladen werden',
      },
      quickOverview: {
        title: 'Schnellübersicht',
      },
      stats: {
        documentsProcessed: 'Verarbeitete Dokumente',
        successfulIntegrations: 'Erfolgreiche Integrationen',
        successRate: '{0}% Erfolgsrate',
        recentActivity: 'Kürzliche Aktivität (7 Tage)',
      },
      noRecentTransactions: 'Keine kürzlichen Transaktionen',
      viewAllTransactions: 'Alle Transaktionen anzeigen',
      mostUsedModules: 'Meistgenutzte Module',
      moduleUsage: '{0} mal verwendet',
      noModulesUsed: 'Noch keine Module verwendet',
      comingSoon: 'Demnächst verfügbar',
      inProgress: 'In Bearbeitung',
    },
    moduleDrawer: {
      title: 'Modul auswählen',
      authenticate: 'Authentifizieren',
      switchUser: 'Benutzer wechseln',
      empty: 'Keine Module verfügbar',
      closeAriaLabel: 'Modulpanel schließen',
      openAriaLabel: 'Modulpanel öffnen',
    },
    appShell: {
      brandLogoAlt: 'BEO Software',
      userMenuAriaLabel: 'Benutzermenü',
      collapseSidebar: 'Seitenleiste einklappen',
      expandSidebar: 'Seitenleiste ausklappen',
    },
    documentProcessing: {
      title: 'Smart Exports',
      subtitle: '',
      sections: {
        selectModule: 'Modul/Endpunkt auswählen',
        uploadDocuments: 'Dokumente hochladen',
        reviewExtractedData: 'Extrahierte Daten überprüfen',
        reviewSendData: 'Daten überprüfen & senden',
      },
      module: {
        chooseModule: 'Modul auswählen',
        label: 'Modul: {0}',
        authenticated: 'Authentifiziert',
        tokenExpired: 'Token abgelaufen',
        notAuthenticated: 'Nicht authentifiziert',
      },
      moduleAuth: {
        title: 'Mit {0} authentifizieren',
        demoMode: 'Demo-Modus',
        demoDescription: 'Geben Sie beliebige Anmeldedaten ein, um sich mit {0} zu authentifizieren. In der Produktion sind echte Modulanmeldedaten erforderlich.',
        username: 'Benutzername',
        usernamePlaceholder: 'Benutzername eingeben',
        usernameRequired: 'Benutzername ist erforderlich',
        password: 'Passwort',
        passwordPlaceholder: 'Passwort eingeben',
        passwordRequired: 'Passwort ist erforderlich',
        authSuccess: 'Erfolgreich mit {0} authentifiziert!',
        userDetailsSaved: 'Benutzerdetails gespeichert.',
        authFailed: 'Authentifizierung fehlgeschlagen. Bitte versuchen Sie es erneut.',
        cancel: 'Abbrechen',
        authenticate: 'Authentifizieren',
        authenticating: 'Authentifiziere...',
        demoWarning: 'Demo-Modus: Alle Anmeldedaten funktionieren zum Testen. In der Produktion sind echte Modulanmeldedaten erforderlich. Test-Tokens laufen automatisch nach 24 Stunden ab.',
      },
      upload: {
        dragDrop: 'Dateien hierher ziehen',
        dropHere: 'Dateien hier ablegen...',
        clickToBrowse: 'oder klicken zum Durchsuchen • PDF, Bilder • Max. 50MB pro Datei',
        filesCount: '{0} Datei{1}',
        minDocumentsHint: 'Mindestens 2 Dokumente hochladen, um die Korrelation für die ATLAS-Übermittlung zu validieren.',
      },
      file: {
        ready: 'Bereit',
        fileXofY: 'Datei {0} von {1}',
        imagePreview: '🖼️ Bildvorschau',
        pdfDocument: '📄 PDF-Dokument',
        annotate: 'Mit Magic Marker annotieren',
        remove: 'Datei entfernen',
      },
      processing: {
        start: 'Verarbeitung starten',
        inProgress: 'Verarbeite...',
        uploadingFiles: 'Dateien werden hochgeladen...',
        uploadingToServer: 'Dateien werden zum Server hochgeladen...',
        uploadingFile: 'Datei {0} von {1} wird hochgeladen: {2}',
        processingExtracted: 'Extrahierte Daten werden verarbeitet...',
        filesUploadedSuccess: 'Dateien erfolgreich hochgeladen und verarbeitet',
        checkingAuth: 'Authentifizierung wird geprüft...',
        validatingData: 'Daten werden validiert...',
        preparingData: 'Daten werden für die Übertragung vorbereitet...',
        sendingData: 'Daten werden an externe API gesendet...',
        complete: 'Verarbeitung abgeschlossen!',
        completePercentage: '{0}% abgeschlossen',
        complianceCheck: 'Compliance-Prüfung läuft...',
        complianceCheckComplete: 'Compliance-Prüfung abgeschlossen',
      },
      navGuard: {
        activeReason: 'Die Dokumentverarbeitung läuft.',
        title: 'Diese Seite verlassen?',
        body: '{reason} Wenn Sie jetzt verlassen, läuft die Arbeit im Hintergrund weiter.',
        stay: 'Bleiben',
        leave: 'Verlassen',
      },
      viewMode: {
        table: 'Tabellenansicht',
        form: 'ATLAS-Formularansicht',
      },
      actions: {
        selectAll: 'Alle Felder auswählen',
        clearAll: 'Alle Auswahlen löschen',
        reset: 'Zurücksetzen',
        sendData: 'Validieren und Senden',
        close: 'Schließen',
      },
      switchUser: {
        button: 'Benutzer wechseln',
        title: 'Benutzer wechseln',
        message: 'Es sind aktuell Vorhersagedaten geladen. Wie möchten Sie fortfahren?',
        resetForm: 'Formular zurücksetzen',
        keepForm: 'Formular behalten',
        cancel: 'Abbrechen',
        resetFormDone: 'Formular auf ursprünglich extrahierte Daten zurückgesetzt.',
        keepFormDone: 'Formulardaten behalten. Benutzer gewechselt.',
      },
      emptyState: {
        title: 'Dokumente verarbeiten, um Daten anzuzeigen',
        subtitle: 'Dateien hochladen und auf "Verarbeitung starten" klicken, um Daten zu extrahieren',
      },
      atlasStatus: {
        title: 'ATLAS-Feldstatus (entspricht BEO ATLAS-Validierung):',
        complete: 'Vollständig ({0})',
        missing: 'Fehlend ({0})',
        note: 'Hinweis: Gelbe Felder haben Werte, benötigen aber Überprüfung',
      },
      fieldStatus: {
        required: 'Erforderlich',
        verify: 'Prüfen',
        aiPowered: 'KI-gestützt',
        autoClassify: 'Automatisch mit KI klassifizieren',
        enterRequired: 'Wert eingeben (erforderlich)',
      },
      tableView: {
        fieldName: 'Feldname',
        extractedValue: 'Extrahierter Wert',
        mlPrediction: 'ML-Vorhersage',
        applyAll: 'Alle anwenden',
        applyAllTooltip: 'Alle ML-Vorhersagen auf Formularfelder anwenden',
      },
      sendPartial: {
        label: 'Teildaten senden',
        description: 'Teildaten von hier senden und später in ATLAS hinzufügen/ändern, um Anmeldung zu erstellen',
      },
      dialogs: {
        magicMarker: 'Magic Marker Pro - Dokumentenannotation',
        complianceReport: 'Compliance-Bericht',
      },
      toasts: {
        loadModulesFailed: 'Module konnten nicht geladen werden',
        uploadFailed: 'Dateien konnten nicht hochgeladen werden',
        fileRemoved: 'Datei entfernt',
        selectModuleFirst: 'Bitte wählen Sie zuerst ein Modul aus und laden Sie Dateien hoch',
        filesUploaded: '{0} Datei(en) erfolgreich hochgeladen',
        authExpired: 'Authentifizierung für {0} abgelaufen. Bitte erneut authentifizieren.',
        moduleAuthenticated: 'Modul "{0}" ist jetzt für 24 Stunden authentifiziert!',
        documentRouted: 'Dokument weitergeleitet an: {0}',
        complianceCompliant: '✅ Dokument entspricht allen Vorschriften',
        complianceIssues: '⚠️ Compliance-Probleme erkannt! Bericht überprüfen.',
        complianceReview: '⚠️ Dokument erfordert Compliance-Überprüfung',
        complianceCheckFailed: 'Compliance-Prüfung konnte nicht abgeschlossen werden',
        complianceExported: 'Compliance-Bericht exportiert',
        readyForNext: 'Bereit für nächstes Dokument',
        noPositionData: 'Keine Positionsdaten zum Klassifizieren gefunden',
        processingCancelled: 'Verarbeitung abgebrochen',
        cancellingProcessing: 'Verarbeitung wird abgebrochen...',
        complianceCheckComplete: 'Compliance-Prüfung abgeschlossen!',
        complianceResultsFailed: 'Compliance-Ergebnisse konnten nicht abgerufen werden',
        complianceTimeout: 'Compliance-Prüfung dauert länger als erwartet',
        filesUploadedProcessed: '{0} Datei(en) erfolgreich hochgeladen und verarbeitet',
        dataSentSuccess: '✅ Daten erfolgreich an {0} gesendet!',
        dataSentWithDetails: '✅ Daten erfolgreich an {0} gesendet!\n📄 Dateien: {1} verarbeitet\n📊 Transaktions-ID: {2}',
        hsCodeApplied: 'HS-Code {0} angewendet ({1}% Vertrauen)',
        classifyingProducts: '{0} Produkte werden klassifiziert... Dies kann bis zu 2 Minuten dauern.',
        classificationSuccess: '{0} von {1} Positionen erfolgreich klassifiziert{2, select, 0{} other{ ({2} fehlgeschlagen)}}',
        classificationFailed: 'Klassifizierung für alle {0} Positionen fehlgeschlagen. Details in der Konsole prüfen.',
      },
      successDialog: {
        title: 'Daten erfolgreich gesendet!',
        message: 'Ihre Daten wurden erfolgreich an das Modul gesendet.',
        transactionDetails: 'Transaktionsdetails',
        module: 'Modul',
        filesProcessed: 'Verarbeitete Dateien',
        transactionId: 'Transaktions-ID',
        processAnother: 'Möchten Sie ein weiteres Dokument verarbeiten?',
        goToDashboard: 'Zum Dashboard',
        processAnotherButton: 'Weitere verarbeiten',
      },
      atlasValidation: {
        title: 'ATLAS-Anmeldungsvalidierung',
        percentComplete: '{0}% abgeschlossen',
        allFieldsComplete: 'Alle erforderlichen ATLAS-Felder sind vollständig. Bereit zur Übermittlung an den Zoll.',
        missingFields: '{0} erforderliche(s) Feld(er) fehlt/fehlen. Übermittlung nicht möglich, bis alle Felder ausgefüllt sind.',
        missingRequiredFields: 'Fehlende Pflichtfelder',
        completeFields: 'Vollständige Felder ({0})',
        pleaseVerify: 'Bitte überprüfen',
        verifyRecipient: 'Bitte überprüfen Sie, ob die Empfängerinformationen korrekt sind',
        verifyHsCode: 'Bitte überprüfen Sie, ob die HS-Code-Klassifizierung korrekt ist',
        requiredDocuments: 'Erforderliche Dokumente für ATLAS-Export',
        andMore: '... und {0} weitere',
        sections: {
          header: 'Kopfinformationen',
          exporter: 'Ausführer (Ihr Unternehmen)',
          recipient: 'Empfänger (Käufer)',
          declarant: 'Anmelder',
          delivery: 'Lieferdetails',
          shipment: 'Sendungsdetails',
          lineItems: 'Positionen',
        },
        documents: {
          commercialInvoice: 'Handelsrechnung',
          packingList: 'Packliste (bei mehreren Artikeln)',
          certificateOfOrigin: 'Ursprungszeugnis (für bestimmte Länder)',
          exportLicense: 'Ausfuhrgenehmigung (für kontrollierte Waren)',
        },
      },
      atlasForm: {
        sections: {
          header: 'Kopfinformationen',
          headerDesc: 'Rechnungsdetails und Anmeldungsart',
          exporter: 'Ausführer (Ihr Unternehmen)',
          exporterDesc: 'Ihre Unternehmensinformationen - automatisch aus Profil ausgefüllt',
          recipient: 'Empfänger (Käufer)',
          recipientDesc: 'Unternehmensinformationen des Käufers',
          declarant: 'Anmelder',
          declarantDesc: 'Informationen zur anmeldenden Partei',
          delivery: 'Lieferdetails',
          deliveryDesc: 'Exportziel und Lieferbedingungen',
          shipment: 'Sendungsdetails',
          shipmentDesc: 'Paketanzahl, Gewicht und Transportart',
          lineItems: 'Positionen',
          lineItemsDesc: 'Produktdetails, Mengen und Werte',
          borderTransport: 'Grenzüberschreitendes Beförderungsmittel',
        },
        fieldGroups: {
          declaration: 'Anmeldung & Kennungen',
          customsOffices: 'Zollstellen',
          transaction: 'Geschäft & Rechnung',
          countries: 'Länder',
          delivery: 'Lieferbedingungen (Incoterms)',
          transport: 'Beförderung',
          goodsLocation: 'Warenort',
          dates: 'Datum & Zeitpunkte',
          security: 'Sicherheit & Besonderes',
          authorisation: 'Bewilligung',
          declarant: 'Anmelder',
          representative: 'Vertreter',
          subcontractor: 'Subunternehmer',
          general: 'Sonstige',
        },
        fieldHelpers: {
          required: 'Pflichtfeld - muss ausgefüllt werden',
          verify: 'Bitte überprüfen Sie diesen Wert',
        },
        activeFields: 'Aktive Felder',
        fieldsCount: '{0} Felder',
        requiredCount: '{0} erforderlich',
        showOptionalFields: 'Andere Felder ({0})',
        optionalCount: '{0} optional',
        noFieldsConfigured: 'Keine Felder für diesen Abschnitt konfiguriert',
        mlPredictionsUnavailable: 'ML-Vorhersagen vorübergehend nicht verfügbar',
        fieldMovedToTab: '„{0}" wurde in den Tab „{1}" verschoben',
      },
      errors: {
        missingAtlasFields: '{0} erforderliche ATLAS-Felder fehlen',
        missingFieldsList: '{0} und {1} weitere',
        fillRedFields: 'Bitte füllen Sie alle ROTEN Felder aus, bevor Sie absenden.',
        authenticationRequired: 'Authentifizierung erforderlich',
        authRequiredFor: 'Authentifizierung für {0} erforderlich. Bitte authentifizieren Sie sich zuerst.',
        tokenExpired: 'Authentifizierungstoken für {0} abgelaufen. Bitte erneut authentifizieren.',
        validationFailed: 'Validierung fehlgeschlagen: {0} Fehler',
        complianceAuthRequired: 'BEO Sanctions-Authentifizierung ist erforderlich, um automatische Compliance-Prüfungen für Ihre Dokumente durchzuführen. Sie können sich jetzt authentifizieren oder diesen Schritt überspringen und ohne Compliance-Prüfungen fortfahren.',
        continueWithout: 'Ohne fortfahren',
        authenticateNow: 'Jetzt authentifizieren',
        generalErrorsTitle: 'Validierungsfehler - {0} Problem(e) gefunden',
        generalErrorsDescription: 'Die folgenden Fehler konnten nicht bestimmten Formularfeldern zugeordnet werden. Bitte überprüfen und korrigieren Sie diese:',
        fixFormErrors: 'Bitte beheben Sie die markierten Formularfehler und versuchen Sie es erneut.',
      },
      fieldsSelected: '{0} von {1} Feldern ausgewählt',
      classifying: 'Klassifiziere...',
      autoClassifyHsCodes: 'HS-Codes automatisch klassifizieren',
      enableComplianceScreening: 'Compliance-Prüfung aktivieren',
      complianceScreeningDesc: 'Entitäten automatisch gegen Sanktionslisten prüfen (erfordert BEO Sanctions-Authentifizierung)',
    },
    teachAI: {
      title: 'KI trainieren',
      viewMode: 'Ansicht',
      drawMode: 'Zeichnen',
      resetRules: 'Regeln zurücksetzen',
      loadingPdf: 'PDF wird geladen…',
      loadingRules: 'Regeln werden geladen…',
      extractingText: 'Text wird extrahiert…',
      drawModeHint: 'Zeichenmodus — ziehen Sie auf dem PDF, um einen Bereich auszuwählen',
      emptyStateHint: 'Wechseln Sie in den <strong>Zeichenmodus</strong>, dann ziehen Sie ein Rechteck über ein Feld im PDF, um eine Regel zu erstellen.',
      extractedText: 'Extrahierter Text',
      mapToField: 'Feld zuordnen',
      searchFields: 'Felder suchen…',
      applyMapping: 'Zuordnung anwenden',
      noFieldsFound: 'Keine Felder gefunden',
      savedRules: 'Gespeicherte Regeln',
      rulesSaved: '{0} Regel gespeichert',
      rulesSavedPlural: '{0} Regeln gespeichert',
      onThisPage: '{0} auf dieser Seite',
      ruleSelected: 'Regel ausgewählt',
      deleteHint: 'Drücken Sie <kbd>Entf</kbd> oder das Papierkorb-Symbol zum Entfernen:',
      hoverDeleteHint: 'Fahren Sie über eine Regel, um die Löschtaste anzuzeigen.',
      pageLabel: 'Seite {0} / {1}',
      resetAllTitle: 'Alle Regeln zurücksetzen?',
      resetAllMessage: 'Dadurch werden alle Extraktionsregeln für diese Lieferantenvorlage gelöscht. Zukünftige Dokumente verwenden wieder die Standard-KI-Extraktion.',
      resetAllWarning: 'Diese Aktion kann nicht rückgängig gemacht werden.',
      cancel: 'Abbrechen',
      yesResetAll: 'Ja, alle zurücksetzen',
      failedToLoadPdf: 'PDF konnte nicht geladen werden.',
    },
    fieldValidator: {
      noPattern: 'Kein Validierungsmuster definiert',
      validFormat: 'Format ist gültig',
      expectedFormat: 'Erwartetes Format',
    },
    atlasFields: {
      // Kopf (Header) Felder
      invoiceNumber: 'Rechnungsnummer (LRN)',
      declarationType: 'Art der Anmeldung',
      exportDeclarationType: 'Ausfuhranmeldungsart',
      participantConstellation: 'Beteiligten-Konstellation',
      declarationTimestamp: 'Anmeldungszeitpunkt',
      securityIndicator: 'Sicherheitsindikator',
      customsOfficePresentation: 'Gestellungszollstelle',
      exportCustomsOffice: 'Ausfuhrzollstelle',
      totalGrossMass: 'Gesamt-Rohmasse',
      countryOfDestination: 'Bestimmungsland',
      incotermCode: 'Incoterm-Code',
      totalInvoiceAmount: 'Gesamtrechnungsbetrag',
      invoiceCurrency: 'Rechnungswährung',
      invoiceDate: 'Rechnungsdatum',
      exportCountry: 'Ausfuhrland',
      deliveryPlace: 'Lieferort',
      domesticTransportMode: 'Inländischer Verkehrszweig',
      borderTransportMode: 'Verkehrszweig an der Grenze',
      // Ausführer Felder
      exporterName: 'Ausführer Name',
      exporterStreet: 'Ausführer Straße',
      exporterPostalCode: 'Ausführer PLZ',
      exporterCity: 'Ausführer Ort',
      exporterCountry: 'Ausführer Land',
      exporterEORI: 'Ausführer EORI/TIN',
      // Empfänger Felder
      recipientName: 'Empfänger Name',
      recipientStreet: 'Empfänger Straße',
      recipientPostalCode: 'Empfänger PLZ',
      recipientCity: 'Empfänger Ort',
      recipientCountry: 'Empfänger Land',
      recipientEORI: 'Empfänger EORI/TIN',
      recipientIdentificationType: 'Empfänger Identifikationsart',
      // Anmelder Felder
      declarantName: 'Anmelder Name',
      declarantStreet: 'Anmelder Straße',
      declarantPostalCode: 'Anmelder PLZ',
      declarantCity: 'Anmelder Ort',
      declarantCountry: 'Anmelder Land',
      declarantEORI: 'Anmelder EORI/TIN',
      declarantAuthorizedName: 'Anmelder Beauftragter Name',
      declarantContactPerson: 'Anmelder Ansprechpartner',
      declarantPhone: 'Anmelder Telefon',
      declarantBranchNumber: 'Anmelder Niederlassungsnummer',
      declarantIdentificationType: 'Anmelder Identifikationsart',
      // Anmeldedetails Felder
      containerIndicator: 'Container-Indikator',
      numberOfPositions: 'Anzahl Positionen',
      referenceNumber: 'Bezugsnummer',
      eoriBranchNumber: 'EORI Niederlassungsnummer',
      // Zoll/Transport Felder
      intendedExitCustomsOffice: 'Vorgesehene Ausgangszollstelle',
      supplementaryExportCustomsOffice: 'Ergänzungszollstelle Ausfuhr',
      loadingPlaceCode: 'Ladeort Code',
      borderTransportModeType: 'Grenzübergangsmittel Art',
      borderTransportNationality: 'Grenzübergangsmittel Nationalität',
      // Sendung Felder
      numberOfPackages: 'Anzahl Pakete',
      totalGrossWeight: 'Gesamt-Bruttogewicht (kg)',
      transportMode: 'Verkehrszweig',
      transitCountries: 'Transitländer',
      // Lieferung Felder
      destinationCountry: 'Bestimmungsland',
      incoterm: 'Incoterm',
      deliveryTermsText: 'Lieferbedingung Freitext',
      transportCostPaymentMethod: 'Beförderungskosten Zahlungsart',
      // Zeitstempel / Datum
      relevantDate: 'Maßgebliches Datum',
      dateOfExit: 'Datum des Ausgangs',
      presentationTimestamp: 'Zeitpunkt der Gestellung',
      endOfLoadingTimestamp: 'Zeitpunkt des Endes der Ladetätigkeit',
      // Warenort Felder
      locationType: 'Warenort Art des Ortes',
      locationDeterminationType: 'Warenort Art der Ortsbestimmung',
      locationAuthorizationNumber: 'Warenort Bewilligungsnummer',
      // Beförderungsmittel bei Abgang
      transportIdTypeAtDeparture: 'Art der Identifikation bei Abgang',
      vehicleRegistrationAtDeparture: 'Kennzeichen bei Abgang',
      vehicleNationalityAtDeparture: 'Staatszugehörigkeit bei Abgang',
      // Empfänger zusätzlich
      recipientBranchNumber: 'Empfänger Niederlassungsnummer',
      recipientTINEORI: 'Empfänger TIN/EORI',
      // Position Felder
      positionNumber: 'Positionsnummer',
      requestedProcedure: 'Beantragtes Verfahren',
      previousProcedure: 'Vorhergehendes Verfahren',
      countryOfOrigin: 'Ursprungsland',
      productDescription: 'Warenbezeichnung',
      hsCode: 'HS/CN-Code',
      grossWeight: 'Rohmasse (kg)',
      netWeight: 'Eigenmasse (kg)',
      quantity: 'Menge',
      quantityInSpecialUnit: 'Menge in besonderer Maßeinheit',
      statisticalValue: 'Statistischer Wert',
      packageType: 'Art der Verpackung',
      numberOfPackagesItem: 'Anzahl der Packstücke',
      shippingMarks: 'Versandzeichen',
      unit: 'Einheit',
      unitValue: 'Stückwert',
      totalValue: 'Gesamtwert',
      currency: 'Währung',
      userId: 'Benutzer-ID (System)',
      item: 'Artikel',
      // Zusätzliche Felder - Kopf
      typeOfTransaction: 'Art des Geschäfts',
      natureOfTransaction: 'Art des Geschäftsvorgangs',
      customsOfficeNumber: 'Dienststellennummer',
      specialCircumstances: 'Besondere Umstände',
      additionalLocationIdentifier: 'Zusätzliche Ortskennung',
      // Zusätzliche Felder - Grenzüberschreitendes Beförderungsmittel
      borderVehicleNationality: 'Grenzfahrzeug Staatszugehörigkeit',
      borderVehicleRegistration: 'Grenzfahrzeug Kennzeichen',
      // Zusätzliche Felder - Vertreter
      representativeTIN: 'Vertreter EORI/TIN',
      representativeName: 'Vertreter Name',
      representativePhone: 'Vertreter Telefon',
      representativeBranchNumber: 'Vertreter Niederlassungsnummer',
      representativeContactPerson: 'Vertreter Ansprechpartner',
      // Zusätzliche Felder - Subunternehmer
      subcontractorBranchNumber: 'Subunternehmer Niederlassungsnummer',
      // Bewilligung (Authorization)
      authorizationType: 'Bewilligung Art',
      authorizationReference: 'Bewilligung Referenznummer',
      // System / Kopf Felder
      versionNumber: 'Versionsnummer',
      creationDate: 'Erstelldatum',
      procedureCode: 'Verfahrenscode',
      // Versender
      senderName: 'Versender Name',
      senderStreet: 'Versender Straße',
      senderPostalCode: 'Versender PLZ',
      senderCity: 'Versender Ort',
      senderCountry: 'Versender Land',
      senderEORI: 'Versender EORI/TIN',
      // Außenwirtschaftsrechtlicher Ausführer
      externalExporterName: 'Außenwirtschaftl. Ausführer Name',
      externalExporterStreet: 'Außenwirtschaftl. Ausführer Straße',
      externalExporterPostalCode: 'Außenwirtschaftl. Ausführer PLZ',
      externalExporterCity: 'Außenwirtschaftl. Ausführer Ort',
      externalExporterCountry: 'Außenwirtschaftl. Ausführer Land',
      externalExporterEORI: 'Außenwirtschaftl. Ausführer EORI/TIN',
      // Zusätzliche Felder - Position
      regionOfOrigin: 'Ursprungs-/Versendungsregion',
      // Schaltflächen
      exportStatus: 'Exportstatus',
      loadFromHistory: 'Aus Verlauf laden',
      applyAllPredictions: 'Alle Vorhersagen anwenden',
      auditDeclaration: 'Zollanmeldung prüfen',
      teachAI: 'KI trainieren',
      enterField: '{0} eingeben',
      selectField: '{0} auswählen',
      filterTabs: {
        extracted: 'Extrahiert',
        aiPrefilled: 'KI-vorausgefüllt',
        errors: 'Fehler',
        otherFields: 'Andere Felder',
      },
      allErrors: 'Alle Fehler',
      aiGenerated: 'KI-generiert',
      fillFromHistory: 'Aus Verlauf übernehmen: „{0}"',
      errorCount: '{0} Fehler',
      noValidationErrors: 'Keine Validierungsfehler',
      allFieldsValid: 'Alle Felder sind gültig.',
    },
    submissionPreview: {
      title: 'Vorschau der Anmeldedaten',
      proceed: 'Weiter',
      edit: 'Abbrechen',
      sections: {
        registrationTypes: 'Anmeldearten',
        generalInformation: 'Allgemeine Informationen',
        provisionInformation: 'Informationen zur Gestellung',
        transportMeansDetails: 'Details des Beförderungsmittels',
        customsOffices: 'Dienststellen',
        businessTransaction: 'Handelsgeschäft',
        deliveryTerms: 'Lieferbedingungen',
        participants: 'Beteiligte',
        locationOfGoods: 'Angaben zum Warenort',
        locationAddress: 'Warenort / Anschrift',
        positions: 'Positionen',
        contactPerson: 'Ansprechpartner',
        otherFields: 'Weitere Felder',
      },
      participants: {
        empfanger: 'Empfänger',
        anmelder: 'Anmelder',
        vertreter: 'Vertreter',
        subunternehmer: 'Subunternehmer',
        ausfuhrer: 'Ausführer',
        aussenAusfuhrer: 'Außenwirtschaftlicher Ausführer',
        beforderer: 'Beförderer',
        versender: 'Versender',
      },
    },
    // German ATLAS field path labels - using original German ATLAS terminology
    atlasFieldPaths: {
      // Kopf (Header) - Main fields
      'kopf.lrn': 'Lokale Referenznummer (LRN)',
      'kopf.artderAnmeldung': 'Art der Anmeldung',
      'kopf.artderAusfuhranmeldung': 'Art der Ausfuhranmeldung',
      'kopf.beteiligtenKonstellation': 'Beteiligten-Konstellation',
      'kopf.zeitpunktderAnmeldung': 'Zeitpunkt der Anmeldung',
      'kopf.sicherheit': 'Sicherheit',
      'kopf.gestellungszollstelle': 'Gestellungszollstelle',
      'kopf.ausfuhrzollstelleDienststellennummer': 'Ausfuhrzollstelle Dienststellennummer',
      'kopf.inRechnunggestellterGesamtbetrag': 'In Rechnung gestellter Gesamtbetrag',
      'kopf.rechnungswahrung': 'Rechnungswährung',
      'kopf.datum': 'Datum',
      'kopf.eoriNiederlassungsnummer': 'EORI Niederlassungsnummer',
      'kopf.dienststellennummer': 'Dienststellennummer',
      'kopf.massgeblichesDatum': 'Maßgebliches Datum',
      'kopf.besondereUmstande': 'Besondere Umstände',
      'kopf.artdesGeschafts': 'Art des Geschäfts',
      'kopf.ausfuhrland': 'Ausfuhrland',
      'kopf.bestimmungsLand': 'Bestimmungsland',
      'kopf.containerIndikator': 'Container-Indikator',
      'kopf.container': 'Container-Indikator',
      'kopf.gesamtRohmasse': 'Gesamt-Rohmasse (kg)',
      'kopf.inlandischerVerkehrszweig': 'Inländischer Verkehrszweig',
      'kopf.verkehrszweigAnDerGrenze': 'Verkehrszweig an der Grenze',
      'kopf.kopfDatumdesAusgangs': 'Datum des Ausgangs',
      'kopf.zeitpunktDerGestellung': 'Zeitpunkt der Gestellung',
      'kopf.zeitpunktdesEndesderLadetatigkeit': 'Zeitpunkt des Endes der Ladetätigkeit',
      'kopf.packstuckanzahl': 'Packstückanzahl',
      'kopf.packstuckzeichennummern': 'Packstückzeichennummern',
      // Lieferbedingungen
      'kopf.lieferbedingungIncotermCode': 'Lieferbedingung Incoterm-Code',
      'kopf.lieferbedingungOrt': 'Lieferbedingung Ort',
      'kopf.lieferbedingungLand': 'Lieferbedingung Land',
      'kopf.lieferbedingungUnLocode': 'Lieferbedingung UN/LOCODE',
      'kopf.lieferbedingungText': 'Lieferbedingung Text',
      // Zollstellen
      'kopf.ausfuhrZollstelleFurDieErganzende': 'Ausfuhrzollstelle für die ergänzende Anmeldung',
      'kopf.erganzungszollstelleAusfuhrDienststellennummer': 'Ergänzungszollstelle Ausfuhr',
      'kopf.vorgeseheneAusgangszollstelleDienststellennummer': 'Vorgesehene Ausgangszollstelle',
      // Ausfuhrer (Exporter)
      'kopf.ausfuhrer.name': 'Ausführer Name',
      'kopf.ausfuhrer.strasse': 'Ausführer Straße',
      'kopf.ausfuhrer.plz': 'Ausführer PLZ',
      'kopf.ausfuhrer.ort': 'Ausführer Ort',
      'kopf.ausfuhrer.land': 'Ausführer Land',
      'kopf.ausfuhrer.tin': 'Ausführer EORI/TIN',
      'kopf.ausfuhrer.telefonnummer': 'Ausführer Telefon',
      'kopf.ausfuhrer.fax': 'Ausführer Fax',
      'kopf.ausfuhrer.email': 'Ausführer E-Mail',
      'kopf.ausfuhrer.bundesland': 'Ausführer Bundesland',
      // Empfanger (Recipient)
      'kopf.empfanger.name': 'Empfänger Name',
      'kopf.empfanger.strasse': 'Empfänger Straße',
      'kopf.empfanger.plz': 'Empfänger PLZ',
      'kopf.empfanger.ort': 'Empfänger Ort',
      'kopf.empfanger.land': 'Empfänger Land',
      'kopf.empfanger.tin': 'Empfänger EORI/TIN',
      'kopf.empfanger.identifikationsart': 'Empfänger Identifikationsart',
      'kopf.empfanger.niederlassungsNummer': 'Empfänger Niederlassungsnummer',
      // Anmelder (Declarant)
      'kopf.anmelder.tin': 'Anmelder EORI/TIN',
      'kopf.anmelder.niederlassungsNummer': 'Anmelder Niederlassungsnummer',
      'kopf.anmelder.phone': 'Anmelder Telefon',
      'kopf.anmelder.ansprechName': 'Anmelder Ansprechpartner',
      'kopf.anmelder.beauftragterName': 'Anmelder Beauftragter Name',
      'kopf.anmelder.ansprechpartnerName': 'Anmelder Ansprechpartner',
      'kopf.anmelder.identifikationsart': 'Anmelder Identifikationsart',
      // Vertreter (Representative)
      'kopf.vertreter.tin': 'Vertreter EORI/TIN',
      'kopf.vertreter.niederlassungsNummer': 'Vertreter Niederlassungsnummer',
      'kopf.vertreter.phone': 'Vertreter Telefon',
      'kopf.vertreter.ansprechName': 'Vertreter Ansprechpartner',
      // Subunternehmer
      'kopf.subunternehmer.tin': 'Subunternehmer EORI/TIN',
      'kopf.subunternehmer.niederlassungsNummer': 'Subunternehmer Niederlassungsnummer',
      // Bewilligung (Authorization)
      'kopf.bewilligung[0].art': 'Bewilligung Art',
      'kopf.bewilligung[0].referenznummer': 'Bewilligung Referenznummer',
      // Warenort
      'kopf.warenortArtdesOrtes': 'Warenort Art des Ortes',
      'kopf.warenortArtDerOrtsbestimmung': 'Warenort Art der Ortsbestimmung',
      'kopf.warenortZusatzlicheKennung': 'Warenort Zusätzliche Kennung',
      'kopf.warenortBewilligungsnummer': 'Warenort Bewilligungsnummer',
      // Beforderungsmittel
      'kopf.beforderungsmittelBeimAbgang[0].artderIdentifikation': 'Beförderungsmittel Art der Identifikation',
      'kopf.beforderungsmittelBeimAbgang[0].kennzeichen': 'Beförderungsmittel Kennzeichen',
      'kopf.beforderungsmittelBeimAbgang[0].staatszugehorigkeit': 'Beförderungsmittel Staatszugehörigkeit',
      'kopf.beforderungsroute.ausgewahlteLander': 'Beförderungsroute Ausgewählte Länder',
      'kopf.beforderungsmittelderGrenzeArt': 'Grenzüberschreitendes Beförderungsmittel Art',
      'kopf.beforderungsmittelderGrenzeKennzeichen': 'Beförderungsmittel Grenze Kennzeichen',
      'kopf.beforderungsmittelderGrenzeStaatszugehorigkeit': 'Grenzfahrzeug Staatszugehörigkeit',
      'kopf.beforderungskostenZahlungsart': 'Beförderungskosten Zahlungsart',
      // Position fields
      'warePositionsnummer': 'Positionsnummer',
      'beantragtesVerfahren': 'Beantragtes Verfahren',
      'vorhergehendesVerfahren': 'Vorhergehendes Verfahren',
      'zusatzlichesVerfahren': 'Zusätzliches Verfahren',
      'ursprungsland': 'Ursprungsland',
      'ursprungsVersendungsregion': 'Ursprungs-/Versendungsregion',
      'wareWarenbezeichnung': 'Warenbezeichnung',
      'wareWarennummerKN8': 'Warennummer (KN8)',
      'wareRohmasse': 'Rohmasse (kg)',
      'wareEigenmasse': 'Eigenmasse (kg)',
      'mengeinbesondererMabeinheit': 'Menge in besonderer Maßeinheit',
      'statistischerWert': 'Statistischer Wert',
      'referenznummerUCR': 'Referenznummer UCR',
      'warecusnummer': 'CUS-Nummer',
      'registriernummerextern': 'Externe Registriernummer',
      // Verpackung
      'artderVerpackung': 'Art der Verpackung',
      'anzahlderPackstucke': 'Anzahl der Packstücke',
      'versandzeichen': 'Versandzeichen',
      'packstuckverweisPositionsnummer': 'Packstückverweis Positionsnummer',
      // Unterlage
      'unterlage.art': 'Unterlage Art',
      'unterlage.referenznummer': 'Unterlage Referenznummer',
      'unterlage.qualifikator': 'Unterlage Qualifikator',
      'unterlage.datumderAusstellung': 'Unterlage Datum der Ausstellung',
      'unterlage.gultigkeitsdatum': 'Unterlage Gültigkeitsdatum',
      'unterlage.zusatzlicheAngaben': 'Unterlage Zusätzliche Angaben',
      'unterlage.detail': 'Unterlage Detail',
      'unterlage.mabeinheit': 'Unterlage Maßeinheit',
      'unterlage.menge': 'Unterlage Menge',
      'unterlage.wahrung': 'Unterlage Währung',
      'unterlage.betrag': 'Unterlage Betrag',
      'unterlage.namederausstellendenBehorde': 'Unterlage Name der ausstellenden Behörde',
      'unterlage.zeilenPositionsnummer': 'Unterlage Zeilen-Positionsnummer',
      // Sonstiger Verweis
      'sonstigerVerweis.art': 'Sonstiger Verweis Art',
      'sonstigerVerweis.referenznummer': 'Sonstiger Verweis Referenznummer',
      'sonstigerVerweis.qualifikator': 'Sonstiger Verweis Qualifikator',
      'sonstigerVerweis.detail': 'Sonstiger Verweis Detail',
      'sonstigerVerweis.wahrung': 'Sonstiger Verweis Währung',
      'sonstigerVerweis.betrag': 'Sonstiger Verweis Betrag',
      // Item suffix
      'itemSuffix': '(Artikel {0})',
    },
    adminPage: {
      processingHistory: {
        title: 'Verarbeitungsverlauf',
        subtitle: 'Alle Dokumentenverarbeitungstransaktionen anzeigen und verwalten',
        tabs: {
          moduleTransactions: 'Atlas-Transaktionen',
          tamperingDetection: 'Manipulationserkennung',
          emailProcessing: 'E-Mail-Verarbeitung',
        },
        filters: {
          search: 'Suche nach Dateiname, Modul oder Kennung...',
          filterByUser: 'Nach Benutzer filtern',
          searchUsers: 'Benutzer suchen...',
          allUsers: 'Alle Benutzer',
          status: 'Status',
          allStatus: 'Alle Status',
          dateFrom: 'Datum von',
          dateTo: 'Datum bis',
          clearFilters: 'Filter löschen',
        },
        table: {
          status: 'Status',
          user: 'Benutzer',
          file: 'Datei',
          module: 'Modul',
          type: 'Typ',
          dataIdentifier: 'Datenkennung',
          ipAddress: 'IP-Adresse',
          processedAt: 'Verarbeitet am',
          processingTime: 'Verarbeitungszeit',
          fileView: 'Dateiansicht',
          actions: 'Aktionen',
          noFile: 'Keine Datei',
        },
        expandedView: {
          requestDetails: 'Anfrage-Details',
          responseDetails: 'Antwort-Details',
          extractedData: 'Extrahierte Daten',
          apiResponse: 'API-Antwort',
          transactionId: 'Transaktions-ID',
          clientIp: 'Client-IP',
          userAgent: 'User-Agent',
          createdAt: 'Erstellt am',
          completedAt: 'Abgeschlossen am',
        },
        dialog: {
          title: 'Transaktionsdetails',
        },
        statusValues: {
          success: 'Erfolgreich',
          failed: 'Fehlgeschlagen',
          processing: 'Wird verarbeitet',
          pending: 'Ausstehend',
          completed: 'Abgeschlossen',
        },
        kpi: {
          totalDocuments: 'Dokumente gesamt',
          documentProcessing: 'Dokumentenverarbeitung',
          tariffClassification: 'Zolltarifierung',
          emailProcessing: 'E-Mail-Verarbeitung',
        },
        types: {
          apiPost: 'API-Post',
          extraction: 'Extraktion',
        },
        toolbar: {
          refresh: 'Aktualisieren',
          filters: 'Filter',
          viewDetails: 'Details anzeigen',
          transactionsOne: '{{count}} Transaktion',
          transactionsOther: '{{count}} Transaktionen',
        },
        emptyState: {
          noneTitle: 'Noch keine Transaktionen',
          noneBody: 'Laden Sie Dokumente hoch und verarbeiten Sie sie, um hier Ihren Transaktionsverlauf zu sehen.',
          noMatchTitle: 'Keine passenden Transaktionen',
          noMatchBody: 'Passen Sie Ihre Suche oder Filter an, um zu finden, wonach Sie suchen.',
          clearSearchFilters: 'Suche & Filter zurücksetzen',
        },
      },
      auditLogs: {
        title: 'Audit-Protokolle',
        subtitle: 'Systemaktivität und Sicherheitsüberwachung',
        filters: {
          search: 'Protokolle durchsuchen...',
          eventType: 'Ereignistyp',
          allEvents: 'Alle Ereignisse',
          user: 'Benutzer',
          allUsers: 'Alle Benutzer',
          dateFrom: 'Datum von',
          dateTo: 'Datum bis',
        },
        statistics: {
          totalEvents: 'Gesamte Ereignisse',
          userActions: 'Benutzeraktionen',
          systemEvents: 'Systemereignisse',
          securityEvents: 'Sicherheitsereignisse',
        },
        table: {
          timestamp: 'Zeitstempel',
          user: 'Benutzer',
          event: 'Ereignis',
          ipAddress: 'IP-Adresse',
          details: 'Details',
        },
      },
      atlasStructure: {
        title: 'ATLAS-Struktur',
        subtitle: 'BEO ATLAS-Anmeldungsstruktur und Felddefinitionen anzeigen',
        search: 'Felder suchen...',
        expandAll: 'Alle erweitern',
        collapseAll: 'Alle reduzieren',
        sections: {
          header: 'Kopf (kopf)',
          exporter: 'Ausführer (ausfuhrer)',
          recipient: 'Empfänger (empfaenger)',
          delivery: 'Lieferung (lieferung)',
          shipment: 'Sendung (sendung)',
          lineItems: 'Positionen (position)',
        },
        fieldInfo: {
          path: 'Feldpfad',
          type: 'Typ',
          required: 'Erforderlich',
          description: 'Beschreibung',
          example: 'Beispiel',
          yes: 'Ja',
          no: 'Nein',
        },
      },
    },
    userProfile: {
      modules: {
        title: 'Ihre Module',
        subtitle: 'Verwalten Sie Ihren Modulzugriff und Ihre Authentifizierung',
        noModules: 'Keine Module zugewiesen',
        authenticate: 'Authentifizieren',
        authenticated: 'Authentifiziert',
        expired: 'Abgelaufen',
        notAuthenticated: 'Nicht authentifiziert',
      },
    },
    emailProcessorPage: {
      title: 'E-Mail-Anhang-Prozessor',
      subtitle: 'Überwachen Sie E-Mail-Konten automatisch und verarbeiten Sie Anhänge mit KI-gestützter Dokumentenextraktion',
      tabs: {
        configuration: 'E-Mail-Konfiguration',
        threads: 'E-Mail-Threads',
        statistics: 'Statistiken',
      },
    },
    documentCombiner: {
      title: 'Dokumentenkombinationsüberprüfung',
      steps: {
        reviewData: 'Daten überprüfen',
        resolveConflicts: 'Konflikte lösen',
        validate: 'Validieren',
        submit: 'Senden',
      },
      alerts: {
        noConflicts: 'Keine Konflikte erkannt. Alle Daten erfolgreich zusammengeführt.',
        conflictsFound: '{0} Feld{1} widersprüchliche Werte. Bitte wählen Sie den richtigen Wert für jedes Feld.',
        resolveAllConflicts: 'Bitte lösen Sie alle Konflikte vor dem Fortfahren',
        reviewMergedData: 'Überprüfen Sie die zusammengeführten Daten aus {0} Dokumenten. Sie können jedes Feld bearbeiten, indem Sie auf das Bearbeitungssymbol klicken.',
      },
      sections: {
        commercialData: 'Handelsdaten',
        invoiceData: 'Rechnungsdaten',
        goodsInformation: 'Wareninformationen',
        transportData: 'Transportdaten',
      },
      fields: {
        seller: 'Verkäufer',
        sellerAddress: 'Verkäuferadresse',
        sellerCountry: 'Verkäuferland',
        sellerEori: 'Verkäufer-EORI',
        buyer: 'Käufer',
        buyerAddress: 'Käuferadresse',
        buyerCountry: 'Käuferland',
        invoiceNumber: 'Rechnungsnummer',
        invoiceDate: 'Rechnungsdatum',
        currency: 'Währung',
        value: 'Wert',
        paymentTerms: 'Zahlungsbedingungen',
        goodsDescription: 'Warenbeschreibung',
        hsCode: 'HS-Code',
        quantity: 'Menge',
        netWeight: 'Nettogewicht',
        grossWeight: 'Bruttogewicht',
        countryOfOrigin: 'Ursprungsland',
        transportMode: 'Transportart',
        billOfLading: 'Frachtbrief',
        awbNumber: 'Luftfrachtbriefnummer',
        containerNumber: 'Containernummer',
        vesselName: 'Schiffsname',
        portOfLoading: 'Verladehafen',
        portOfDischarge: 'Entladehafen',
      },
      conflict: {
        from: 'Von',
        confidence: 'Zuverlässigkeit',
      },
      validation: {
        title: 'Datenvalidierung',
        confidenceScore: 'Zuverlässigkeitswert',
        requiredFields: 'Erforderliche Felder',
        allFieldsPresent: 'Alle erforderlichen Felder vorhanden',
        missingFields: 'Fehlende Felder: {0}',
        mergedDataSummary: 'Zusammengefasste Datenzusammenfassung',
        parties: 'Parteien',
        from: 'Von',
        to: 'An',
        invoice: 'Rechnung',
        number: 'Nummer',
        goods: 'Waren',
        notAvailable: 'N/V',
      },
      actions: {
        back: 'Zurück',
        next: 'Weiter',
        submitToAtlas: 'An BEO ATLAS senden',
        tryAgain: 'Erneut versuchen',
      },
      submission: {
        title: 'An BEO ATLAS senden',
        chooseMode: 'Wählen Sie den Übermittlungsmodus und fügen Sie eine optionale Referenz hinzu.',
        testMode: 'Testmodus',
        production: 'Produktion',
        customReference: 'Benutzerdefinierte Referenz (Optional)',
        success: 'Erfolgreich übermittelt!',
        submissionId: 'Übermittlungs-ID: {0}',
        failed: 'Übermittlung fehlgeschlagen',
      },
      processing: 'Dokumente werden verarbeitet...',
    },
    complianceChecker: {
      title: 'KI-gestützte Compliance-Prüfung',
      subtitle: 'Mistral AI Erweitert',
      options: {
        semanticMatching: 'Semantisches Matching',
        semanticMatchingTooltip: '95% Genauigkeit mit mistral-embed für Entitätsabgleich',
        autoEntityExtraction: 'Automatische Entitätsextraktion',
        autoEntityExtractionTooltip: 'Automatische Extraktion von Entitäten mit mistral-large',
        riskAssessment: 'Risikobewertung',
        riskAssessmentTooltip: 'KI-gestützte Risikomusteranalyse',
      },
      steps: {
        uploadDocument: 'Dokument hochladen',
        documentProcessing: 'Dokumentenverarbeitung',
        entityExtraction: 'Entitätsextraktion',
        complianceAnalysis: 'Compliance-Analyse',
        results: 'Ergebnisse',
      },
      upload: {
        dragDrop: 'Dokument per Drag & Drop ablegen',
        dropHere: 'Dokument hier ablegen',
        clickToSelect: 'oder zum Auswählen klicken (PDF, PNG, JPG)',
      },
      processing: {
        withMistralOcr: 'Dokument wird mit Mistral OCR verarbeitet...',
        runningAnalysis: 'KI-gestützte Compliance-Analyse läuft...',
        success: 'Dokument erfolgreich verarbeitet: {0}',
      },
      extraction: {
        aiExtracted: 'KI hat {0} Entitäten und {1} Produkte mit {2}% Zuverlässigkeit extrahiert',
        entities: 'Entitäten ({0})',
        products: 'Produkte ({0})',
        transactionDetails: 'Transaktionsdetails',
        type: 'Typ',
        route: 'Route',
        totalValue: 'Gesamtwert',
        reference: 'Referenz',
        hsCode: 'HS',
        quantity: 'Menge',
      },
      actions: {
        runComplianceCheck: 'KI-Compliance-Prüfung durchführen',
        checkAnother: 'Anderes Dokument prüfen',
      },
      results: {
        status: 'Status: {0}',
        riskAssessment: 'Risikobewertung',
        riskScore: 'Risikobewertung',
        riskLevel: 'Risikostufe',
        highestRisks: 'Höchste Risiken',
        aiRecommendations: 'KI-Empfehlungen',
      },
      toasts: {
        processing: '🔍 Dokument wird verarbeitet...',
        extracting: '🤖 Extrahierte Daten werden verarbeitet...',
        foundEntities: '✨ {0} Entitäten und {1} Produkte gefunden!',
        runningCheck: '🚀 Compliance-Prüfung läuft...',
        checkPassed: '✅ Compliance-Prüfung bestanden!',
        issuesDetected: '⚠️ Compliance-Probleme erkannt!',
        reviewRequired: '📋 Überprüfung erforderlich',
        timeout: 'Dokumentenverarbeitung abgelaufen. Bitte versuchen Sie es mit einer kleineren Datei erneut.',
        extractionFailed: 'Daten konnten nicht aus dem Dokument extrahiert werden. Bitte stellen Sie sicher, dass das Dokument gültig ist.',
        checkTimeout: 'Compliance-Prüfung abgelaufen. Bitte versuchen Sie es erneut.',
        serviceUnavailable: 'Compliance-Service nicht verfügbar. Bitte versuchen Sie es später erneut.',
        failed: 'Dokumentenverarbeitung fehlgeschlagen - bitte versuchen Sie es erneut',
        checkFailed: 'Compliance-Prüfung fehlgeschlagen',
        noDocumentText: 'Kein Dokumententext verfügbar',
      },
    },
    documentAnnotator: {
      title: 'Anmerkungen',
      statistics: {
        totalAnnotations: '{0} Anmerkungen',
        criticalCount: '{0} Kritisch',
        missingCount: '{0} Fehlend',
        complete: '{0}% Vollständig',
        complianceIssues: '{0} Compliance-Probleme gefunden',
      },
      modes: {
        view: 'Anzeigen',
        edit: 'Bearbeiten',
      },
      types: {
        critical: 'Kritisch',
        compliance: 'Compliance',
        missing: 'Fehlend',
        workflow: 'Workflow',
        comment: 'Kommentar',
      },
      priority: {
        critical: 'Kritisch',
        high: 'Hoch',
        medium: 'Mittel',
        low: 'Niedrig',
      },
      status: {
        resolved: 'Gelöst',
        unresolved: 'Ungelöst',
      },
      actions: {
        refresh: 'Aktualisieren',
        export: 'Exportieren',
        toggleAnnotations: 'Anmerkungen umschalten',
        toggleCompliance: 'Compliance umschalten',
        toggleRouting: 'Routing umschalten',
        startCollaboration: 'Zusammenarbeit starten',
        saveAnnotation: 'Anmerkung speichern',
        resolveAnnotation: 'Anmerkung lösen',
      },
      messages: {
        loading: 'Anmerkungen werden geladen...',
        noDocumentContent: 'Kein Dokumenteninhalt verfügbar',
        pdfNotAvailable: 'PDF-Vorschau nicht verfügbar. Extrahierte Daten werden angezeigt:',
        success: 'Dokument erfolgreich annotiert',
        annotationAdded: 'Anmerkung hinzugefügt',
        annotationResolved: 'Anmerkung gelöst',
        collaborationStarted: 'Zusammenarbeit gestartet',
        annotationsExported: 'Anmerkungen exportiert',
        failed: 'Anmerkungen konnten nicht geladen werden',
        addFailed: 'Anmerkung konnte nicht hinzugefügt werden',
        resolveFailed: 'Anmerkung konnte nicht gelöst werden',
        collaborationFailed: 'Zusammenarbeit konnte nicht gestartet werden',
        exportFailed: 'Anmerkungen konnten nicht exportiert werden',
      },
      collaboration: {
        title: 'Kollaborative Anmerkung',
        realTime: 'Echtzeit-Zusammenarbeit aktiviert',
        participants: 'Teilnehmer',
        activeUsers: 'Aktive Benutzer',
      },
      compliance: {
        title: 'Compliance-Bericht',
        compliant: 'Konform',
        nonCompliant: 'Nicht konform',
        requiresReview: 'Erfordert Überprüfung',
      },
      routing: {
        title: 'Dokumentenrouting',
        decision: 'Routing-Entscheidung',
        destination: 'Ziel',
        priority: 'Priorität',
      },
    },
    profile: {
      title: 'Mein Profil',
      personalInformation: 'Persönliche Informationen',
      accountStatus: 'Kontostatus',
      planFeatures: 'Plan-Funktionen',
      billingInformation: 'Rechnungsinformationen',
      firstName: 'Vorname',
      lastName: 'Nachname',
      phoneNumber: 'Telefonnummer',
      email: 'E-Mail',
      fullName: 'Vollständiger Name',
      username: 'Benutzername',
      memberSince: 'Mitglied seit',
      active: 'Aktiv',
      inactive: 'Inaktiv',
      lastUpdated: 'Zuletzt aktualisiert',
      administrator: 'Administrator',
      user: 'Benutzer',
      essential: 'Essential',
      advanced: 'Advanced',
      enterprise: 'Enterprise',
      planAdopted: 'Plan übernommen',
      nextBilling: 'Nächste Abrechnung',
      paymentStatus: 'Zahlungsstatus',
      paymentMethod: 'Zahlungsmethode',
      paid: 'Bezahlt',
      pending: 'Ausstehend',
      overdue: 'Überfällig',
      failed: 'Fehlgeschlagen',
      notSet: 'Nicht festgelegt',
      settingsHintTitle: 'Module, Einstellungen & ATLAS-Konfiguration',
      settingsHintSubtitle: 'Verwalten Sie Modul-Authentifizierung, E-Mail-Sprache und ATLAS-Einstellungen unter Einstellungen',
      paymentReference: 'Zahlungsreferenz',
      billingAddress: 'Rechnungsadresse',
      advancedExtraction: 'Erweiterte KI-Extraktion',
      tamperingDetection: 'Dokumentenfälschungserkennung',
      workflowAutomation: 'Workflow-Automatisierung',
      prioritySupport: 'Priority-Support',
      customApiDevelopment: 'Benutzerdefinierte API-Entwicklung',
      enabled: 'Aktiviert',
      notAvailable: 'Nicht verfügbar',
      support24x7: '24/7 Support',
      standardSupport: 'Standard-Support',
      available: 'Verfügbar',
      // Usage Statistics
      usageStatistics: 'Nutzungsstatistiken',
      documentsUsedThisMonth: 'Diesen Monat verwendete Dokumente',
      usagePercentage: 'Nutzung in Prozent',
      daysUntilReset: 'Tage bis zum Reset',
      dataRetentionPeriod: 'Datenspeicherzeitraum',
      managePlan: 'Plan verwalten',
      planSummary: 'Plan-Zusammenfassung',
      usageNearLimit: 'Sie nähern sich Ihrem monatlichen Limit',
      usageAtLimit: 'Sie haben Ihr monatliches Limit erreicht',
      usageExceeded: 'Sie haben Ihr monatliches Limit überschritten',
      documentsRemaining: '{0} Dokumente verbleibend',
      unlimitedDocuments: 'Unbegrenzte Dokumente',
      daysSingular: 'Tag',
      daysPlural: 'Tage',
      // ATLAS Configuration
      atlasConfiguration: 'ATLAS-Konfiguration',
      atlasCredentials: 'ATLAS-Zugangsdaten',
      complete: 'Vollständig',
      incomplete: 'Unvollständig',
      atlasConfigDescription: 'Konfigurieren Sie die ATLAS-Zugangsdaten Ihres Unternehmens für automatisierte Zollanmeldungen. Diese Einstellungen sind für BEO ATLAS-Modulübermittlungen erforderlich.',
      atlasUserId: 'ATLAS-Benutzer-ID',
      atlasUserIdHelper: 'ATLAS-Benutzer-ID Ihres Unternehmens',
      eoriNumber: 'EORI-Nummer',
      eoriHelper: 'Economic Operators Registration and Identification Nummer',
      customsOffice: 'Zollstelle',
      customsOfficeHelper: 'Ihr zugewiesener Zollstellencode',
      declarationOffice: 'Anmeldestelle',
      declarationOfficeHelper: 'Stellencode für Anmeldungen',
      exportOffice: 'Ausfuhrstelle',
      exportOfficeHelper: 'Ausfuhrstellencode',
      companyInformation: 'Unternehmensinformationen',
      companyName: 'Firmenname',
      companyNameHelper: 'Vollständiger rechtlicher Firmenname',
      streetAddress: 'Straßenadresse',
      streetAddressHelper: 'Firmenanschrift',
      postalCode: 'Postleitzahl',
      postalCodeHelper: 'Postleitzahl',
      city: 'Stadt',
      cityHelper: 'Stadtname',
      countryCode: 'Ländercode',
      countryCodeHelper: 'ISO-Ländercode (z.B. DE, FR)',
      defaultSettings: 'Standardeinstellungen',
      currency: 'Währung',
      currencyHelper: 'Standardwährung für Transaktionen',
      incoterm: 'Incoterm',
      incotermHelper: 'Standard-Incoterms (z.B. FOB, CIF)',
      originCountry: 'Ursprungsland',
      originCountryHelper: 'Standard-Ursprungsland für Waren',
      transportMode: 'Transportart',
      transportModeHelper: 'Standard-Transportart',
      transportModes: {
        sea: 'See',
        rail: 'Schiene',
        road: 'Straße',
        air: 'Luft',
        inlandWaterway: 'Binnenschifffahrt',
      },
      countries: {
        germany: 'Deutschland (DE)',
        china: 'China (CN)',
        france: 'Frankreich (FR)',
        usa: 'USA (US)',
        uk: 'Vereinigtes Königreich (GB)',
      },
      saveConfiguration: 'Konfiguration speichern',
      saving: 'Wird gespeichert...',
      configSaved: 'ATLAS-Konfiguration erfolgreich gespeichert',
      configSaveFailed: 'ATLAS-Konfiguration konnte nicht gespeichert werden',
    },
    auth: {
      login: 'Anmelden',
      register: 'Registrieren',
      logout: 'Abmelden',
      email: 'E-Mail',
      password: 'Passwort',
      confirmPassword: 'Passwort bestätigen',
      firstName: 'Vorname',
      lastName: 'Nachname',
      phoneNumber: 'Telefonnummer',
      rememberMe: 'Angemeldet bleiben',
      forgotPassword: 'Passwort vergessen?',
      signIn: 'Anmelden',
      signUp: 'Registrieren',
      createAccount: 'Konto erstellen',
      alreadyHaveAccount: 'Haben Sie bereits ein Konto?',
      dontHaveAccount: 'Sie haben noch kein Konto?',
      signingIn: 'Anmeldung läuft...',
      username: 'Benutzername',
      usernameOrEmail: 'Benutzername oder E-Mail',
      enterUsername: 'Geben Sie Ihren Benutzernamen oder E-Mail ein',
      enterPassword: 'Geben Sie Ihr Passwort ein',
      loginSuccessful: 'Anmeldung erfolgreich!',
      registrationSuccessful: 'Registrierung erfolgreich',
      logoutSuccessful: 'Abmeldung erfolgreich',
      // Validation messages
      usernameRequired: 'Bitte geben Sie Ihren Benutzernamen oder E-Mail-Adresse ein',
      usernameMinLength: 'Benutzername oder E-Mail muss mindestens 3 Zeichen lang sein',
      usernameMaxLength: 'Benutzername oder E-Mail ist zu lang',
      passwordRequired: 'Bitte geben Sie Ihr Passwort ein',
      passwordEmpty: 'Passwort kann nicht leer sein',
      passwordMaxLength: 'Passwort ist zu lang',
      // Error messages
      invalidCredentials: 'Ungültige E-Mail oder Passwort',
      credentialsNoMatch: 'Anmeldedaten stimmen nicht überein. Bitte überprüfen Sie Ihren Benutzernamen/E-Mail und Passwort. Falls Sie Ihr Passwort vergessen haben, klicken Sie unten auf "Passwort vergessen?".',
      invalidUsernamePassword: 'Ungültiger Benutzername oder Passwort. Ihre Anmeldedaten stimmen nicht mit unseren Aufzeichnungen überein. Bitte überprüfen und erneut versuchen.',
      accountInactive: 'Ihr Konto ist inaktiv. Bitte kontaktieren Sie den Support, um Ihr Konto zu reaktivieren.',
      invalidPasswordFormat: 'Ungültiges Passwort-Format. Bitte geben Sie ein gültiges Passwort ein.',
      invalidUsernameFormat: 'Ungültiges Benutzername-Format. Bitte geben Sie einen gültigen Benutzernamen oder eine E-Mail-Adresse ein.',
      invalidLoginInfo: 'Ungültige Anmeldeinformationen. Bitte überprüfen Sie Ihre Anmeldedaten und versuchen Sie es erneut.',
      accessDenied: 'Zugriff verweigert. Ihr Konto könnte gesperrt sein. Bitte kontaktieren Sie den Support für Unterstützung.',
      serviceUnavailable: 'Anmeldedienst ist vorübergehend nicht verfügbar. Bitte versuchen Sie es in einigen Augenblicken erneut.',
      invalidFormat: 'Bitte geben Sie gültigen Benutzernamen/E-Mail und Passwort ein. Überprüfen Sie Ihr Eingabeformat.',
      tooManyAttempts: 'Zu viele Anmeldeversuche. Bitte warten Sie einige Minuten, bevor Sie es erneut versuchen.',
      serverError: 'Serverfehler aufgetreten. Bitte versuchen Sie es später erneut oder kontaktieren Sie den Support, wenn das Problem weiterhin besteht.',
      networkError: 'Verbindung zum Server nicht möglich. Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
      connectionError: 'Netzwerkverbindungsfehler. Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
      timeout: 'Anfrage-Timeout. Bitte überprüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
      passwordsDoNotMatch: 'Passwörter stimmen nicht überein',
      passwordTooShort: 'Passwort muss mindestens 8 Zeichen lang sein',
      emailRequired: 'E-Mail ist erforderlich',
      firstNameRequired: 'Vorname ist erforderlich',
      lastNameRequired: 'Nachname ist erforderlich',
      invalidPhoneNumber: 'Bitte geben Sie eine gültige Telefonnummer ein',
      // Additional validation messages
      invalidEmailFormat: 'Ungültiges E-Mail-Format',
      usernameMinChars: 'Benutzername muss mindestens 3 Zeichen lang sein',
      usernameMaxChars: 'Benutzername muss weniger als 50 Zeichen haben',
      usernameInvalidChars: 'Benutzername darf nur Buchstaben, Zahlen und Unterstriche enthalten',
      passwordMinChars: 'Passwort muss mindestens 8 Zeichen lang sein',
      passwordMaxChars: 'Passwort muss weniger als 100 Zeichen haben',
      passwordUppercase: 'Passwort muss mindestens einen Großbuchstaben enthalten',
      passwordLowercase: 'Passwort muss mindestens einen Kleinbuchstaben enthalten',
      passwordDigit: 'Passwort muss mindestens eine Ziffer enthalten',
      passwordsMustMatch: 'Passwörter müssen übereinstimmen',
      // Form labels
      selectPlan: 'Plan auswählen',
      phoneOptional: 'Telefonnummer (Optional)',
      planHelper: 'Wählen Sie den Plan, der am besten zu Ihren Bedürfnissen passt',
      // Buttons and actions
      haveAccount: 'Haben Sie bereits ein Konto?',
      noAccount: 'Noch kein Konto?',
      signInHere: 'Hier anmelden',
      registerHere: 'Hier registrieren',
      // Register modal
      registerSubtitle: 'Treten Sie uns noch heute bei und beginnen Sie, Ihre Dokumente effizient zu verarbeiten.',
      creating: 'Erstellen...',
      // Login modal
      loginSubtitle: 'Willkommen zurück! Bitte melden Sie sich bei Ihrem Konto an.',
      // Error messages for login
      loginFailedCredentials: 'Anmeldung fehlgeschlagen. Ihre Anmeldedaten stimmen nicht überein. Bitte überprüfen Sie Ihren Benutzernamen/E-Mail und Ihr Passwort.',
      credentialsMismatch: 'Anmeldedaten stimmen nicht überein. Bitte überprüfen Sie Ihren Benutzernamen/E-Mail und Ihr Passwort noch einmal. Wenn Sie Ihr Passwort vergessen haben, klicken Sie unten auf "Passwort vergessen?".',
      accountCreated: 'Ihr Konto wurde erfolgreich erstellt.',
      pendingApproval: 'Ihr Konto wartet auf Admin-Genehmigung. Sie erhalten eine Benachrichtigung, wenn Ihr Konto aktiviert wird.',
      approvalNote: '💡 Sie können sich anmelden, sobald ein Administrator Ihr Konto genehmigt hat.',
      registrationSuccess: 'Registrierung erfolgreich! Willkommen an Bord!',
      registrationFailed: 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.',
      validationFailed: 'Validierung fehlgeschlagen',
      confirmPasswordRequired: 'Bitte bestätigen Sie Ihr Passwort',
      // Plan details
      plans: {
        essential: 'Essential - €29/Monat',
        advanced: 'Advanced - €79/Monat',
        enterprise: 'Enterprise - €199/Monat',
        essentialDesc: 'Bis zu 500 Dokumente/Monat, Basis-Extraktion, Standard-Support',
        advancedDesc: 'Bis zu 2.500 Dokumente/Monat, KI-Extraktion, Priority-Support, Workflow-Automatisierung',
        enterpriseDesc: 'Unbegrenzte Dokumente, Enterprise-Funktionen, 24/7-Support, individuelle Entwicklung',
      },
    },
    admin: {
      title: 'Admin-Dashboard',
      userManagement: 'Benutzerverwaltung',
      moduleManagement: 'Modulverwaltung',
      groupManagement: 'Gruppenverwaltung',
      systemSettings: 'Systemeinstellungen',
      analytics: 'Analytik',
      users: 'Benutzer',
      superAdmins: 'Super-Admins',
      modules: 'Module',
      transactions: 'Transaktionen',
      reports: 'Berichte',
      settings: 'Einstellungen',
      groups: 'Gruppen',
      auditLogs: 'Audit-Protokolle',
      totalUsers: 'Benutzer insgesamt',
      activeUsers: 'Aktive Benutzer',
      totalModules: 'Modul insgesamt',
      totalGroups: 'Gruppen insgesamt',
      activeModules: 'Aktive Module',
      totalTransactions: 'Transaktionen insgesamt',
      createModule: 'Modul erstellen',
      createGroup: 'Gruppe erstellen',
      createUser: 'Benutzer erstellen',
      editUser: 'Benutzer bearbeiten',
      deleteUser: 'Benutzer löschen',
      name: 'Name',
      description: 'Beschreibung',
      status: 'Status',
      created: 'Erstellt',
      actions: 'Aktionen',
      username: 'Benutzername',
      email: 'E-Mail',
      role: 'Rolle',
      joined: 'Beigetreten',
      members: 'Mitglieder',
      active: 'Aktiv',
      inactive: 'Inaktiv',
      admin: 'Administrator',
      user: 'Benutzer',
      confirmDelete: 'Löschen bestätigen',
      deleteConfirmMessage: 'Sind Sie sicher, dass Sie dieses {0} löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      module: 'Modul',
      group: 'Gruppe',
      item: 'Element',
      resetPassword: 'Passwort zurücksetzen',
      userDetails: 'Benutzerdetails',
      passwordReset: 'Passwort zurücksetzen',
      newPassword: 'Neues Passwort',
      passwordRequirements: 'Passwort-Anforderungen',
      chars8Plus: '8+ Zeichen',
      mixedCase: 'Groß-/Kleinschreibung',
      numbers: 'Zahlen',
      symbols: 'Symbole',
      resetPasswordButton: 'Passwort zurücksetzen',
      resetting: 'Wird zurückgesetzt...',
      createSecurePassword: 'Erstellen Sie ein neues sicheres Passwort für',
      planDetails: 'Plandetails',
      planType: 'Plan-Typ',
      planStatus: 'Plan-Status',
      planAdopted: 'Plan übernommen',
      monthlyLimit: 'Monatslimit',
      documentsUsed: 'Verwendete Dokumente',
      limitResetDate: 'Limit-Reset-Datum',
      planFeatures: 'Plan-Funktionen',
      essential: 'Essential',
      advanced: 'Advanced',
      enterprise: 'Enterprise',
      suspended: 'Gesperrt',
      expired: 'Abgelaufen',
      advancedAiExtraction: 'Erweiterte KI-Extraktion',
      tamperingDetection: 'Fälschungserkennung',
      workflowAutomation: 'Workflow-Automatisierung',
      prioritySupport: 'Priority-Support',
      customApiDevelopment: 'Benutzerdefinierte API-Entwicklung',
      basicExtraction: 'Basis-Extraktion',
      editLimit: 'Limit bearbeiten',
      saveLimit: 'Limit speichern',
      cancelEdit: 'Bearbeitung abbrechen',
      extendLimit: 'Limit erweitern',
      resetUsage: 'Nutzung zurücksetzen',
      enterLimit: 'Limit eingeben oder "unlimited"',
      limitUpdated: 'Benutzerlimits erfolgreich aktualisiert',
      limitExtended: 'Limit um {0} Dokumente erweitert',
      usageReset: 'Benutzernutzung erfolgreich zurückgesetzt',
      updateLimitsFailed: 'Aktualisierung der Benutzerlimits fehlgeschlagen',
      extendLimitFailed: 'Erweiterung des Benutzerlimits fehlgeschlagen',
      resetUsageFailed: 'Zurücksetzen der Benutzernutzung fehlgeschlagen',
      editResetDate: 'Reset-Datum bearbeiten',
      saveResetDate: 'Reset-Datum speichern',
      resetDateUpdated: 'Reset-Datum erfolgreich aktualisiert',
      updateResetDateFailed: 'Aktualisierung des Reset-Datums fehlgeschlagen',
      nextMonth: 'Nächster Monat',
      today: 'Heute',
      // User Form
      userForm: {
        createUser: 'Neuen Benutzer erstellen',
        editUser: 'Benutzer bearbeiten',
        userInformation: 'Benutzerinformationen',
        firstName: 'Vorname',
        lastName: 'Nachname',
        email: 'E-Mail',
        username: 'Benutzername',
        phoneNumber: 'Telefonnummer (optional)',
        password: 'Passwort',
        newPasswordHint: 'Neues Passwort (leer lassen, um das aktuelle zu behalten)',
        administratorPrivileges: 'Administrator-Berechtigungen',
        activeUser: 'Aktiver Benutzer',
        inactiveUser: 'Inaktiver Benutzer',
        authenticated: 'AUTHENTIFIZIERT',
        blocked: 'GESPERRT',
        userCanLogin: 'Benutzer kann sich anmelden und auf das System zugreifen',
        userBlocked: 'Benutzer ist vom Anmelden blockiert',
        authenticationManagement: 'Authentifizierungsverwaltung',
        moduleAuthStatus: 'Modul-Authentifizierungsstatus',
        moduleAuthStatusDesc: 'Dieser Benutzer kann Authentifizierungs-Token für verschiedene Module gespeichert haben. Verwenden Sie "Auth zurücksetzen", um eine erneute Authentifizierung mit allen Modulen zu erzwingen.',
        moduleAccess: 'Modulzugriff',
        moduleAccessDesc: 'Wählen Sie aus, auf welche Module dieser Benutzer für die Dokumentenverarbeitung zugreifen kann.',
        assignedModules: 'Zugewiesene Module',
        selectModules: 'Module auswählen...',
        moduleAccessHelperText: 'Wählen Sie aus, auf welche Module dieser Benutzer für die Dokumentenverarbeitung zugreifen kann',
        noDescriptionAvailable: 'Keine Beschreibung verfügbar',
        deactivateUser: 'Benutzerkonto deaktivieren',
        activateUser: 'Benutzerkonto aktivieren',
        deactivate: 'Deaktivieren',
        activate: 'Aktivieren',
        processing: 'Verarbeitung läuft...',
        resetUserPassword: 'Benutzerpasswort zurücksetzen',
        resetPasswordButton: 'Passwort zurücksetzen',
        resetting: 'Wird zurückgesetzt...',
        resetAllModuleAuth: 'Alle Modul-Authentifizierungs-Token zurücksetzen',
        resetAuth: 'Auth zurücksetzen',
        cancel: 'Abbrechen',
        saving: 'Wird gespeichert...',
        updateUser: 'Benutzer aktualisieren',
        createNewUser: 'Benutzer erstellen',
        resetUserAuthentication: 'Benutzer-Authentifizierung zurücksetzen',
        warning: 'Warnung:',
        resetAuthWarning: 'Diese Aktion setzt alle Authentifizierungs-Token zurück für',
        performFollowingActions: 'Dies führt folgende Aktionen aus:',
        removeAllTokens: 'Alle Modul-Authentifizierungs-Token entfernen',
        userLoseAccess: 'Benutzer verliert Zugriff auf alle derzeit authentifizierten Module',
        forceReauth: 'Erneute Authentifizierung erzwingen',
        userMustReauth: 'Benutzer muss sich erneut bei jedem Modul authentifizieren, das er verwenden möchte',
        immediateEffect: 'Sofortige Wirkung',
        cannotUndone: 'Diese Aktion tritt sofort in Kraft und kann nicht rückgängig gemacht werden',
        useCases: 'Anwendungsfälle:',
        useCasesDesc: 'Sicherheitsbedenken, Zugriffsprobleme, Token-Beschädigung oder Richtlinienänderungen',
        resetAuthentication: 'Authentifizierung zurücksetzen',
        resetPassword: 'Passwort zurücksetzen',
        createSecurePassword: 'Erstellen Sie ein neues sicheres Passwort für',
        passwordRequirements: 'Passwort-Anforderungen',
        passwordStrength: 'Passwortstärke',
        weak: 'Schwach',
        medium: 'Mittel',
        strong: 'Stark',
        newPassword: 'Neues Passwort',
        confirmPassword: 'Passwort bestätigen',
        passwordsDoNotMatch: 'Passwörter stimmen nicht überein',
        securityTips: 'Sicherheitstipps:',
        securityTipsDesc: 'Verwenden Sie eine Kombination aus Groß- und Kleinbuchstaben, Zahlen und Sonderzeichen. Vermeiden Sie gebräuchliche Wörter oder persönliche Informationen.',
        // Validation messages
        invalidEmail: 'Ungültige E-Mail',
        emailRequired: 'E-Mail ist erforderlich',
        usernameRequired: 'Benutzername ist erforderlich',
        usernameMinLength: 'Benutzername muss mindestens 3 Zeichen lang sein',
        firstNameRequired: 'Vorname ist erforderlich',
        lastNameRequired: 'Nachname ist erforderlich',
        passwordRequired: 'Passwort ist erforderlich',
        passwordMinLength: 'Passwort muss mindestens 6 Zeichen lang sein',
        passwordMinLength8: 'Passwort muss mindestens 8 Zeichen lang sein',
        passwordTooWeak: 'Das Passwort ist zu schwach. Bitte wählen Sie ein stärkeres Passwort.',
        // Success messages
        userUpdated: 'Benutzer erfolgreich aktualisiert',
        userCreated: 'Benutzer erfolgreich erstellt',
        userSavedWithModules: 'Benutzer mit {0} Modulzuweisungen gespeichert',
        modulesAssignFailed: 'Benutzer gespeichert, aber Modulzuweisungen fehlgeschlagen',
        passwordResetSuccess: 'Passwort erfolgreich zurückgesetzt',
        authResetSuccess: 'Authentifizierung erfolgreich zurückgesetzt',
        // Error messages
        saveFailed: 'Fehler beim Speichern des Benutzers',
        toggleStatusFailed: 'Fehler beim Umschalten des Benutzerstatus',
        resetPasswordFailed: 'Fehler beim Zurücksetzen des Passworts',
        resetAuthFailed: 'Fehler beim Zurücksetzen der Benutzer-Authentifizierung',
        loadModulesFailed: 'Fehler beim Laden der Module',
        tokensReset: '{0} Authentifizierungs-Token für {1} entfernt',
        pending: 'Ausstehend',
      },
    },
    atlasStructure: {
      title: 'ATLAS-Strukturansicht',
      loading: 'ATLAS-Struktur wird geladen...',
      loadFailed: 'ATLAS-Struktur konnte nicht geladen werden',
      documentation: {
        officialSpec: 'Offizielle Spezifikation',
        location: 'Standort',
        usage: 'Verwendung',
      },
      sections: {
        positionFields: 'ATLAS-Positionsfelder ({0} Felder)',
        requiredFieldsOnly: 'Nur Pflichtfelder ({0} Felder)',
        jsonTemplate: 'ATLAS JSON-Vorlage (für Mistral AI)',
        fieldTypeSummary: 'Feldtyp-Zusammenfassung',
      },
      table: {
        fieldName: 'Feldname',
        description: 'Beschreibung',
        type: 'Typ',
        required: 'Erforderlich',
        source: 'Quelle',
        default: 'Standard',
      },
      labels: {
        required: 'Erforderlich',
        optional: 'Optional',
        string: 'Zeichenkette',
        float: 'Gleitkomma',
        array: 'Array',
      },
      fieldCounts: {
        string: 'Zeichenkette: {0}',
        float: 'Gleitkomma: {0}',
        array: 'Array: {0}',
        required: 'Erforderlich: {0}',
        optional: 'Optional: {0}',
      },
      actions: {
        copyTemplate: 'Vorlage kopieren',
        copied: 'Kopiert!',
        completeStructure: 'Vollständige ATLAS-Struktur',
        copyCompleteStructure: 'Vollständige Struktur kopieren',
      },
      toasts: {
        loadFailed: 'ATLAS-Struktur konnte nicht geladen werden',
        copySuccess: '{0} in Zwischenablage kopiert',
        copyFailed: 'Kopieren in Zwischenablage fehlgeschlagen',
      },
    },
    errors: {
      somethingWentWrong: 'Etwas ist schiefgelaufen',
      networkError: 'Netzwerkfehler aufgetreten',
      serverError: 'Serverfehler aufgetreten',
      notFound: 'Ressource nicht gefunden',
      unauthorized: 'Unbefugter Zugriff',
      forbidden: 'Zugriff verboten',
      validationError: 'Validierungsfehler',
      fileUploadError: 'Datei-Upload fehlgeschlagen',
      processingError: 'Verarbeitung fehlgeschlagen',
      tryAgain: 'Bitte versuchen Sie es erneut',
      contactSupport: 'Kontaktieren Sie den Support, wenn das Problem weiterhin besteht',
    },
    languages: {
      english: 'Englisch',
      german: 'Deutsch',
      selectLanguage: 'Sprache auswählen',
    },
    notFound: {
      title: 'Seite nicht gefunden',
      message: 'Entschuldigung, die gesuchte Seite existiert nicht oder wurde verschoben.',
      goBack: 'Zurück',
      goHome: 'Zur Startseite',
    },
    documentSetManager: {
      steps: {
        upload: 'Dokumente hochladen',
        detectRelationships: 'Beziehungen erkennen',
        reviewSubmit: 'Prüfen & Absenden',
      },
    },
    features: {
      documentProcessing: {
        title: 'Dokumentenverarbeitung',
        subtitle: 'Laden Sie Dokumente hoch und extrahieren Sie Daten mit KI'
      },
      complianceDashboard: {
        title: 'KI-Compliance-Engine',
        subtitle: 'Umfassende Compliance-Prüfung mit Sanktionsscreening'
      },
      documentSetManager: {
        title: 'Dokumentenübergreifende Kombination',
        subtitle: 'Kombinieren Sie mehrere Dokumente für Zollanmeldungen'
      },
      magicMarker: {
        title: 'Magic Marker Pro',
        subtitle: 'Intelligente Dokumentenannotation mit KI-Unterstützung'
      },
      transactionHistory: {
        title: 'Transaktionsverlauf',
        subtitle: 'Zeigen Sie alle Ihre Verarbeitungshistorie und API-Integrationen an'
      },
      exportStatusPage: {
        title: 'Exportstatus',
        noModule: 'Kein Modul ausgewählt. Bitte gehen Sie zurück und wählen Sie zuerst ein Modul aus.',
        loading: 'Exportstatus wird geladen...',
        emptyTitle: 'Keine Exportstatus-Einträge gefunden',
        emptyDescription: 'Exportstatus-Einträge werden hier angezeigt, nachdem Sie Zollanmeldungen eingereicht haben.',
        refresh: 'Aktualisieren',
        refreshing: 'Aktualisieren...',
        records: '{{count}} Eintrag/Einträge',
        authExpired: 'Authentifizierung für {{module}} abgelaufen. Bitte erneut authentifizieren.',
        notAuthenticated: 'Nicht authentifiziert bei {{module}}. Bitte zuerst authentifizieren.',
        fetchError: 'Exportstatus konnte nicht abgerufen werden. Bitte versuchen Sie es erneut.',
        messageNumber: 'Nachrichtennummer',
        date: 'Datum',
        mrnLrn: 'MRN / LRN',
        recipient: 'Empfänger',
        shipmentId: 'Sendungskennzeichen',
        status: 'Status',
        releasedPdfs: 'Freigegebene PDFs',
        notificationPdfs: 'Benachrichtigungs-PDFs',
        extPdfs: 'Ext. PDFs',
        released: 'Freigegeben',
        notification: 'Benachrichtigung',
        external: 'Extern',
        download: '{{label}} {{index}} herunterladen',
        downloadFailed: 'Herunterladen fehlgeschlagen',
        page: 'Seite',
        rowsPerPage: 'Zeilen pro Seite',
        allUsers: 'Alle Benutzer',
        currentUser: 'Aktueller Benutzer',
        filters: 'Filter',
        filterByLrn: 'Nach MRN/LRN filtern',
        filterByDate: 'Nach Datum filtern',
        filterByStatus: 'Nach Status filtern',
        allStatuses: 'Alle Status',
        applyFilters: 'Anwenden',
        clearFilters: 'Zurücksetzen',
        authenticate: 'Authentifizieren',
        statusRisiko: 'Risiko',
        statusUeberlassen: 'Überlassen',
        statusAusfuhranzeige: 'Ausfuhranzeige',
        statusStorno: 'Storno',
        statusFehlermeldung: 'Fehlermeldung',
        statusStatusmeldung: 'Statusmeldung',
      }
    },
    documentSets: {
      newDocumentSet: 'Neuer Dokumentensatz',
    },
    tamperingDetection: {
      pageTitle: 'Dokumenten-Manipulationserkennung',
      pageSubtitle: 'Fortschrittliche KI-gestützte Analyse zur Erkennung von Fälschungen und Manipulationen',
      uploadTitle: 'Dokument hochladen',
      uploadSubtitle: 'Wählen Sie eine PDF-Datei aus, um sie auf Manipulationsindikatoren zu analysieren',
      dropHere: 'PDF hier ablegen',
      dragDrop: 'PDF-Datei per Drag & Drop ablegen',
      browseHint: 'oder klicken Sie zum Durchsuchen • Nur PDF-Dateien',
      analyzing: 'Dokument wird auf Manipulationsindikatoren analysiert...',
      pdfOnly: 'Nur PDF-Dateien werden unterstützt',
      tamperingDetected: 'Manipulation erkannt',
      tamperingDescription: 'Dieses Dokument zeigt Anzeichen von Manipulation',
      documentVerified: 'Dokument verifiziert',
      verifiedDescription: 'Keine Manipulationsindikatoren erkannt',
      severityLevel: 'Schweregrad',
      riskScore: 'Risikobewertung',
      detectedIssues: 'Erkannte Probleme',
      analysisCompleted: 'Analyse in {0}ms abgeschlossen',
      analyzeAnother: 'Weiteres Dokument analysieren',
      analyzeFailed: 'Datei konnte nicht analysiert werden',
    },
    customsDeclaration: {
      title: 'KI-Zollanmeldung',
      subtitle: 'Erstellen Sie Zollanmeldungen mit KI-gestützter Dokumentenverarbeitung',
      tabs: {
        aiUpload: 'KI-Upload',
        general: 'Allgemein',
        parties: 'Beteiligte',
        items: 'Waren',
        documents: 'Dokumente',
        transport: 'Transport',
        summary: 'Zusammenfassung'
      },
      aiUpload: {
        title: 'KI-Dokumentenupload & Extraktion',
        subtitle: 'Laden Sie Dokumente hoch, um Zolldaten automatisch zu extrahieren',
        uploadDocuments: 'Dokumente hochladen',
        autoFillButton: 'Anmeldung automatisch ausfüllen',
        confidence: 'Vertrauen',
        extractedData: 'Extrahierte Daten',
        processing: 'Verarbeitung',
        completed: 'Abgeschlossen',
        failed: 'Fehlgeschlagen'
      },
      general: {
        title: 'Allgemeine Informationen',
        subtitle: 'Grundlegende Anmeldungsdetails eingeben',
        referenceNumber: 'Referenznummer',
        declarationType: 'Anmeldungsart',
        export: 'Ausfuhr',
        import: 'Einfuhr',
        declarationDate: 'Anmeldungsdatum',
        currency: 'Währung',
        totalValue: 'Gesamtwert'
      },
      parties: {
        title: 'Beteiligte Parteien',
        subtitle: 'Ausführer, Einführer und Anmelder Details eingeben',
        exporter: 'Ausführer',
        importer: 'Einführer',
        declarant: 'Anmelder',
        eori: 'EORI-Nummer',
        name: 'Name',
        address: 'Adresse',
        city: 'Stadt',
        postalCode: 'Postleitzahl',
        country: 'Land',
        contactPerson: 'Kontaktperson',
        phone: 'Telefon',
        email: 'E-Mail'
      },
      items: {
        title: 'Waren & Güter',
        subtitle: 'Anmeldungspositionen hinzufügen und konfigurieren',
        addItem: 'Position hinzufügen',
        itemNumber: 'Position Nr.',
        description: 'Beschreibung',
        hsCode: 'Warennummer',
        quantity: 'Menge',
        unit: 'Einheit',
        netWeight: 'Eigenmasse (kg)',
        grossWeight: 'Rohmasse (kg)',
        value: 'Wert',
        origin: 'Ursprungsland'
      },
      documents: {
        title: 'Begleitdokumente',
        subtitle: 'Erforderliche Dokumentation anhängen',
        uploadDocument: 'Dokument hochladen',
        documentType: 'Dokumenttyp',
        documentNumber: 'Dokumentnummer',
        documentDate: 'Dokumentdatum',
        invoice: 'Rechnung',
        packingList: 'Packzettel',
        certificate: 'Zertifikat',
        other: 'Sonstiges'
      },
      transport: {
        title: 'Transportdetails',
        subtitle: 'Versand- und Logistikinformationen eingeben',
        transportMode: 'Transportart',
        sea: 'Seefracht',
        air: 'Luftfracht',
        road: 'Straße',
        rail: 'Schiene',
        vessel: 'Schiff/Flug',
        departureDate: 'Abgangsdatum',
        arrivalDate: 'Ankunftsdatum',
        portOfLoading: 'Verladeort',
        portOfDischarge: 'Entladeort',
        incoterms: 'Incoterms'
      },
      summary: {
        title: 'Anmeldungsübersicht',
        subtitle: 'Ihre Anmeldung überprüfen und einreichen',
        validation: 'Validierung',
        exportOptions: 'Exportoptionen',
        downloadPDF: 'PDF herunterladen',
        exportJSON: 'JSON exportieren',
        submitDeclaration: 'Anmeldung einreichen'
      },
      status: {
        draft: 'Entwurf',
        submitted: 'Eingereicht',
        processing: 'In Bearbeitung',
        approved: 'Genehmigt',
        rejected: 'Abgelehnt'
      },
      actions: {
        save: 'Speichern',
        next: 'Weiter',
        previous: 'Zurück',
        submit: 'Einreichen',
        cancel: 'Abbrechen'
      }
    },
    landing: {
      header: {
        login: 'Anmelden',
        register: 'Registrieren',
      },
      hero: {
        title: 'Zollanmeldung leicht gemacht',
        subtitle: 'Laden Sie Geschäftsdokumente hoch, extrahieren Sie Daten und integrieren Sie sie mit leistungsstarken APIs',
        getStartedFree: 'Kostenlos starten',
        signIn: 'Anmelden',
      },
      features: {
        title: 'Leistungsstarke Funktionen',
        subtitle: 'Alles was Sie zur Verarbeitung und Integration Ihrer Dokumente benötigen',
        documentUpload: {
          title: 'Dokument-Upload',
          description: 'Einfacher Upload von Bildern und PDFs für automatisierte Datenextraktion mit Drag-and-Drop-Oberfläche.',
        },
        smartExtraction: {
          title: 'Intelligente Extraktion',
          description: 'Erweiterte KI-gestützte Datenextraktion aus Rechnungen, Verträgen und anderen Geschäftsdokumenten.',
        },
        apiIntegration: {
          title: 'API-Integration',
          description: 'Nahtlose Integration mit mehreren externen Systemen und APIs unter Verwendung von OAuth-Authentifizierung.',
        },
        secureCompliant: {
          title: 'Sicher & Konform',
          description: 'Unternehmensweite Sicherheit mit JWT-Authentifizierung und rollenbasierter Zugriffskontrolle.',
        },
      },
      howItWorks: {
        title: 'So funktioniert es',
        subtitle: 'Einfacher 3-Schritte-Prozess zur Verarbeitung Ihrer Dokumente',
        step1: {
          title: 'Dokumente hochladen',
          description: 'Ziehen Sie einfach Ihre PDF- oder Bilddateien per Drag & Drop auf unsere sichere Plattform.',
        },
        step2: {
          title: 'Daten extrahieren',
          description: 'Unsere KI extrahiert automatisch wichtige Informationen aus Ihren Dokumenten mit hoher Genauigkeit.',
        },
        step3: {
          title: 'Integrieren & Übertragen',
          description: 'Senden Sie die verarbeiteten Daten über sichere APIs an Ihre gewählten externen Systeme.',
        },
      },
      modules: {
        title: 'Verfügbare Module',
        subtitle: 'Wählen Sie aus unserer wachsenden Sammlung spezialisierter Dokumentprozessoren',
        invoiceProcessor: 'Rechnungsprozessor',
        tamperingDetection: 'Dokumentenfälschungserkennung',
        emailProcessor: 'Intelligenter E-Mail-Anhang-Prozessor',
        hrDocuments: 'HR-Dokumente',
        tariffClassifier: 'Tarif-Klassifizierer',
        exportControl: 'KI-Exportkontrolle',
        active: 'Aktiv',
        comingSoon: 'Demnächst verfügbar',
        status: {
          inProgress: 'In Bearbeitung',
          comingSoon: 'Demnächst verfügbar',
        },
        documentProcessing: {
          name: 'Dokumentenverarbeitung',
          description: 'Hochladen und Verarbeiten von Dokumenten mit KI-gestützter Datenextraktion',
        },
        tamperingDetectionModule: {
          name: 'Manipulationserkennung',
          description: 'KI-gestützte Erkennung von Dokumentenfälschung und Manipulation',
        },
        emailProcessorModule: {
          name: 'E-Mail-Prozessor',
          description: 'Automatische Überwachung von E-Mail-Konten und Verarbeitung von Anhängen mit KI',
        },
        tariffClassifierModule: {
          name: 'Tariff-Classifier',
          description: 'ML-gestützte HS-Code-Bestimmung mit 98% Genauigkeit',
        },
        aiExportControl: {
          name: 'KI-Exportkontrolle',
          description: 'ECCN-Klassifizierung und Identifizierung von Dual-Use-Artikeln',
        },
        documentSets: {
          name: 'Dokumentensätze',
          description: 'Intelligente Kombination von Daten aus mehreren Dokumenten für umfassende Verarbeitung',
        },
        magicMarkerPro: {
          name: 'Magic Marker Pro',
          description: 'Intelligente Dokumentenannotation mit KI-gestützter Felderkennung',
        },
        complianceEngine: {
          name: 'Compliance-Engine',
          description: 'Echtzeit-Sanktionsprüfung und Compliance-Validierung',
        },
        aiLearningModule: {
          name: 'KI-Lernmodul',
          description: 'Kontinuierliche Verbesserungs-Engine, die aus Benutzerfeedback lernt',
        },
        businessIntelligence: {
          name: 'Business Intelligence Dashboard',
          description: 'Echtzeitanalysen und Einblicke für Handelsoperationen',
        },
        conversationalAI: {
          name: 'Konversations-KI-Assistent',
          description: 'Natürliche Sprachunterstützung für Handelsdokumentationsanfragen',
        },
        workflowAutomation: {
          name: 'Workflow-Automatisierungs-Engine',
          description: 'Visueller Workflow-Builder für automatisierte Dokumentenverarbeitung',
        },
        blockchainIntegration: {
          name: 'Blockchain-Integration',
          description: 'Unveränderliche Dokumentenverifizierung mit Blockchain-Technologie',
        },
        multiLanguageSupport: {
          name: 'Mehrsprachige Unterstützung',
          description: 'Verarbeitung von Dokumenten in über 50 Sprachen mit KI-Übersetzung',
        },
        masterDataManagement: {
          name: 'Stammdatenverwaltung',
          description: 'Zentrales Repository für Produkte, Lieferanten und Handelsdaten',
        },
        predictiveAnalytics: {
          name: 'Predictive Analytics',
          description: 'ML-basierte Vorhersagen für Versandzeiten und Kosten',
        },
        smartContractGeneration: {
          name: 'Smart Contract Generierung',
          description: 'Automatisierte Erstellung von Handelsverträgen und Verträgen',
        },
        intelligentDocumentRouting: {
          name: 'Intelligentes Dokumenten-Routing',
          description: 'KI-basiertes Routing zu geeigneten Verarbeitungsworkflows',
        },
        supplyChainVisibility: {
          name: 'Supply Chain Sichtbarkeit',
          description: 'End-to-End-Tracking und Sichtbarkeit über die gesamte Lieferkette',
        },
        documentQualityScoring: {
          name: 'Dokumentenqualitätsbewertung',
          description: 'KI-Qualitätsbewertung mit automatischer Fehlererkennung',
        },
        versionControlSystem: {
          name: 'Versionskontrollsystem',
          description: 'Dokumentenversionierung mit Änderungsverfolgung und Rollback',
        },
      },
      pricing: {
        title: 'Wählen Sie Ihren Plan',
        subtitle: 'Wählen Sie den perfekten Plan für Ihre Geschäftsanforderungen',
        mostPopular: 'BELIEBTESTER',
        essential: {
          name: 'Essential',
          description: 'Perfekt für kleine Unternehmen, die gerade anfangen',
          features: [
            'Bis zu 500 Dokumente/Monat',
            'Grundlegende Dokumentenextraktion',
            'Rechnungsverarbeitung',
            'E-Mail-Support',
            'Standard-API-Zugang',
            'Basis-Analytics-Dashboard',
            'Einzelbenutzer-Konto',
            '30-tägige Datenspeicherung',
          ],
        },
        advanced: {
          name: 'Advanced',
          description: 'Ideal für wachsende Teams und mittelständische Unternehmen',
          features: [
            'Bis zu 2.500 Dokumente/Monat',
            'Erweiterte KI-Extraktion',
            'Alle Dokumenttypen',
            'Prioritäts-E-Mail- und Chat-Support',
            'Erweiterte API-Integrationen',
            'Benutzerdefinierte Analysen & Berichte',
            'Bis zu 5 Benutzerkonten',
            '90-tägige Datenspeicherung',
            'Dokumentenfälschungserkennung',
            'Workflow-Automatisierung',
            'Benutzerdefinierte Feldextraktion',
          ],
        },
        enterprise: {
          name: 'Enterprise',
          description: 'Für große Organisationen mit komplexen Anforderungen',
          features: [
            'Unbegrenzte Dokumente',
            'Unternehmensweite KI-Verarbeitung',
            'Alle Module & Integrationen',
            '24/7 Telefon- und dedizierter Support',
            'Benutzerdefinierte API-Entwicklung',
            'Erweiterte Sicherheit & Compliance',
            'Unbegrenzte Benutzerkonten',
            '1-jährige Datenspeicherung',
            'Alle kommenden Funktionen',
            'Benutzerdefinierte Modulentwicklung',
            'On-Premise-Bereitstellungsoption',
            'SLA-Garantie (99,9% Betriebszeit)',
          ],
        },
        getStarted: 'Loslegen',
        startFreeTrial: 'Kostenlose Testversion starten',
        month: 'Monat',
      },
      footer: {
        tagline: 'Optimieren Sie Ihren Dokumentenverarbeitungs-Workflow mit unserer leistungsstarken Extraktions- und Integrationsplattform.',
        quickLinks: 'Schnellzugriff',
        signIn: 'Anmelden',
        createAccount: 'Konto erstellen',
        copyright: '© 2026 Document Builder. Alle Rechte vorbehalten.',
      },
    },
    workflow: {
      uploadDocument: 'Dokument hochladen',
      analyse: 'Analysieren',
      review: 'Überprüfen',
    },
  }
};

// Simple translation function
export const getNestedValue = (obj: Translation, path: string): string | string[] => {
  return path.split('.').reduce((current: any, key: string) => {
    return current && current[key] !== undefined ? current[key] : path;
  }, obj);
};

export const t = (key: string, language: Language = 'en', ...params: any[]): string | string[] => {
  const translation = getNestedValue(translations[language], key);
  if (typeof translation === 'string') {
    let result = translation;

    // Check if first param is an object for named parameters (i18next style {{name}})
    if (params.length === 1 && typeof params[0] === 'object' && params[0] !== null) {
      const namedParams = params[0] as Record<string, any>;
      result = result.replace(/\{\{(\w+)\}\}/g, (match, paramName) => {
        return namedParams[paramName] !== undefined ? String(namedParams[paramName]) : match;
      });
    } else {
      // Replace positional placeholders {0}, {1}, etc. with provided parameters
      result = result.replace(/\{(\d+)\}/g, (match, index) => {
        const paramIndex = parseInt(index, 10);
        return params[paramIndex] !== undefined ? String(params[paramIndex]) : match;
      });
    }

    return result;
  }
  if (Array.isArray(translation)) {
    return translation;
  }
  return key;
};